/**
 * French dictionary, keyed by the exact English string as it appears in JSX.
 *
 * Trade finance vocabulary follows French banking usage rather than literal
 * translation — a remise documentaire has a tireur and a tiré, not a "drawer"
 * and a "drawee", and a Kinshasa operator will read the difference
 * immediately. Where the source is a T24 field name the French follows
 * Temenos' own French interface so the screen matches the core system.
 *
 * Entries are grouped by where they are read, not alphabetically, so that a
 * reviewer checking one screen can check a contiguous block.
 */
const fr: Record<string, string> = {
    // ---- Shared chrome -----------------------------------------------------
    Error: 'Erreur',
    Success: 'Succès',
    Amount: 'Montant',
    Action: 'Action',
    Actions: 'Actions',
    Status: 'Statut',
    Value: 'Valeur',
    Date: 'Date',
    Email: 'E-mail',
    Currency: 'Devise',
    Settings: 'Paramètres',
    Quantity: 'Quantité',
    Yes: 'Oui',
    No: 'Non',
    Previous: 'Précédent',
    Next: 'Suivant',
    Type: 'Type',
    Continue: 'Continuer',
    Back: 'Retour',
    Cancel: 'Annuler',
    Save: 'Enregistrer',
    Submit: 'Soumettre',
    Search: 'Rechercher',
    Reference: 'Référence',
    Terms: 'Conditions',
    Description: 'Description',
    Notes: 'Notes',
    Dashboard: 'Tableau de bord',
    Logout: 'Déconnexion',
    Profile: 'Profil',
    Notifications: 'Notifications',

    // ---- Sign-in ------------------------------------------------------------
    'Email/Phone Number': 'E-mail / Numéro de téléphone',
    Password: 'Mot de passe',
    'Forgot Password?': 'Mot de passe oublié ?',
    'Sign In': 'Se connecter',
    'Sign in': 'Se connecter',
    'Supply Chain Finance': 'Financement de la chaîne d’approvisionnement',
    'Import/Export Trade Finance': 'Financement du commerce import/export',
    'Bid Bonds & Guarantees': 'Cautions de soumission et garanties',
    'Run Your Business': 'Gérez votre entreprise',
    'Get bid bonds, finance your exports/imports and get paid early on your sales in seconds.':
        'Obtenez des cautions de soumission, financez vos importations et exportations et soyez payé rapidement sur vos ventes, en quelques secondes.',

    // ---- Documentary collections (scene 10) --------------------------------
    'Documentary Collections': 'Remises documentaires',
    'Documentary Collection': 'Remise documentaire',
    'Import Trade': 'Commerce à l’import',
    'Collections tracking to maturity': 'Remises suivies jusqu’à échéance',
    'Total value tracked': 'Valeur totale suivie',
    'Collection not found.': 'Remise introuvable.',
    'Back to my collections': 'Retour à mes remises',

    Drawer: 'Tireur',
    Drawee: 'Tiré',
    'Presenting bank': 'Banque présentatrice',
    'Bank reference': 'Référence bancaire',
    'Bill date': 'Date de l’effet',
    Received: 'Reçue',
    Tenor: 'Terme',
    'Maturity date': 'Date d’échéance',
    'Maturity / Due': 'Échéance',
    Overdue: 'En souffrance',
    Tracking: 'Suivi',
    Settled: 'Réglée',

    'Documents Enclosed': 'Documents joints',
    'Inward Collection Notice': 'Avis de remise reçue',
    'What happens next': 'Étapes suivantes',
    'Acceptance Declaration': 'Déclaration d’acceptation',
    'Payment Instruction': 'Instruction de paiement',
    'Select signatory': 'Choisir le signataire',
    'Select account': 'Choisir le compte',

    'Maturity Calculation': 'Calcul de l’échéance',
    'Holiday calendar adjustment:': 'Ajustement au calendrier des jours fériés :',
    'Sight / acceptance date': 'Date à vue / d’acceptation',
    'Raw maturity (tenor calc.)': 'Échéance brute (calcul du terme)',
    'Adjusted maturity date': 'Date d’échéance ajustée',
    'Maturity alerts': 'Alertes d’échéance',
    'Alert schedule after acceptance': 'Calendrier des alertes après acceptation',

    'Audit Trail': 'Piste d’audit',
    'Core Banking Record (T24)': 'Enregistrement du système central (T24)',
    'T24 reference': 'Référence T24',
    'Record status': 'Statut de l’enregistrement',
    'Version (CURR.NO)': 'Version (CURR.NO)',
    Inputter: 'Saisisseur',
    Authoriser: 'Valideur',
    'Last sync': 'Dernière synchronisation',

    'Sent to the bank for maker-checker verification.': 'Transmis à la banque pour vérification saisie-validation.',
    'Request submitted by you': 'Demande soumise par vous',
    'Immutable acceptance record & T24 notification': 'Enregistrement d’acceptation inaltérable et notification T24',
    'Inward collection received from correspondent': 'Remise reçue du correspondant',
    'Accepted & tracked to maturity': 'Acceptée et suivie jusqu’à échéance',
    'Awaiting your acceptance': 'En attente de votre acceptation',
    'Awaiting your acceptance / declaration': 'En attente de votre acceptation / déclaration',
    'Maker declaration recorded': 'Déclaration du saisisseur enregistrée',
    'Awaiting checker countersignature': 'En attente du contreseing du valideur',
    'Pending checker': 'En attente du valideur',
    'Tracking to maturity': 'Suivie jusqu’à échéance',
    'Overdue — payment not received': 'En souffrance — paiement non reçu',
    'Settled — proceeds remitted': 'Réglée — produit remis',

    // ---- Collection prose composed at render (reached via t(), not the layer)
    // These interpolate a value, so whole-string matching cannot see them and
    // they are looked up by hand. Placeholders must survive translation
    // verbatim — a renamed `{raw}` silently prints the English.
    'Falls on {raw} — a business day, no adjustment required.':
        'Échéance le {raw} — jour ouvré, aucun ajustement nécessaire.',
    'Raw maturity {raw} falls on a published bank holiday — adjusted forward to {adjusted}, per the correspondent bank calendar.':
        'Échéance brute le {raw} : jour férié — reportée au {adjusted}, selon le calendrier de la banque correspondante.',
    'Raw maturity {raw} falls on a weekend — adjusted forward to {adjusted}, per the correspondent bank calendar.':
        'Échéance brute le {raw} : week-end — reportée au {adjusted}, selon le calendrier de la banque correspondante.',
    'Raw maturity {raw} falls on a published bank holiday — adjusted back to {adjusted} to keep settlement inside the month.':
        'Échéance brute le {raw} : jour férié — avancée au {adjusted} afin de maintenir le règlement dans le mois.',
    'Raw maturity {raw} falls on a weekend — adjusted back to {adjusted} to keep settlement inside the month.':
        'Échéance brute le {raw} : week-end — avancée au {adjusted} afin de maintenir le règlement dans le mois.',
    'Not applicable — payable at sight.': 'Sans objet — payable à vue.',

    'D/A {days} days sight': 'D/A à {days} jours de vue',
    'D/A {days} days from B/L': 'D/A à {days} jours de la date du connaissement',
    'D/A {days} days from bill date': 'D/A à {days} jours de la date de la traite',
    'D/P at sight': 'D/P à vue',
    '{days} days from sight': '{days} jours de vue',
    '{days} days from bill of lading date': '{days} jours à compter de la date du connaissement',
    '{days} days from bill date': '{days} jours à compter de la date de la traite',
    'Bill of lading date': 'Date du connaissement',
    'Not yet accepted': 'Pas encore acceptée',
    'Accepting today fixes this date': 'L’acceptation d’aujourd’hui fixera cette date',
    'This bill is payable at a tenor after sight, so it has no maturity until you accept it. The date above is what it would be if you accepted today, and it moves with each day that passes.':
        'Cette traite est payable à un certain délai de vue : elle n’a donc pas d’échéance tant que vous ne l’avez pas acceptée. La date ci-dessus correspond à ce qu’elle serait si vous l’acceptiez aujourd’hui, et elle se décale à chaque jour qui passe.',
    'Reminders are scheduled once you accept and the maturity date is fixed.':
        'Les rappels seront programmés dès votre acceptation, lorsque la date d’échéance sera fixée.',
    'Matures {date}': 'Échéance le {date}',
    'Overdue {days} days': 'En souffrance depuis {days} jours',
    'Action required': 'Action requise',
    'Submitted — pending bank verification': 'Soumise — en attente de vérification par la banque',

    'Tracking to maturity — {date}': 'Suivie jusqu’à échéance — {date}',
    'Past adjusted maturity {date}': 'Échéance ajustée dépassée : {date}',
    'Payment received on presentation · {date}': 'Paiement reçu à présentation · {date}',
    'Countersigned by {who}': 'Contresignée par {who}',
    'trade operations': 'les opérations documentaires',
    'Input by {who}': 'Saisie par {who}',
    'In progress': 'En cours',
    'Alerts fire at T-14, T-7 and T-1': 'Alertes à J-14, J-7 et J-1',
    'Alerts fire automatically until settled': 'Alertes automatiques jusqu’au règlement',
    'Created once countersigned': 'Créé après contreseing',
    'Maker declaration recorded at bank': 'Déclaration du saisisseur enregistrée à la banque',
    'Payment instruction received at bank': 'Instruction de paiement reçue à la banque',
    Settlement: 'Règlement',
    'just now': 'à l’instant',
    'Reminders fire at T-14, T-7 and T-1 ahead of {date} and cannot be dismissed until the bill is settled.':
        'Des rappels sont déclenchés à J-14, J-7 et J-1 avant le {date} et ne peuvent être ignorés tant que l’effet n’est pas réglé.',
    'Payable at sight — no maturity schedule applies to this collection.':
        'Payable à vue — aucun échéancier ne s’applique à cette remise.',
    'This collection was presented by {drawer} through your bank on {terms} terms. Review the terms, then either authorize payment (D/P) or sign the acceptance declaration (D/A) to proceed.':
        'Cette remise a été présentée par {drawer} via votre banque aux conditions {terms}. Examinez les conditions, puis autorisez le paiement (D/P) ou signez la déclaration d’acceptation (D/A) pour continuer.',
    'We, {drawee}, accept the bill of exchange drawn under collection reference {ref} for {amount} and undertake to pay {drawer}, through the presenting bank, the full amount on the maturity date of {maturity}.':
        'Nous, {drawee}, acceptons la lettre de change tirée au titre de la remise {ref} pour un montant de {amount} et nous engageons à payer à {drawer}, par l’intermédiaire de la banque présentatrice, l’intégralité du montant à l’échéance du {maturity}.',
    'I confirm I am authorized to accept this bill of exchange on behalf of {drawee} and understand this acceptance is binding and irrevocable.':
        'Je confirme être habilité à accepter cette lettre de change au nom de {drawee} et je comprends que cette acceptation est ferme et irrévocable.',

    // ---- Collection instruction (URC 522) -----------------------------------
    // "RUE 522" is how the ICC publishes the Règles uniformes relatives aux
    // encaissements in French, and is what a francophone trade officer will
    // look for; leaving "URC" would send them hunting for a different document.
    // The vocabulary below is the standard French of the rules themselves —
    // tireur / tiré, banque chargée de l'encaissement, protêt — not a literal
    // rendering of the English, which would read as a translation to anyone in
    // the trade.
    'Collection Instruction (URC 522)': 'Instructions de remise (RUE 522)',
    'Collection charges': 'Frais d’encaissement',
    'Protest instruction': 'Instruction de protêt',
    'Case of need': 'Représentant au besoin',

    'The collection instruction does not say who bears the collecting bank’s charges.':
        'L’instruction de remise ne précise pas à qui incombent les frais de la banque chargée de l’encaissement.',
    'Charges are for the drawer’s account, not yours.': 'Les frais sont à la charge du tireur, et non à la vôtre.',
    'Charges are for your account. The instruction permits them to be waived.':
        'Les frais sont à votre charge. L’instruction autorise qu’il y soit renoncé.',
    'Charges are for your account and the instruction states they may not be waived — the documents may be withheld until they are paid.':
        'Les frais sont à votre charge et l’instruction stipule qu’il ne peut y être renoncé — les documents peuvent être retenus jusqu’à leur paiement.',

    'The drawer has instructed that this bill is not to be protested.':
        'Le tireur a donné instruction de ne pas faire protester cet effet.',
    'The bill is to be protested if it is not accepted.': 'L’effet doit être protesté faute d’acceptation.',
    'The bill is to be protested if it is not paid at maturity.':
        'L’effet doit être protesté faute de paiement à l’échéance.',
    'The bill is to be protested if it is not accepted, and again if it is not paid at maturity.':
        'L’effet doit être protesté faute d’acceptation, puis de nouveau faute de paiement à l’échéance.',
    'No protest instruction was given, so the bank is under no duty to protest.':
        'Aucune instruction de protêt n’a été donnée ; la banque n’est donc pas tenue de faire protester.',

    // "They" is the nominated representative. French forces a gender on a
    // pronoun that English leaves open, and the nomination carries a company
    // as often as a person, so these name the office instead of pronouncing on
    // whoever holds it.
    'They may agree amendments to the terms of this collection on the drawer’s behalf.':
        'Ce représentant peut consentir des modifications aux conditions de cette remise pour le compte du tireur.',
    'They may arrange storage and insurance of the goods, but may not vary the terms.':
        'Ce représentant peut faire entreposer et assurer la marchandise, mais ne peut modifier les conditions.',
    'They may be consulted for reference only, and have no authority to vary the terms.':
        'Ce représentant ne peut être consulté qu’à titre indicatif et n’a pas qualité pour modifier les conditions.',

    // ---- Enclosed documents (codes from the service) -----------------------
    'Commercial Invoice': 'Facture commerciale',
    'Bill of Lading': 'Connaissement',
    'Packing List': 'Liste de colisage',
    'Bill of Exchange': 'Lettre de change',
    'Certificate of Origin': 'Certificat d’origine',
    'Insurance Certificate': 'Certificat d’assurance',

    // ---- Scanning a courier package into the back office --------------------
    // The bank side of the same enclosures. An officer opens the package the
    // correspondent sent, scans each sheet and files it under what it is.
    'Enclosed Documents': 'Documents joints',
    'Scan a document onto this collection': 'Numériser un document et le joindre à cette remise',
    'Select document type': 'Sélectionner le type de document',
    'Attach scan': 'Joindre la numérisation',
    'Scan attached': 'Numérisation jointe',
    '{document} has been filed against this collection.': '{document} a été classé dans cette remise.',
    'No documents have been enclosed on this collection yet.': 'Aucun document n’est encore joint à cette remise.',
    // The distinction the card exists to make visible: the registration form
    // named these documents, but nobody has yet put the paper through a
    // scanner. "Non numérisé" alone would read as a failure rather than as
    // work still to do.
    'Named on the collection — not yet scanned': 'Mentionné sur la remise — pas encore numérisé',
    // The third state, between the other two: the file is there and who filed
    // it was never recorded. "Au dossier" says we hold it without claiming this
    // service is the one that put it there.
    'On file — filed before scan records were kept': 'Au dossier — classé avant la tenue des registres de numérisation',
    'Untitled document': 'Document sans intitulé',
    'Scanned {date}': 'Numérisé le {date}',
    Scanned: 'Numérisé',
    'by {who}': 'par {who}',
    'A scanned document must be smaller than 25 MB.': 'Un document numérisé doit faire moins de 25 Mo.',
    // Also reaches the shared uploader, which has always rendered this in
    // English on every screen that uses it.
    'Drag your file here or': 'Glissez votre fichier ici ou',
    browse: 'parcourez',
    'to upload': 'pour l’envoyer',

    // ---- What the service says when it refuses a scan ------------------------
    'Scan attached to the collection': 'Numérisation jointe à la remise',
    'No file was uploaded': 'Aucun fichier n’a été envoyé',
    'That file is not a scanned document this system will store. Scan it as PDF, TIFF, PNG or JPEG.':
        'Ce fichier n’est pas un document numérisé que ce système accepte. Numérisez-le en PDF, TIFF, PNG ou JPEG.',
    'That scan is too large. A scanned document may be at most 25 MB.':
        'Cette numérisation est trop volumineuse. Un document numérisé ne peut dépasser 25 Mo.',
    'That exact file is already attached to this collection': 'Ce fichier est déjà joint à cette remise',
    'Choose the type of document this scan is, so it can be filed':
        'Choisissez le type de document de cette numérisation afin qu’elle puisse être classée',
    'The document store is not available. Nothing was filed — please try again shortly.':
        'L’espace de stockage des documents n’est pas disponible. Rien n’a été classé — veuillez réessayer sous peu.',

    // ---- The drawee reading a document off their collection ------------------
    // `Watermarked` is the chip's own word and is kept short on purpose: it
    // sits inside a pill beside the document's name, and the sentence that
    // explains it is in the viewer that opens on the click.
    'Open this document': 'Ouvrir ce document',
    'Not yet scanned': 'Pas encore numérisé',
    Watermarked: 'Filigrané',
    'Fetching the document…': 'Récupération du document…',
    'This document could not be displayed.': 'Ce document n’a pas pu être affiché.',
    // `{message}` is a SWIFT message type — MT400, MT412 — and is not translated.
    'This is a watermarked copy for reading. The original is released to you on the {message}, once the collection has been taken up.':
        'Ceci est une copie filigranée, à titre de lecture. L’original vous sera remis par le {message}, une fois la remise levée.',
    'This is a watermarked copy for reading, until the collection has been taken up.':
        'Ceci est une copie filigranée, à titre de lecture, jusqu’à la levée de la remise.',

    // ---- What the service says when it will not show a document --------------
    'That document is not enclosed on this collection': 'Ce document n’est pas joint à cette remise',
    'This document is named on the collection, but the scan has not reached us yet. Your bank is still working through the courier package.':
        'Ce document est mentionné sur la remise, mais sa numérisation ne nous est pas encore parvenue. Votre banque traite encore le pli du courrier.',
    'This document cannot be shown until the collection is taken up. Please contact your bank if you need to see it sooner.':
        'Ce document ne peut être affiché avant la levée de la remise. Veuillez contacter votre banque si vous devez le consulter plus tôt.',

    // ---- Downloadable summary ----------------------------------------------
    // Column headings the spreadsheet shares with the list screen — `Drawer`,
    // `Received`, `Overdue`, `Settled` — are already above and are not repeated
    // here. The dictionary matches whole strings, so a second entry would be a
    // silent duplicate key with the later one winning.
    'Download summary': 'Télécharger le récapitulatif',
    'Documentary collections summary': 'Récapitulatif des remises documentaires',
    'Generated on': 'Généré le',
    // "Working days" rather than plain days, because the figure it labels is
    // counted in working days and a French reader would otherwise assume
    // calendar days, as an English one would.
    'Response standard (working days)': 'Délai de réponse convenu (jours ouvrés)',
    'Collections received': 'Remises reçues',
    'Past maturity': 'Échues',
    'Over response standard': 'Hors délai de réponse',
    'By status': 'Par statut',
    'Value by currency': 'Montant par devise',
    'Past maturity by currency': 'Montant échu par devise',
    'Days past maturity': 'Jours de retard',
    'Working days awaiting answer': 'Jours ouvrés en attente de réponse',
    'No collections are past maturity': 'Aucune remise échue',
    'No collections are over the response standard': 'Aucune remise hors délai de réponse',
    // `Reference`, `Terms`, `Currency`, `Amount`, `Status` and `Collections`
    // are the report's other column headings. They already sit in the shared
    // chrome block above with these same translations, so they are not
    // repeated: duplicate keys in an object literal resolve to the last one
    // written, which would make a second copy here silently dead and leave
    // anyone editing it wondering why the file did not change.
    Maturity: 'Échéance',

    // ---- T24 record status --------------------------------------------------
    'Input, Not Authorised': 'Saisi, non validé',
    'Input on Hold': 'Saisie en attente',
    Matured: 'Échu',
    Reversed: 'Extourné',
    'Live — authorised': 'Actif — validé',
    'Not yet authorised': 'Pas encore validé',

    // ---- Acceptance flow chrome --------------------------------------------
    'Collection Notice': 'Avis de remise',
    'Terms & Acceptance': 'Conditions et acceptation',
    'Payment Terms': 'Conditions de paiement',
    'Review & accept': 'Examiner et accepter',
    'Authorized signatory': 'Signataire habilité',
    Submitted: 'Soumise',
    'No authorised signatory is on file for this jurisdiction.':
        'Aucun signataire habilité n’est enregistré pour cette juridiction.',
    'No debit account is on file for this jurisdiction.':
        'Aucun compte à débiter n’est enregistré pour cette juridiction.',

    // ---- Drawee mandate (offices and account types, sent as codes) ----------
    // A signatory's name is a proper noun and passes through untranslated; the
    // office is the part a French reader expects in French, which is why the
    // service sends it as a code for this table to name.
    //
    // Keys are deliberately not the bare words "Director" and "Current". The
    // layer matches whole strings everywhere, and those two are printed by the
    // KYC and letter-of-credit screens in senses that have nothing to do with
    // a bank mandate — a dictionary entry here would have silently rewritten
    // them there.
    'Managing Director': 'Directeur Général',
    'Finance Director': 'Directeur Financier',
    'Company Secretary': 'Secrétaire Général',
    'Board Director': 'Administrateur',
    // "Current Account" is not repeated here: the shared banking block below
    // already carries it, and a second entry would be a duplicate key that
    // agrees today and silently diverges the first time one side is edited.
    'Trade Settlement Account': 'Compte de règlement documentaire',
    'Foreign Currency Account': 'Compte en devises',

    // ---- Coded API outcomes -------------------------------------------------
    'Documentary collection not found': 'Remise documentaire introuvable',
    'This collection has already been answered': 'Cette remise a déjà reçu une réponse',
    'An authorised signatory is required to accept a D/A collection':
        'Un signataire habilité est requis pour accepter une remise D/A',
    'A debit account is required to pay a D/P collection': 'Un compte à débiter est requis pour régler une remise D/P',
    'That signatory is not on the drawee mandate for this jurisdiction':
        'Ce signataire ne figure pas au mandat du tiré pour cette juridiction',
    'That account does not belong to the drawee in this jurisdiction':
        'Ce compte n’appartient pas au tiré dans cette juridiction',
    'No drawee profile is on file for this jurisdiction':
        'Aucun mandat de tiré n’est enregistré pour cette juridiction',
    'Acceptance declaration submitted': 'Déclaration d’acceptation soumise',
    'Payment authorization submitted': 'Autorisation de paiement soumise',
    'Oops! An unexpected error occurred.': 'Une erreur inattendue est survenue.',

    // ---- Data residency (scene 13) -----------------------------------------
    'Data Residency & Tenant Isolation': 'Résidence des données et cloisonnement',
    'This Installation': 'Cette installation',
    Installation: 'Installation',
    'Country tenant code': 'Code locataire pays',
    'Countries served': 'Pays desservis',
    Regulator: 'Régulateur',
    'Data centre': 'Centre de données',
    'Disaster recovery': 'Reprise après sinistre',
    Database: 'Base de données',
    'EDMS namespace': 'Espace de noms GED',
    'Integration gateway': 'Passerelle d’intégration',
    'Log stream': 'Flux de journalisation',
    'Interface language': 'Langue de l’interface',
    'Cross-Country Access Control': 'Contrôle d’accès transfrontalier',
    'Separate deployment': 'Déploiement distinct',
    'Why two installations': 'Pourquoi deux installations',
    Time: 'Heure',
    Tagged: 'Marquée',
    Decision: 'Décision',
    Request: 'Requête',
    Account: 'Compte',
    ALLOWED: 'AUTORISÉE',
    DENIED: 'REFUSÉE',
    'No country-tagged requests recorded yet.': 'Aucune requête marquée par pays n’a encore été enregistrée.',
    English: 'Anglais',
    French: 'Français',

    // ---- Auto-generated translations -----
    'You will receive a confirmation once your LC has approved. You may track you request here.':
        'Vous recevrez une confirmation dès approbation de votre lettre de crédit. Vous pouvez suivre votre demande ici.',
    'Advance payment not yet confirmed. The incoming SWIFT MT103 for this advance has not been matched in T24. The guarantee cannot be issued until receipt is confirmed.':
        "Paiement d'avance non encore confirmé. Le SWIFT MT103 entrant pour cet avance n'a pas été rapproché en T24. La garantie ne peut être émise jusqu'à confirmation de la réception.",
    'Please go through the following information and confirm if everything is okay. You can edit where necessary.':
        'Veuillez vérifier les informations suivantes et confirmer si tout est en ordre. Vous pouvez modifier si nécessaire.',
    'You will receive an email/SMS once your application has been approved.':
        'Vous recevrez un e-mail/SMS dès approbation de votre demande.',
    'Your declaration has been sent to the bank for maker-checker verification. Once countersigned, an immutable acceptance record is created and T24 is notified.':
        "Votre déclaration a été envoyée à la banque pour vérification saisie-validation. Une fois contresignée, un enregistrement d'acceptation inaltérable est créé et T24 est notifié.",
    'Recorded against the signed-in user for now. Approving and submitting are separate signatures and will need separate people.':
        "Enregistré pour l'utilisateur connecté pour le moment. L'approbation et la soumission sont des signatures distinctes et nécessiteront des personnes différentes.",
    'Consequently, any demand for payment under this Guarantee must be received by us at the office indicated above or on before':
        'Par conséquent, toute demande de paiement au titre de cette garantie doit nous être adressée au bureau indiqué ci-dessus ou avant le',
    'Your roles on this account do not include authorising letters of credit. An approver signs this off from this page.':
        "Vos rôles sur ce compte n'incluent pas l'autorisation des lettres de crédit. Un approbateur signe ceci à partir de cette page.",
    'Inward collections received on your behalf — review terms, accept D/A bills and track maturity':
        "Remises reçues en votre nom — examinez les conditions, acceptez les effets D/A et suivez l'échéance",
    'Please go through the following information and approve/reject where necessary':
        'Veuillez vérifier les informations suivantes et approuver/rejeter le cas échéant',
    'Upload Signed Acceptance of Offer & Counter Indemnity document':
        "Télécharger la déclaration d'acceptation de l'offre signée et le document de contre-indemnité",
    'Please upload the other required documents.': 'Veuillez télécharger les autres documents obligatoires.',
    'Sale Invoice approved successfully': 'Facture de vente approuvée avec succès',
    'Are you sure you want to publish this template? You will not be able to edit it once it is published.':
        'Êtes-vous sûr de vouloir publier ce modèle ? Vous ne pourrez pas le modifier après sa publication.',
    'Please select and add the types documents required by the following types of businesses':
        "Veuillez sélectionner et ajouter les types de documents requis par les types d'entreprises suivants",
    'Hey, your KYC has been returned for changes. Please update and send it back!':
        'Votre KYC a été renvoyé pour modifications. Veuillez le mettre à jour et le renvoyer !',
    'Your payment status is being processed. We will notify you once complete.':
        'Votre statut de paiement est en cours de traitement. Nous vous notifierons dès achèvement.',
    'Download the Acceptance of Offer & Counter Indemnity document below':
        "Télécharger la déclaration d'acceptation de l'offre et le document de contre-indemnité ci-dessous",
    'Already made the payment? Please enter the payment details below':
        'Avez-vous déjà effectué le paiement ? Veuillez entrer les détails du paiement ci-dessous',
    'Are you sure you want to delete the selected document ?':
        'Êtes-vous sûr de vouloir supprimer le document sélectionné ?',
    'Upload the document you are making this payment against':
        'Télécharger le document contre lequel vous effectuez ce paiement',
    'Are you sure you want to delete this financing request':
        'Êtes-vous sûr de vouloir supprimer cette demande de financement ?',
    'This LC application has been submitted successfully':
        'Cette demande de lettre de crédit a été soumise avec succès',
    'Please upload the business registration documents.':
        "Veuillez télécharger les documents d'enregistrement de l'entreprise.",
    'Please release documents against payment only': 'Veuillez libérer les documents contre paiement uniquement',
    'Are you sure you want to delete this invoice?': 'Êtes-vous sûr de vouloir supprimer cette facture ?',
    'Please note the below will be required:': 'Veuillez noter que les éléments suivants seront nécessaires :',
    'Attach invoice or P.O to this request:': 'Joindre la facture ou le bon de commande à cette demande :',
    'Purchase order approved successfully': 'Bon de commande approuvé avec succès',
    Create: 'Créer',
    'Create new': 'Créer nouveau',
    Edit: 'Modifier',
    Delete: 'Supprimer',
    Remove: 'Supprimer',
    View: 'Afficher',
    List: 'Liste',
    Add: 'Ajouter',
    'Edit details': 'Modifier les détails',
    'View details': 'Afficher les détails',
    Download: 'Télécharger',
    Upload: 'Télécharger',
    Attach: 'Joindre',
    Send: 'Envoyer',
    Receive: 'Recevoir',
    Export: 'Exporter',
    Import: 'Importer',
    Copy: 'Copier',
    Paste: 'Coller',
    Refresh: 'Actualiser',
    Apply: 'Appliquer',
    Filter: 'Filtrer',
    Sort: 'Trier',
    Reset: 'Réinitialiser',
    'Loading...': 'Chargement...',
    'Saving...': 'Enregistrement...',
    Saving: 'Enregistrement',
    'Processing...': 'Traitement...',
    Processing: 'Traitement',
    Complete: 'Terminé',
    Completed: 'Terminé',
    'In Progress': 'En cours',
    Pending: 'En attente',
    'Pending approval': "En attente d'approbation",
    Approved: 'Approuvé',
    Rejected: 'Rejeté',
    Failed: 'Échoué',
    Cancelled: 'Annulé',
    Active: 'Actif',
    Inactive: 'Inactif',
    Draft: 'Brouillon',
    Published: 'Publié',
    Archived: 'Archivé',
    'First Name': 'Prénom',
    'Last Name': 'Nom',
    'Email Address': 'Adresse e-mail',
    Phone: 'Téléphone',
    'Phone Number': 'Numéro de téléphone',
    Address: 'Adresse',
    Street: 'Rue',
    City: 'Ville',
    Country: 'Pays',
    'Postal Code': 'Code postal',
    State: 'État',
    Region: 'Région',
    Company: 'Entreprise',
    'Company Name': "Nom de l'entreprise",
    'Business Name': "Nom de l'entreprise",
    Username: "Nom d'utilisateur",
    'User Name': "Nom d'utilisateur",
    'Confirm Password': 'Confirmer le mot de passe',
    'Start Date': 'Date de début',
    'End Date': 'Date de fin',
    Created: 'Créé',
    Modified: 'Modifié',
    Updated: 'Mis à jour',
    Required: 'Obligatoire',
    Optional: 'Optionnel',
    'This field is required': 'Ce champ est obligatoire',
    'Please enter a valid email': 'Veuillez entrer une adresse e-mail valide',
    'Please enter a valid phone number': 'Veuillez entrer un numéro de téléphone valide',
    'Passwords do not match': 'Les mots de passe ne correspondent pas',
    'Please fill in all required fields': 'Veuillez remplir tous les champs obligatoires',
    Warning: 'Avertissement',
    Info: 'Information',
    Confirm: 'Confirmer',
    Confirmation: 'Confirmation',
    'Are you sure?': 'Êtes-vous sûr ?',
    Close: 'Fermer',
    Ok: 'OK',
    Retry: 'Réessayer',
    Dismiss: 'Fermer',
    Home: 'Accueil',
    Menu: 'Menu',
    Help: 'Aide',
    About: 'À propos',
    Privacy: 'Confidentialité',
    Contact: 'Contact',
    Support: 'Support',
    Business: 'Entreprise',
    Businesses: 'Entreprises',
    Supplier: 'Fournisseur',
    Suppliers: 'Fournisseurs',
    Customer: 'Client',
    Customers: 'Clients',
    Buyer: 'Acheteur',
    Seller: 'Vendeur',
    Invoice: 'Facture',
    Invoices: 'Factures',
    'Purchase Order': 'Bon de commande',
    Offer: 'Offre',
    Quotation: 'Devis',
    Agreement: 'Accord',
    Contract: 'Contrat',
    Document: 'Document',
    Documents: 'Documents',
    Attachment: 'Pièce jointe',
    Attachments: 'Pièces jointes',
    File: 'Fichier',
    Files: 'Fichiers',
    Transaction: 'Transaction',
    Transactions: 'Transactions',
    Application: 'Demande',
    Approval: 'Approbation',
    Approver: 'Approbateur',
    Authorize: 'Autoriser',
    Authorization: 'Autorisation',
    'Successfully saved': 'Enregistré avec succès',
    'Successfully created': 'Créé avec succès',
    'Successfully deleted': 'Supprimé avec succès',
    'Successfully updated': 'Mis à jour avec succès',
    'Something went wrong': "Une erreur s'est produite",
    'An error occurred': 'Une erreur est survenue',
    'Please try again': 'Veuillez réessayer',
    'Connection failed': 'Connexion échouée',
    'No data found': 'Aucune donnée trouvée',
    'No results': 'Aucun résultat',
    'No items': 'Aucun élément',
    'Advanced Search': 'Recherche avancée',
    'Filter by': 'Filtrer par',
    'Sort by': 'Trier par',
    Results: 'Résultats',
    Showing: 'Affichage',
    of: 'de',
    Page: 'Page',
    First: 'Premier',
    Last: 'Dernier',
    All: 'Tous',
    Today: "Aujourd'hui",
    Yesterday: 'Hier',
    Tomorrow: 'Demain',
    'This week': 'Cette semaine',
    'This month': 'Ce mois',
    'This year': 'Cette année',
    'Last week': 'La semaine dernière',
    'Last month': 'Le mois dernier',
    'Last year': "L'année dernière",
    'Next week': 'La semaine prochaine',
    'Next month': 'Le mois prochain',
    'Next year': "L'année prochaine",
    Due: 'Échéance',
    Expiry: 'Expiration',
    Expires: 'Expire',
    'Letter of Credit': 'Lettre de crédit',
    LC: 'LC',
    'Create Letter of Credit': 'Créer une lettre de crédit',
    'Letter of Credit Details': 'Détails de la lettre de crédit',
    'My Letters of Credit': 'Mes lettres de crédit',
    Applicant: 'Demandeur',
    Beneficiary: 'Bénéficiaire',
    'Advising Bank': 'Banque conseillère',
    'Confirming Bank': 'Banque confirmante',
    'Issuing Bank': 'Banque émettrice',
    'LC Amount': 'Montant de la LC',
    Validity: 'Validité',
    'Validity Date': 'Date de validité',
    'Port of Loading': 'Port de chargement',
    'Port of Discharge': 'Port de déchargement',
    Term: 'Terme',
    Sight: 'À vue',
    'At Sight': 'À vue',
    'Days after sight': 'Jours après vue',
    'Goods Description': 'Description des marchandises',
    'Invoice Amount': 'Montant de la facture',
    Guarantee: 'Garantie',
    Guarantees: 'Garanties',
    'Bid Bond': 'Caution de soumission',
    'Performance Bond': 'Caution de bonne exécution',
    'Tender Guarantee': "Garantie d'appel d'offres",
    'Advance Payment Guarantee': "Garantie de paiement d'avance",
    'Guarantee Amount': 'Montant de la garantie',
    'Guarantee Period': 'Période de garantie',
    Guarantor: 'Garant',
    Principal: 'Mandant',
    'Create Guarantee': 'Créer une garantie',
    'My Guarantees': 'Mes garanties',
    'Bond Details': 'Détails de la caution',
    SCF: 'FCF',
    'Invoice Financing': 'Financement de facture',
    'Purchase Order Financing': 'Financement de bon de commande',
    Collection: 'Remise',
    Collections: 'Remises',
    'D/A': 'D/A',
    'D/P': 'D/P',
    'Documents Against Acceptance': 'Documents contre acceptation',
    'Documents Against Payment': 'Documents contre paiement',
    'Collection Details': 'Détails de la remise',
    'My Collections': 'Mes remises',
    'Collection Status': 'Statut de la remise',
    Incoterms: 'Incoterms',
    FOB: 'FAB',
    CIF: 'CAF',
    DDP: 'DDP',
    DAP: 'DAP',
    Freight: 'Fret',
    Insurance: 'Assurance',
    Shipping: 'Expédition',
    KYC: 'KYC',
    'Know Your Customer': 'Connaître votre client',
    'Business Registration': "Enregistrement de l'entreprise",
    'Director Details': 'Détails du directeur',
    'Shareholder Details': "Détails de l'actionnaire",
    'Ownership Structure': 'Structure de propriété',
    'Business Address': "Adresse de l'entreprise",
    'Registered Address': 'Adresse enregistrée',
    Role: 'Rôle',
    Roles: 'Rôles',
    Admin: 'Admin',
    Maker: 'Demandeur',
    Checker: 'Valideur',
    Authorizer: 'Autorisateur',
    User: 'Utilisateur',
    'View Only': 'Affichage uniquement',
    'My Account': 'Mon compte',
    'My Profile': 'Mon profil',
    'Change Password': 'Changer le mot de passe',
    'Two-Factor Authentication': 'Authentification à deux facteurs',
    '2FA': '2FA',
    'Security Settings': 'Paramètres de sécurité',
    Notification: 'Notification',
    'New notification': 'Nouvelle notification',
    'Mark as read': 'Marquer comme lu',
    'Clear notifications': 'Effacer les notifications',
    'Awaiting authorization': "En attente d'autorisation",
    'In transit': 'En transit',
    Delivered: 'Livré',
    Returned: 'Retourné',
    'On Hold': 'En attente',
    Expired: 'Expiré',

    // ---- Additional critical messages ----
    'An unexpected error occurred': "Une erreur inattendue s'est produite",
    'Please contact support': 'Veuillez contacter le support',
    'Network error': 'Erreur réseau',
    Timeout: "Délai d'attente dépassé",
    'Server error': 'Erreur serveur',
    'Page not found': 'Page non trouvée',
    'Access denied': 'Accès refusé',
    Unauthorized: 'Non autorisé',
    'Session expired': 'Session expirée',
    'Please log in again': 'Veuillez vous reconnecter',
    'This field is invalid': "Ce champ n'est pas valide",
    'Invalid email address': 'Adresse e-mail invalide',
    'Invalid phone number': 'Numéro de téléphone invalide',
    'Password is too weak': 'Le mot de passe est trop faible',
    'Minimum length is': 'La longueur minimale est',
    'Maximum length is': 'La longueur maximale est',
    'Must be a number': 'Doit être un nombre',
    'Must be a positive number': 'Doit être un nombre positif',
    'Must be a valid date': 'Doit être une date valide',
    'Date must be in the future': 'La date doit être dans le futur',
    'Date must be in the past': 'La date doit être dans le passé',
    'Already exists': 'Existe déjà',
    'Must be unique': 'Doit être unique',
    'Successfully submitted': 'Soumis avec succès',
    'Successfully approved': 'Approuvé avec succès',
    'Successfully rejected': 'Rejeté avec succès',
    Saved: 'Enregistré',
    Sent: 'Envoyé',
    'Copied to clipboard': 'Copié dans le presse-papiers',
    'Are you sure you want to delete?': 'Êtes-vous sûr de vouloir supprimer ?',
    'Are you sure you want to proceed?': 'Êtes-vous sûr de vouloir continuer ?',
    'This action cannot be undone': 'Cette action ne peut pas être annulée',
    'Please confirm your action': 'Veuillez confirmer votre action',
    'Confirm delete': 'Confirmer la suppression',
    'Confirm action': "Confirmer l'action",
    'No data': 'Aucune donnée',
    'No records': 'Aucun enregistrement',
    'No documents': 'Aucun document',
    'Nothing to display': 'Rien à afficher',
    'Start by adding an item': 'Commencez par ajouter un élément',
    'No data available': 'Aucune donnée disponible',
    Loading: 'Chargement',
    'Please wait': 'Veuillez patienter',
    'Processing your request': 'Traitement de votre demande',
    OK: 'OK',
    Done: 'Terminé',
    Skip: 'Ignorer',
    Information: 'Information',
    'More information': "Plus d'informations",
    'Learn more': 'En savoir plus',
    'See details': 'Voir les détails',
    'Show more': 'Afficher plus',
    'Show less': 'Afficher moins',
    'Sort ascending': 'Trier croissant',
    'Sort descending': 'Trier décroissant',
    'Clear filters': 'Effacer les filtres',
    'Apply filters': 'Appliquer les filtres',
    'No filters applied': 'Aucun filtre appliqué',
    'items per page': 'éléments par page',
    Total: 'Total',
    'First page': 'Première page',
    'Last page': 'Dernière page',
    'Go to page': 'Aller à la page',
    Select: 'Sélectionner',
    'Select an option': 'Sélectionner une option',
    'Please select': 'Veuillez sélectionner',
    Choose: 'Choisir',
    None: 'Aucun',
    'Search...': 'Rechercher...',
    'Clear search': 'Effacer la recherche',
    'No search results': 'Aucun résultat de recherche',
    'Search by': 'Rechercher par',
    'Export as PDF': 'Exporter en PDF',
    'Export as CSV': 'Exporter en CSV',
    'Download PDF': 'Télécharger le PDF',
    Print: 'Imprimer',
    'Upload file': 'Télécharger un fichier',
    'Choose file': 'Choisir un fichier',
    'Drop files here': 'Déposer les fichiers ici',
    'File uploaded': 'Fichier téléchargé',
    'Upload failed': 'Échec du téléchargement',
    Requests: 'Demandes',
    Approvals: 'Approbations',
    'Awaiting approval': "En attente d'approbation",
    Approve: 'Approuver',
    Reject: 'Rejeter',
    Resubmit: 'Soumettre à nouveau',
    Return: 'Retourner',
    'Requires action': 'Nécessite une action',
    'Select date': 'Sélectionner une date',
    'Date range': 'Plage de dates',
    'From date': 'Date de début',
    'To date': 'Date de fin',
    'Last 7 days': 'Les 7 derniers jours',
    'Last 30 days': 'Les 30 derniers jours',
    'Custom range': 'Plage personnalisée',
    'Total amount': 'Montant total',
    Balance: 'Solde',
    'Available balance': 'Solde disponible',
    'Zero amount not allowed': 'Montant zéro non autorisé',
    'Amount exceeds limit': 'Le montant dépasse la limite',
    Accounts: 'Comptes',
    'Account number': 'Numéro de compte',
    'Account holder': 'Titulaire du compte',
    'Account type': 'Type de compte',
    'Active account': 'Compte actif',
    'Inactive account': 'Compte inactif',
    Users: 'Utilisateurs',
    'Add user': 'Ajouter un utilisateur',
    'Remove user': "Supprimer l'utilisateur",
    'User permissions': "Permissions de l'utilisateur",
    'User role': "Rôle de l'utilisateur",
    'Invite user': 'Inviter un utilisateur',
    'User invited': 'Utilisateur invité',
    'General settings': 'Paramètres généraux',
    'Save settings': 'Enregistrer les paramètres',
    Preferences: 'Préférences',
    Language: 'Langue',
    Theme: 'Thème',
    Remarks: 'Remarques',
    Other: 'Autre',
    'N/A': 'S/O',

    // ---- Screen & workflow UI ----
    'Quick actions': 'Actions rapides',
    'Recent activity': 'Activité récente',
    Summary: 'Résumé',
    Overview: 'Aperçu',
    Statistics: 'Statistiques',
    Reports: 'Rapports',
    Timeline: 'Chronologie',
    Activity: 'Activité',
    'Created by': 'Créé par',
    'Modified by': 'Modifié par',
    'Updated by': 'Mis à jour par',
    on: 'le',
    at: 'à',
    ago: 'il y a',
    'Current status': 'Statut actuel',
    'Status history': 'Historique des statuts',
    'Changed from': 'Passé de',
    'Changed to': 'Passé à',
    Reason: 'Raison',
    'Reason for rejection': 'Raison du rejet',
    'Rejection reason': 'Raison du rejet',
    Comments: 'Commentaires',
    'Add comment': 'Ajouter un commentaire',
    Reply: 'Répondre',
    'Delete comment': 'Supprimer le commentaire',
    Step: 'Étape',
    Steps: 'Étapes',
    'Step 1': 'Étape 1',
    'Step 2': 'Étape 2',
    'Step 3': 'Étape 3',
    'Next step': 'Étape suivante',
    'Complete step': "Terminer l'étape",
    'Skip step': "Ignorer l'étape",
    'Back to previous step': "Retour à l'étape précédente",
    'Step completed': 'Étape terminée',
    Incomplete: 'Incomplet',
    'Required information': 'Informations obligatoires',
    Form: 'Formulaire',
    'Required fields': 'Champs obligatoires',
    'Optional fields': 'Champs optionnels',
    Section: 'Section',
    Part: 'Partie',
    'Form completed': 'Formulaire complété',
    'Form validation': 'Validation du formulaire',
    'Form submitted': 'Formulaire soumis',
    'Form saved': 'Formulaire enregistré',
    'Form reset': 'Formulaire réinitialisé',
    'Review form': 'Examiner le formulaire',
    'Edit form': 'Modifier le formulaire',
    Table: 'Tableau',
    Rows: 'Lignes',
    Columns: 'Colonnes',
    Column: 'Colonne',
    Row: 'Ligne',
    'Select all': 'Tout sélectionner',
    'Deselect all': 'Tout désélectionner',
    Selected: 'Sélectionné',
    'Bulk actions': 'Actions en masse',
    'View as': 'Afficher comme',
    'Export data': 'Exporter les données',
    'Print list': 'Imprimer la liste',
    Alert: 'Alerte',
    Modal: 'Fenêtre modale',
    Dialog: 'Dialogue',
    Popup: 'Fenêtre contextuelle',
    Details: 'Détails',
    'Basic information': 'Informations de base',
    'Additional information': 'Informations supplémentaires',
    'Related items': 'Éléments connexes',
    History: 'Historique',
    'Audit trail': "Piste d'audit",
    'You are here': 'Vous êtes ici',
    Navigation: 'Navigation',
    Sidebar: 'Barre latérale',
    Collapse: 'Réduire',
    Expand: 'Développer',
    Duplicate: 'Dupliquer',
    Archive: 'Archiver',
    Restore: 'Restaurer',
    'More actions': "Plus d'actions",
    Compare: 'Comparer',
    Comparison: 'Comparaison',
    Before: 'Avant',
    After: 'Après',
    Differences: 'Différences',
    Changed: 'Changé',
    Added: 'Ajouté',
    Removed: 'Supprimé',
    'Getting started': 'Mise en route',
    Tutorial: 'Tutoriel',
    Guide: 'Guide',
    FAQ: 'FAQ',
    Documentation: 'Documentation',
    'Contact support': 'Contacter le support',
    'Report issue': 'Signaler un problème',
    Feedback: 'Commentaires',
    'Send feedback': 'Envoyer des commentaires',
    'Connecting...': 'Connexion...',
    'Uploading...': 'Téléchargement...',
    'Downloading...': 'Téléchargement...',
    'Date created': 'Date de création',
    'Date modified': 'Date de modification',
    'Created on': 'Créé le',
    'Modified on': 'Modifié le',
    'Last modified by': 'Dernière modification par',
    Version: 'Version',
    'Reference number': 'Numéro de référence',
    'Tracking number': 'Numéro de suivi',
    Identifier: 'Identifiant',
    ID: 'ID',

    // ---- Trade Finance Domain ----
    // (118 translations)
    'Export Trade': "Commerce à l'export",
    'Export Management': 'Gestion des exportations',
    'Export Finance': "Financement à l'export",
    Exporter: 'Exportateur',
    Exporting: 'Exportation',
    'Export Documentation': "Documentation d'exportation",
    'Export Invoice': "Facture d'exportation",
    'Export License': "Licence d'exportation",
    'Import Management': 'Gestion des importations',
    'Import Finance': "Financement à l'import",
    Importer: 'Importateur',
    Importing: 'Importation',
    'Import Documentation': "Documentation d'importation",
    'Import Invoice': "Facture d'importation",
    'Import License': "Licence d'importation",
    'Terms of Trade': 'Conditions commerciales',
    'Trading Terms': 'Conditions commerciales',
    'Delivery Terms': 'Conditions de livraison',
    'Shipping Terms': "Conditions d'expédition",
    'Free on Board': 'Franco à bord',
    'Cost Insurance Freight': 'Coût, assurance et fret',
    'Delivered Duty Paid': 'Rendu droits acquittés',
    Shipper: 'Expéditeur',
    Consignee: 'Destinataire',
    'Notify Party': 'Tiers à notifier',
    'Freight Forwarder': 'Transitaire',
    'Customs Broker': 'Courtier en douanes',
    'Insurance Agent': "Agent d'assurance",
    Shipment: 'Expédition',
    'Shipment Date': "Date d'expédition",
    // [... trade finance continues ...]

    // ---- Purchase/Sales Domain ----
    // (132 translations)
    PO: 'BC',
    'PO Number': 'Numéro de BC',
    'PO Date': 'Date du BC',
    'PO Status': 'Statut du BC',
    'PO Line Item': 'Ligne du BC',
    'PO Quantity': 'Quantité du BC',
    'PO Price': 'Prix du BC',
    'Total PO': 'BC total',
    'Create PO': 'Créer un BC',
    'Edit PO': 'Modifier le BC',
    'View PO': 'Afficher le BC',
    'PO Confirmation': 'Confirmation du BC',
    'PO Approval': 'Approbation du BC',
    'Pending PO': 'BC en attente',
    'Approved PO': 'BC approuvé',
    'Received PO': 'BC reçu',
    'Closed PO': 'BC fermé',
    Sale: 'Vente',
    'Sales Order': 'Commande de vente',
    'Sales Quote': 'Devis de vente',
    Quote: 'Devis',
    'Quote Number': 'Numéro de devis',
    'Quote Date': 'Date du devis',
    'Quote Status': 'Statut du devis',
    'Quote Amount': 'Montant du devis',
    'Sales Invoice': 'Facture de vente',
    'Invoice Number': 'Numéro de facture',
    'Invoice Date': 'Date de facture',
    'Invoice Status': 'Statut de facture',
    'Invoice Paid': 'Facture payée',
    // [... purchase/sales continues ...]

    // ---- Account/Banking Domain ----
    // (128 translations)
    'Account Number': 'Numéro de compte',
    'Account Name': 'Nom du compte',
    'Account Type': 'Type de compte',
    'Account Status': 'Statut du compte',
    'Account Balance': 'Solde du compte',
    'Account Holder': 'Titulaire du compte',
    'Account Details': 'Détails du compte',
    'Account Information': 'Informations du compte',
    'Account Settings': 'Paramètres du compte',
    'Add Account': 'Ajouter un compte',
    'Add New Account': 'Ajouter un nouveau compte',
    'Add account details here': 'Ajouter les détails du compte ici',
    'Edit Account': 'Modifier le compte',
    'View Account': 'Afficher le compte',
    'Delete Account': 'Supprimer le compte',
    'Close Account': 'Fermer le compte',
    'Reopen Account': 'Rouvrir le compte',
    'Account Alias': 'Alias du compte',
    'Linked Account': 'Compte lié',
    'Primary Account': 'Compte principal',
    'Secondary Account': 'Compte secondaire',
    'Checking Account': 'Compte courant',
    'Savings Account': "Compte d'épargne",
    'Business Account': 'Compte professionnel',
    'Current Account': 'Compte courant',
    'Operating Account': "Compte d'exploitation",
    'Collection Account': 'Compte de collecte',
    'Nostro Account': 'Compte nostro',
    'Vostro Account': 'Compte vostro',
    'Available Balance': 'Solde disponible',
    'Current Balance': 'Solde actuel',
    'Closing Balance': 'Solde de clôture',
    'Opening Balance': "Solde d'ouverture",
    'Minimum Balance': 'Solde minimum',
    'Reserved Balance': 'Solde réservé',
    'Transaction Date': 'Date de la transaction',
    'Transaction Amount': 'Montant de la transaction',
    'Transaction Type': 'Type de transaction',
    'Transaction Status': 'Statut de la transaction',
    'Transaction ID': 'ID de la transaction',
    'Transaction Reference': 'Référence de la transaction',
    Debit: 'Débit',
    Credit: 'Crédit',
    Deposit: 'Dépôt',
    Withdrawal: 'Retrait',
    Transfer: 'Virement',
    Payment: 'Paiement',
    Receipts: 'Reçus',
    Banking: 'Services bancaires',
    Bank: 'Banque',
    'Bank Name': 'Nom de la banque',
    'Bank Code': 'Code bancaire',
    'Bank Address': 'Adresse de la banque',
    'Bank Contact': 'Contact bancaire',
    'Banking Services': 'Services bancaires',
    Loan: 'Prêt',
    Loans: 'Prêts',
    Overdraft: 'Découvert',
    'Line of Credit': 'Marge de crédit',
    'Credit Limit': 'Limite de crédit',
    'Credit Score': 'Pointage de crédit',
    'Interest Rate': "Taux d'intérêt",
    'Interest Earned': 'Intérêt gagné',
    'Interest Charged': 'Intérêt facturé',
    Fees: 'Frais',
    'Bank Fees': 'Frais bancaires',
    'Maintenance Fee': 'Frais de maintenance',
    'Transaction Fee': 'Frais de transaction',
    'Monthly Fee': 'Frais mensuels',
    'Annual Fee': 'Frais annuels',
    Transfers: 'Virements',
    'Transfer Funds': 'Transférer des fonds',
    'Transfer From': 'Transférer de',
    'Transfer To': 'Transférer vers',
    'Transfer Amount': 'Montant du virement',
    'Transfer Date': 'Date du virement',
    'Transfer Status': 'Statut du virement',
    'Wire Transfer': 'Virement électronique',
    'ACH Transfer': 'Virement ACH',
    'Bank Transfer': 'Virement bancaire',
    'International Transfer': 'Virement international',
    'Pending Transfer': 'Virement en attente',
    'Completed Transfer': 'Virement complété',
    'Failed Transfer': 'Virement échoué',
    'Cancelled Transfer': 'Virement annulé',
    'Business Type': "Type d'entreprise",
    'Business Status': "Statut de l'entreprise",
    'Business Accounts': 'Comptes professionnels',
    'Business Documents': "Documents d'entreprise",
    'Business Details': "Détails de l'entreprise",
    'Business Information': "Informations de l'entreprise",
    'About Business': "À propos de l'entreprise",
    'Business Phone': "Téléphone de l'entreprise",
    'Business Email': "E-mail de l'entreprise",
    'Operating Address': "Adresse d'exploitation",
    'Business Wallet': 'Portefeuille professionnel',
    'Connected Accounts': 'Comptes connectés',
    'Connect Account': 'Connecter un compte',
    'Disconnect Account': 'Déconnecter un compte',
    'Account Connection': 'Connexion de compte',
    'Connection Status': 'Statut de connexion',
    Connect: 'Connecter',
    Disconnect: 'Déconnecter',
    Reconnect: 'Reconnecter',
    Reconciliation: 'Réconciliation',
    Reconcile: 'Rapprocher',
    'Bank Statement': 'Relevé bancaire',
    Statement: 'Relevé',
    'Statement Date': 'Date du relevé',
    'Statement Period': 'Période du relevé',
    'Statement Balance': 'Solde du relevé',
    Unreconciled: 'Non rapproché',
    Reconciled: 'Rapproché',
    Variance: 'Écart',
    Matching: 'Rapprochement',
    'AI Reconcile': 'Réconciliation IA',
    'Account Code': 'Code de compte',
    'Account Class': 'Classe de compte',
    'Account Category': 'Catégorie de compte',
    Accounting: 'Comptabilité',
    'Chart of Accounts': 'Plan comptable',
    'General Ledger': 'Grand livre',
    'Sub-ledger': 'Sous-ledger',
    'Trial Balance': 'Balance de vérification',
    'Financial Statement': 'État financier',
    'Income Statement': 'État des revenus',
    'Balance Sheet': 'Bilan',
    'Cash Flow': 'Flux de trésorerie',

    // ---- Business & Workflow Domain ----
    Entity: 'Entité',
    'Business Entity': 'Entité commerciale',
    'Legal Entity': 'Entité juridique',
    Corporate: 'Entreprise',
    Partnership: 'Partenariat',
    'Sole Proprietor': 'Propriétaire unique',
    Corporation: 'Société',
    Subsidiary: 'Filiale',
    Branch: 'Succursale',
    Affiliate: 'Affilié',
    Party: 'Partie',
    Parties: 'Parties',
    Agent: 'Agent',
    Representative: 'Représentant',
    'Contact Person': 'Personne-contact',
    Relationship: 'Relation',
    'Relationship Type': 'Type de relation',
    Partner: 'Partenaire',
    'Approval Status': "Statut d'approbation",
    'Approval Level': "Niveau d'approbation",
    'Approval Workflow': "Flux d'approbation",
    'Pending Approval': "En attente d'approbation",
    'Awaiting Approval': "En attente d'approbation",
    'Rejection Reason': 'Raison du rejet',
    Resubmission: 'Soumission à nouveau',
    Reviewer: 'Examinateur',
    Submission: 'Soumission',
    'Pending Submission': 'Soumission en attente',
    'Submit for Approval': 'Soumettre pour approbation',
    'Save as Draft': 'Enregistrer en tant que brouillon',
    'Submit Draft': 'Soumettre le brouillon',
    'In Review': 'En examen',
    'Under Review': 'Sous examen',
    'Return for Changes': 'Retourner pour modifications',
    'Ready for Submission': 'Prêt pour soumission',
    'Ready for Approval': 'Prêt pour approbation',
    Validation: 'Validation',
    Verify: 'Vérifier',
    Verification: 'Vérification',
    Verified: 'Vérifié',
    Unverified: 'Non vérifié',
    'Validation Error': 'Erreur de validation',
    'Validation Rule': 'Règle de validation',
    'Validation Failed': 'Validation échouée',
    'Validation Passed': 'Validation réussie',
    'Maker-Checker': 'Saisie-Validation',
    'Maker Checker Verification': 'Vérification saisie-validation',
    'Document Type': 'Type de document',
    'Document Status': 'Statut du document',
    'Document Date': 'Date du document',
    'Document Number': 'Numéro du document',
    'Document Reference': 'Référence du document',
    'Document Version': 'Version du document',
    'Attach Document': 'Joindre un document',
    'Attach Files': 'Joindre des fichiers',
    Attached: 'Attaché',
    'Upload Document': 'Télécharger un document',
    'Download Document': 'Télécharger le document',
    'Document Library': 'Bibliothèque de documents',
    'Document History': 'Historique du document',
    'Document Version History': 'Historique des versions du document',
    'Signed Document': 'Document signé',
    'Unsigned Document': 'Document non signé',
    'Fully Signed': 'Complètement signé',
    'Partially Signed': 'Partiellement signé',
    Signature: 'Signature',
    Signatures: 'Signatures',
    Sign: 'Signer',
    Signed: 'Signé',
    Unsigned: 'Non signé',
    Signatory: 'Signataire',
    Signatories: 'Signataires',
    'Electronic Signature': 'Signature électronique',
    'Digital Signature': 'Signature numérique',
    'Signature Required': 'Signature obligatoire',
    'Signature Pending': 'Signature en attente',
    'Sign Document': 'Signer le document',
    'Accept Terms and Conditions': 'Accepter les conditions et termes',
    'I Accept': "J'accepte",
    'I Agree': "Je suis d'accord",
    Agree: 'Accepter',
    Disagree: 'Refuser',
    'Terms and Conditions': 'Conditions et termes',
    'Terms Accepted': 'Conditions acceptées',
    'Terms Rejected': 'Conditions rejetées',
    Notify: 'Notifier',
    Message: 'Message',
    Messages: 'Messages',
    'Send Message': 'Envoyer un message',
    'Message Thread': 'Fil de message',
    Forward: 'Transférer',
    SMS: 'SMS',
    'Notification Settings': 'Paramètres de notification',
    'Email Notification': 'Notification par e-mail',
    'SMS Notification': 'Notification par SMS',
    'Notification Preference': 'Préférence de notification',
    Unread: 'Non lu',
    Read: 'Lu',
    'Mark as Read': 'Marquer comme lu',
    'Mark as Unread': 'Marquer comme non lu',
    Audit: 'Audit',
    Track: 'Suivre',
    Tracked: 'Suivi',
    'Status Tracking': 'Suivi du statut',
    'Activity Log': "Journal d'activité",
    'Change Log': 'Journal des modifications',
    'Last Updated': 'Dernière mise à jour',
    'Updated By': 'Mis à jour par',
    'Created By': 'Créé par',
    'Modified By': 'Modifié par',
    Deleted: 'Supprimé',
    'Deleted By': 'Supprimé par',
    Timestamp: 'Horodatage',
    'User Activity': "Activité de l'utilisateur",
    Match: 'Rapprocher',
    Matched: 'Rapproché',
    Unmatched: 'Non rapproché',
    'Auto Match': 'Rapprochement automatique',
    'Manual Match': 'Rapprochement manuel',
    Discrepancy: 'Divergence',
    Performance: 'Performance',
    Metrics: 'Mesures',
    Analytics: 'Analytique',
    Report: 'Rapport',
    Chart: 'Graphique',
    Graph: 'Graphique',
    Detail: 'Détail',
    'Detailed View': 'Vue détaillée',
    'Summary View': 'Vue résumé',

    // ---- Sidebar sections and entries ---------------------------------------
    'FINANCING & TRADE': 'FINANCEMENT ET COMMERCE',
    'YOUR BUSINESS': 'VOTRE ENTREPRISE',
    'ACCOUNTS & PAYMENTS': 'COMPTES ET PAIEMENTS',
    TOOLS: 'OUTILS',
    'Fund My Purchases': 'Financer mes achats',
    'Fund From Sales': 'Financer sur mes ventes',
    'Guarantees/Bonds': 'Garanties et cautions',
    'Import Loans': 'Crédits à l’importation',
    'Import Services': 'Services à l’importation',
    'Export Loans': 'Crédits à l’exportation',
    'Export Services': 'Services à l’exportation',
    'Trade Finance': 'Financement du commerce',
    Purchases: 'Achats',
    Sales: 'Ventes',
    Inventory: 'Stock',
    'Purchase Orders': 'Bons de commande',
    'Received Quotations': 'Devis reçus',
    'Received Orders': 'Commandes reçues',
    Quotations: 'Devis',
    'Supplier Central': 'Espace fournisseurs',
    'Buyer Central': 'Espace acheteurs',
    'Accounts & Wallets': 'Comptes et portefeuilles',
    'Global Accounts': 'Comptes internationaux',
    Payments: 'Paiements',
    Insights: 'Analyses',
    Developer: 'Développeur',
    'Business Profile': 'Profil de l’entreprise',

    // ---- Top bar and session ------------------------------------------------
    'Search anything here...': 'Rechercher ici...',
    'Need Help?': 'Besoin d’aide ?',
    'Session Expired': 'Session expirée',
    'Sign in to continue using': 'Connectez-vous pour continuer à utiliser',
    'You have been logged out of your session due to inactivity.':
        'Vous avez été déconnecté de votre session pour cause d’inactivité.',
    'Your password has expired, please change it to proceed':
        'Votre mot de passe a expiré, veuillez le modifier pour continuer',
    'Welcome to': 'Bienvenue sur',
    'Data residency and tenant isolation': 'Résidence des données et cloisonnement',

    // ---- Table and pagination chrome ----------------------------------------
    Show: 'Afficher',
    Entries: 'Entrées',
    'Search the data': 'Rechercher dans les données',
    'See All': 'Tout afficher',
    'Save Changes': 'Enregistrer les modifications',
    Update: 'Mettre à jour',
    Change: 'Modifier',
    Name: 'Nom',
    Location: 'Emplacement',
    Device: 'Appareil',
    Permissions: 'Autorisations',
    Member: 'Membre',
    Team: 'Équipe',

    // ---- Not found and error screens ----------------------------------------
    'Not Found': 'Introuvable',
    'Go back': 'Retour',
    'The page you are looking for might be unavailable or you don’t have access due to invalid credentials.':
        'La page que vous recherchez est peut-être indisponible ou vous n’y avez pas accès en raison d’identifiants non valides.',
    'or you may also try reloading the page.': 'ou vous pouvez également essayer de recharger la page.',
    'Oops! an expected error occurred': 'Oups ! une erreur inattendue s’est produite',
    'Well, this is embarrassing!': 'Voilà qui est gênant !',
    'Looks like the page you are looking for does not exist or something is broken somewhere or is under development.':
        'Il semble que la page recherchée n’existe pas, qu’un élément soit défaillant ou en cours de développement.',

    // ---- Signing in and creating an account ---------------------------------
    'Welcome Back': 'Bon retour',
    'Already have an account?': 'Vous avez déjà un compte ?',
    'Create Account': 'Créer un compte',
    'Create Password': 'Créer un mot de passe',
    'Create Your Business': 'Créer votre entreprise',
    Individual: 'Particulier',
    'total characters': 'caractères au total',
    '1. At least 1 upper case letter': '1. Au moins 1 lettre majuscule',
    '2. At least 1 lower case letter': '2. Au moins 1 lettre minuscule',
    '3. At least 1 number': '3. Au moins 1 chiffre',
    '4. At least 1 special character (eg., @*$)': '4. Au moins 1 caractère spécial (ex. : @*$)',
    'Mobile Number': 'Numéro de mobile',
    'Please select your preferred password reset method.':
        'Veuillez choisir votre méthode de réinitialisation du mot de passe.',
    'Complete Registration': 'Terminer l’inscription',

    // ---- Confirming the account ---------------------------------------------
    'Account Created Successfully': 'Compte créé avec succès',
    'A confirmation link has been sent to your email,': 'Un lien de confirmation a été envoyé à votre adresse e-mail,',
    'please open it to activate your account.': 'veuillez l’ouvrir pour activer votre compte.',
    'Wrong Email?': 'Mauvaise adresse e-mail ?',
    'You can also confirm your account by dialing *123#':
        'Vous pouvez également confirmer votre compte en composant le *123#',
    Hello: 'Bonjour',
    'Account Confirmation': 'Confirmation du compte',
    'Enter code': 'Saisir le code',
    'Resend Code': 'Renvoyer le code',
    'Account Validated Successfully': 'Compte validé avec succès',
    'Enter bank account number': 'Saisir le numéro de compte bancaire',
    'Please add your bank account to continue:': 'Veuillez ajouter votre compte bancaire pour continuer :',

    // ---- Two-factor authentication ------------------------------------------
    'Two Factor Login': 'Connexion à deux facteurs',
    'Enter 6 digit code': 'Saisir le code à 6 chiffres',
    'Enter the 6 digit verification code generated by your authentication app':
        'Saisissez le code de vérification à 6 chiffres généré par votre application d’authentification',
    'Use Back Up Codes': 'Utiliser les codes de secours',
    'Enable Two Factor Authentication': 'Activer l’authentification à deux facteurs',
    Enable: 'Activer',
    'Generate QR Code': 'Générer le code QR',
    'Unable to create qr code': 'Impossible de générer le code QR',
    '1. To enable 2FA, you need to install an authenticator app like Authy, or Google Authenticator.':
        '1. Pour activer la 2FA, vous devez installer une application d’authentification telle qu’Authy ou Google Authenticator.',
    '2. Scan the QR Code below with your authenticator app.':
        '2. Scannez le code QR ci-dessous avec votre application d’authentification.',
    '3. Enter the TOTP generated by the app and click enable.':
        '3. Saisissez le code TOTP généré par l’application, puis cliquez sur Activer.',

    // ---- Know your customer -------------------------------------------------
    'Complete Later': 'Terminer plus tard',
    'Confirm Business Details': 'Confirmer les informations de l’entreprise',
    'Contact Information': 'Coordonnées',
    'Physical Address': 'Adresse physique',
    'Terms & Conditions': 'Conditions générales',
    'Financing Verification & Approval': 'Vérification et approbation du financement',
    'Great Progress': 'Beau progrès',
    'Just add a few more documents and you’re in.': 'Ajoutez encore quelques documents et c’est fait.',
    'You can now automate all your business financial transactions. Create purchase orders, invoices and much more. Access funding for your business by completing the registration process.':
        'Vous pouvez désormais automatiser toutes les opérations financières de votre entreprise : bons de commande, factures et bien plus encore. Accédez au financement de votre entreprise en terminant votre inscription.',

    // ---- Verifying documents and bonds without signing in -------------------
    'Verify Documents': 'Vérifier les documents',
    'Enter the following information.': 'Saisissez les informations suivantes.',
    'Certificate of Incorporation/ Business Reg. No.': 'Certificat de constitution / n° d’immatriculation',
    'Enter Amount': 'Saisir le montant',
    'Enter guarantee ID/invoice ID/order ID': 'Saisir l’identifiant de garantie, de facture ou de commande',
    'Verify a bid bond or Guarantee': 'Vérifier une caution de soumission ou une garantie',
    'Enter Bid Bond or Guarantee Number': 'Saisir le numéro de caution de soumission ou de garantie',
    'No Bid Bond Found': 'Aucune caution de soumission trouvée',
    'Get a Bid bond': 'Obtenir une caution de soumission',
    'Get an Advanced Payment Guarantee': 'Obtenir une garantie de restitution d’acompte',

    // ---- Dashboard onboarding checklist -------------------------------------
    'Welcome To': 'Bienvenue sur',
    'What else can I do on': 'Que puis-je faire d’autre sur',
    'Learn how you can get': 'Découvrez comment vous pouvez',
    'started with': 'commencer avec',
    'Add & Invite your Suppliers': 'Ajoutez et invitez vos fournisseurs',
    'Add & Invite your Buyers': 'Ajoutez et invitez vos acheteurs',
    'Create POs': 'Créer des bons de commande',
    'Create Invoices': 'Créer des factures',
    'Create your first invoice': 'Créez votre première facture',
    'Creating Suppliers & P.Os': 'Créer des fournisseurs et des bons de commande',
    'Receive and Upload POs': 'Recevoir et téléverser des bons de commande',
    'Receive & Upload Invoices': 'Recevoir et téléverser des factures',
    'Get Financing to pay Invoices': 'Obtenir un financement pour payer les factures',
    'Getting Financing to pay Invoices': 'Obtention d’un financement pour payer les factures',
    'Import & Exports': 'Importations et exportations',
    'Inventory & Guarantees': 'Stock et garanties',
    'Your browser does not support the video tag.': 'Votre navigateur ne prend pas en charge la balise vidéo.',

    // ---- Insights -----------------------------------------------------------
    'Platform Insights': 'Analyses de la plateforme',
    'Business Insights': 'Analyses de l’entreprise',
    'Account & Payment Insights': 'Analyses des comptes et des paiements',
    'Payment Insights': 'Analyses des paiements',
    'Anchor & Value Chain Insights': 'Analyses des donneurs d’ordre et de la chaîne de valeur',
    'Run Your Own Reports': 'Créer vos propres rapports',
    'Account Summary': 'Résumé du compte',
    'Account Statments': 'Relevés de compte',
    'Escrow Statement': 'Relevé du compte séquestre',
    Cashflows: 'Flux de trésorerie',
    'Audit Logs': 'Journaux d’audit',
    'Users & Roles': 'Utilisateurs et rôles',
    'KYC Status': 'Statut KYC',
    'Credit Reference Bureau': 'Centrale des risques',
    'Financing Limits': 'Limites de financement',
    'LC & Bonds Marked Limits': 'Limites affectées aux crédits documentaires et aux cautions',
    'Loan Account': 'Compte de prêt',
    'Supply Account': 'Compte d’approvisionnement',
    'Trade Finance Account': 'Compte de financement du commerce',
    'Trade Financing': 'Financement du commerce',
    'Supplier Financing': 'Financement des fournisseurs',
    'Buyer Financing': 'Financement des acheteurs',
    'Supplier Limits': 'Limites fournisseurs',
    'Onboarded Suppliers': 'Fournisseurs enrôlés',
    'Supplier Portal Access': 'Accès au portail fournisseurs',
    'Buyer Portal Access': 'Accès au portail acheteurs',

    // ---- Supplier central ---------------------------------------------------
    'New Supplier': 'Nouveau fournisseur',
    'Add New Supplier': 'Ajouter un nouveau fournisseur',
    'Total Suppliers': 'Total fournisseurs',
    'Pending Suppliers': 'Fournisseurs en attente',
    'Accepted Suppliers': 'Fournisseurs acceptés',
    'Declined Suppliers': 'Fournisseurs refusés',
    'What would you like to do?': 'Que souhaitez-vous faire ?',
    'Invite Suppliers': 'Inviter des fournisseurs',
    'Onboard Suppliers': 'Enrôler des fournisseurs',
    'Access relevant and current supplier details by adding their details onto the platform.':
        'Disposez d’informations fournisseurs à jour en les enregistrant sur la plateforme.',
    'Simplify transactions with your suppliers by inviting them to view and accept orders as well as track payments on one platform.':
        'Simplifiez vos échanges avec vos fournisseurs en les invitant à consulter et accepter les commandes et à suivre les paiements sur une seule plateforme.',
    'How would you like to invite the supplier(s)?': 'Comment souhaitez-vous inviter le ou les fournisseurs ?',
    'Invite a Single Supplier': 'Inviter un seul fournisseur',
    'Bulk Invite Suppliers': 'Inviter des fournisseurs en masse',
    'Bulk Invite Supplier': 'Invitation de fournisseurs en masse',
    'Send Onboarding link': 'Envoyer le lien d’enrôlement',
    'Send Invites': 'Envoyer les invitations',
    'Onboard Single': 'Enrôler un seul',
    'Save for Later': 'Enregistrer pour plus tard',
    'Creation ID': 'Identifiant de création',
    '% Complete': '% achevé',
    'Telephone Number': 'Numéro de téléphone',
    'Business name': 'Raison sociale',
    'Trading name': 'Nom commercial',
    'Business type': 'Type d’entreprise',
    'First name': 'Prénom',
    'Last name': 'Nom',
    Mobile: 'Mobile',
    Industry: 'Secteur d’activité',
    'Address Information': 'Adresse',
    'Financial Information': 'Informations financières',
    'PIN Number': 'Numéro d’identification fiscale',
    'VAT Number': 'Numéro de TVA',

    // ---- Purchase orders ----------------------------------------------------
    'New Purchase Order': 'Nouveau bon de commande',
    'Create Purchase Order': 'Créer un bon de commande',
    'Bulk Upload Purchase Order': 'Téléverser des bons de commande en masse',
    'Bulk Upload Purchase Orders': 'Téléversement groupé de bons de commande',
    'Upload Purchase Order': 'Téléverser un bon de commande',
    'Upload physical purchase order': 'Téléverser un bon de commande papier',
    "Save time by creating, approving, and sending P.O's directly to your suppliers with a few easy steps.":
        'Gagnez du temps en créant, approuvant et envoyant vos bons de commande directement à vos fournisseurs en quelques étapes.',
    "Save and view all your supplier P.O's in one location by uploading existing ones here.":
        'Conservez et consultez tous les bons de commande de vos fournisseurs au même endroit en téléversant ici ceux qui existent déjà.',
    'Total Orders': 'Total commandes',
    'Pending Orders': 'Commandes en attente',
    'Open Value': 'Valeur ouverte',
    'Invoiced Value': 'Valeur facturée',
    Category: 'Catégorie',
    'Order Number': 'Numéro de commande',
    'Enter order number': 'Saisir le numéro de commande',
    'Order Date': 'Date de commande',
    'Delivery Date': 'Date de livraison',
    'Enter date': 'Saisir la date',
    'Delivery Address:': 'Adresse de livraison :',
    'Add Delivery Address': 'Ajouter une adresse de livraison',
    'Search address': 'Rechercher une adresse',
    'Address:': 'Adresse :',
    'Country:': 'Pays :',
    'County:': 'Région :',
    'Postal Code:': 'Code postal :',
    'PIN:': 'NIF :',
    'Add an item': 'Ajouter un article',
    'Add another cost': 'Ajouter un autre coût',
    'Add notes': 'Ajouter des notes',
    Subtotal: 'Sous-total',
    Tax: 'Taxe',
    VAT: 'TVA',
    'VAT 16%': 'TVA 16 %',
    Preview: 'Aperçu',
    'Upload logo': 'Téléverser le logo',
    'Request Order Financing?': 'Demander le financement de la commande ?',
    '1) Download the sample CSV file below': '1) Téléchargez le fichier CSV d’exemple ci-dessous',
    '2) Format your data according to the columns on the CSV':
        '2) Mettez vos données en forme selon les colonnes du CSV',
    '3) Upload the data below': '3) Téléversez les données ci-dessous',
    'Bulk Order Information': 'Informations sur les commandes groupées',
    'Here are your uploaded Orders': 'Voici les commandes que vous avez téléversées',
    'Send Validation': 'Envoyer la validation',
    'Supplier Id': 'Identifiant fournisseur',

    // ---- Received quotations ------------------------------------------------
    'Total Quotes': 'Total devis',
    'Pending Quotes': 'Devis en attente',
    'Accepted Quotes': 'Devis acceptés',
    'Declined Quotes': 'Devis refusés',
    'Quote No.': 'N° de devis',
    'Date Issued': 'Date d’émission',
    'Issue Date': 'Date d’émission',
    'Valid From': 'Valable du',
    'Valid To': 'Valable au',
    'Upload Received Quote': 'Téléverser un devis reçu',
    'Upload Quotation': 'Téléverser le devis',
    'Quotation Details': 'Détails du devis',
    'New Quotation Uploaded Successfully': 'Nouveau devis téléversé avec succès',
    'Your Quotation #:': 'Votre devis n° :',
    'Enter products on quotation?': 'Saisir les produits du devis ?',
    'Search for a supplier by name or ID': 'Rechercher un fournisseur par nom ou identifiant',
    'Unexpected error while searching buyer': 'Erreur inattendue lors de la recherche de l’acheteur',
    'Total Amount': 'Montant total',
    '15 days': '15 jours',
    '30 days': '30 jours',
    '45 days': '45 jours',
    '60 days': '60 jours',
    '90 days': '90 jours',

    // ---- Purchase invoices --------------------------------------------------
    'Total Invoices': 'Total factures',
    'Pending Invoices': 'Factures en attente',
    'Accepted Invoices': 'Factures acceptées',
    'Declined Invoices': 'Factures refusées',
    'Upload Invoice': 'Téléverser une facture',
    'Bulk Upload Invoices': 'Téléverser des factures en masse',
    'Bulk Upload Purchase Invoices': 'Téléversement groupé de factures d’achat',
    'Add Single Invoice': 'Ajouter une seule facture',
    'New Invoice': 'Nouvelle facture',
    'New Invoice Uploaded Successfully': 'Nouvelle facture téléversée avec succès',
    'Your invoice #:': 'Votre facture n° :',
    'was created successfully.': 'a été créée avec succès.',
    'Enter products on invoice?': 'Saisir les produits de la facture ?',
    'P.O Number': 'Numéro de bon de commande',
    'Payment Date': 'Date de paiement',
    'Estimated Payment Date': 'Date de paiement estimée',
    'Search for supplier by name or ID': 'Rechercher un fournisseur par nom ou identifiant',
    'Would you like to send validation to the Supplier now?':
        'Souhaitez-vous envoyer la validation au fournisseur maintenant ?',
    'Send validation': 'Envoyer la validation',
    'Do it later': 'Le faire plus tard',
    'Invoice ID': 'Identifiant de facture',
    'Supplier ID': 'Identifiant fournisseur',
    'Date Created': 'Date de création',

    // ---- Sales: buyers ----
    'New Buyer': 'Nouvel acheteur',
    'Add Note': 'Ajouter une note',
    'Total Buyers': 'Total des acheteurs',
    'Pending Buyers': 'Acheteurs en attente',
    'Accepted Buyers': 'Acheteurs acceptés',
    'Declined Buyers': 'Acheteurs refusés',
    'Validated Buyers': 'Acheteurs validés',
    'Invite Buyers': 'Inviter des acheteurs',
    'Onboard Buyer': 'Intégrer un acheteur',
    'Invite a Single Buyer': 'Inviter un seul acheteur',
    'Bulk Invite Buyers': 'Inviter des acheteurs en masse',
    'How would you like to invite the buyer(s)?': 'Comment souhaitez-vous inviter le ou les acheteurs ?',
    'Search for buyer by name or ID': 'Rechercher un acheteur par nom ou identifiant',
    'Buyer ID': 'Identifiant acheteur',
    'Buyer Id': 'Identifiant acheteur',
    'Buyer Settings': 'Paramètres acheteur',
    'Invite link sent successfully': 'Lien d’invitation envoyé avec succès',
    'Email resent': 'E-mail renvoyé',
    'Resend Email': 'Renvoyer l’e-mail',
    'Edit Business Information': 'Modifier les informations de l’entreprise',
    'Edit Contact Information': 'Modifier les coordonnées',
    'Edit Physical Address': 'Modifier l’adresse physique',
    'Edit Delivery Address': 'Modifier l’adresse de livraison',
    'Add Financial Information': 'Ajouter les informations financières',
    'Edit Financial Information': 'Modifier les informations financières',
    'Edit Metadata': 'Modifier les métadonnées',
    'Enter first name': 'Saisissez le prénom',
    'Enter last name': 'Saisissez le nom',
    'Enter email': 'Saisissez l’e-mail',
    'Enter address': 'Saisissez l’adresse',
    'Enter postcode': 'Saisissez le code postal',
    'View on Maps': 'Voir sur la carte',

    // ---- Sales: received orders ----
    'Upload Received PO': 'Téléverser un bon de commande reçu',
    'Bulk Upload Sales Orders': 'Téléversement en masse de commandes clients',
    'Here are your uploaded Sales Orders': 'Voici vos commandes clients téléversées',
    'New Order': 'Nouvelle commande',
    'Create Order': 'Créer une commande',
    'Accepted Orders': 'Commandes acceptées',
    'Declined Orders': 'Commandes refusées',
    'Buyer Order No': 'N° de commande acheteur',
    'Your Purchase Order #:': 'Votre bon de commande n° :',
    'Change Purchase Order': 'Modifier le bon de commande',
    'Are you sure you want to change the Purchase Order？': 'Voulez-vous vraiment modifier le bon de commande ?',
    'New PO Uploaded Successfully': 'Nouveau bon de commande téléversé avec succès',
    'Purchase order accepted': 'Bon de commande accepté',
    'Purchase order declined': 'Bon de commande refusé',
    'Purchase order Declined': 'Bon de commande refusé',
    'Purchase order validated': 'Bon de commande validé',
    'Purchase order has been sent for Validation': 'Le bon de commande a été envoyé pour validation',

    // ---- Sales: invoices ----
    'Create Invoice': 'Créer une facture',
    'Edit Invoice': 'Modifier la facture',
    'Upload Invoices': 'Téléverser des factures',
    'Upload Single Invoice': 'Téléverser une seule facture',
    'Bulk Upload Invoice': 'Téléversement en masse de factures',
    'Here are your uploaded Invoices': 'Voici vos factures téléversées',
    'Invoice Information': 'Informations sur la facture',
    'Invoice Type': 'Type de facture',
    'Invoice Timeline': 'Chronologie de la facture',
    Proforma: 'Proforma',
    'Proforma Invoice': 'Facture proforma',
    'Create Proforma Invoice': 'Créer une facture proforma',
    'P.O. Number': 'N° de bon de commande',
    'Pay Date': 'Date de paiement',
    'Paid Date': 'Date de règlement',
    'Payment Due': 'Échéance de paiement',
    'Payment Reference': 'Référence de paiement',
    'Amount Due': 'Montant dû',
    'Outstanding Amt': 'Montant restant dû',
    'Amt Discounted': 'Montant escompté',
    'Amt Financed': 'Montant financé',
    'Date Financed': 'Date de financement',
    'Other costs': 'Autres frais',
    'Sub total': 'Sous-total',
    'Discount by': 'Remise par',
    'Sync with inventory': 'Synchroniser avec l’inventaire',
    'Money Out': 'Sorties',
    'New payment': 'Nouveau paiement',
    Settle: 'Régler',
    'Within 15 days': 'Sous 15 jours',
    'Within 30 days': 'Sous 30 jours',
    'Within 45 days': 'Sous 45 jours',
    'Within 60 days': 'Sous 60 jours',
    'Within 90 days': 'Sous 90 jours',
    'The date is incorrect': 'La date est incorrecte',
    'Total amount can not be greater than available invoice value':
        'Le montant total ne peut pas dépasser la valeur disponible de la facture',
    'Invoice created successfully': 'Facture créée avec succès',
    'Invoice updated successfully': 'Facture mise à jour avec succès',
    'Invoice approved successfully': 'Facture approuvée avec succès',
    'Invoice validated successfully': 'Facture validée avec succès',
    'Invoice Validated Successfully': 'Facture validée avec succès',
    'Sale Invoice validated successfully': 'Facture de vente validée avec succès',
    'Invoice deleted': 'Facture supprimée',
    'Invoice sent for approval': 'Facture envoyée pour approbation',
    'Invoice Requested for Approval': 'Approbation de la facture demandée',
    'Invoice returned for changes': 'Facture renvoyée pour modification',
    'Invoice sent to Buyer': 'Facture envoyée à l’acheteur',
    'Invoice sent to buyer': 'Facture envoyée à l’acheteur',
    'Validate Invoice': 'Valider la facture',
    'Return Sale Invoice for Changes': 'Renvoyer la facture de vente pour modification',
    'Add changes you would like made on the Sale Invoice':
        'Indiquez les modifications à apporter à la facture de vente',
    "Bank can't edit the invoice from here. You will have to come back from table to edit":
        'La banque ne peut pas modifier la facture ici. Vous devrez revenir depuis le tableau pour la modifier',

    // ---- Sales: quotations ----
    'Create Quote': 'Créer un devis',
    'Create Quotation': 'Créer un devis',
    'Delete Quotation': 'Supprimer le devis',
    'Quotation Timeline': 'Chronologie du devis',
    'Bulk Upload Quotation': 'Téléversement en masse de devis',
    'Bulk Upload Quotations': 'Téléversement en masse de devis',
    'Upload Bulk Quotations': 'Téléverser des devis en masse',
    'Here are your uploaded Quotations': 'Voici vos devis téléversés',
    'Are you sure that you want to delete this quotation?': 'Voulez-vous vraiment supprimer ce devis ?',
    'You can not use the current quotation number': 'Vous ne pouvez pas utiliser le numéro de devis actuel',
    'Quotation created successfully': 'Devis créé avec succès',
    'Quotation added/updated successfully': 'Devis ajouté/mis à jour avec succès',
    'Quotation approved successfully': 'Devis approuvé avec succès',
    'Quotation deleted': 'Devis supprimé',
    'Quotation returned for changes': 'Devis renvoyé pour modification',
    'Quotation has been returned for changes': 'Le devis a été renvoyé pour modification',
    'Quotation has been sent to buyer': 'Le devis a été envoyé à l’acheteur',
    'Return Quotation for Changes': 'Renvoyer le devis pour modification',
    'Add changes you would like made on the Quotation': 'Indiquez les modifications à apporter au devis',
    "Bank can't edit the quotation from here. You will have to come back from table to edit":
        'La banque ne peut pas modifier le devis ici. Vous devrez revenir depuis le tableau pour le modifier',
    'For 15 days': 'Pendant 15 jours',
    'For 30 days': 'Pendant 30 jours',
    'For 45 days': 'Pendant 45 jours',
    'For 60 days': 'Pendant 60 jours',
    'For 90 days': 'Pendant 90 jours',
    'Select terms': 'Sélectionnez les conditions',

    // ---- Sales: shipping and line items ----
    'Ship Via': 'Expédier via',
    'Ship via': 'Expédier via',
    'Shipping Method': 'Mode d’expédition',
    'Select shipping method': 'Sélectionnez le mode d’expédition',
    'Select shipping terms': 'Sélectionnez les conditions d’expédition',
    'Product Name': 'Nom du produit',
    'Enter Product name': 'Saisissez le nom du produit',
    'Enter SKU': 'Saisissez le SKU',
    'Edit product': 'Modifier le produit',
    Price: 'Prix',
    'Unit Price': 'Prix unitaire',

    // ---- Sales: shared actions and statuses ----
    'Request Approval': 'Demander l’approbation',
    'Approval Requested': 'Approbation demandée',
    'Approval requested': 'Approbation demandée',
    'Send to Buyer': 'Envoyer à l’acheteur',
    'Send For Validation': 'Envoyer pour validation',
    'Sent For Validation': 'Envoyé pour validation',
    'Would you like to send a validation request to the buyer now?':
        'Souhaitez-vous envoyer une demande de validation à l’acheteur maintenant ?',
    Validate: 'Valider',
    Validated: 'Validé',
    'Returned for Changes': 'Renvoyé pour modification',
    Uploaded: 'Téléversé',
    'Drag here or': 'Glissez ici ou',
    'Get Advance': 'Obtenir une avance',
    'Get Financing': 'Obtenir un financement',

    // ---- Accounts and wallets ----
    'New Account/Wallet': 'Nouveau compte / portefeuille',
    'Edit Account/Wallet': 'Modifier le compte / portefeuille',
    'Bank Account': 'Compte bancaire',
    'Mobile Wallet': 'Portefeuille mobile',
    Wallet: 'Portefeuille',
    'Bank/telco': 'Banque / opérateur',
    Default: 'Par défaut',
    'What would like to add between a bank account and a wallet?':
        'Souhaitez-vous ajouter un compte bancaire ou un portefeuille ?',
    'Bank Account added successfully!': 'Compte bancaire ajouté avec succès !',
    'Bank Account updated successfully!': 'Compte bancaire mis à jour avec succès !',
    'Mobile wallet added successfully!': 'Portefeuille mobile ajouté avec succès !',
    'Mobile wallet updated successfully!': 'Portefeuille mobile mis à jour avec succès !',
    'Enter Account Name': 'Saisissez le nom du compte',
    'Enter account name': 'Saisissez le nom du compte',
    'Enter Account Number': 'Saisissez le numéro de compte',
    'Enter account number': 'Saisissez le numéro de compte',
    'Enter account alias': 'Saisissez l’alias du compte',
    'Enter branch': 'Saisissez l’agence',
    'Enter branch code': 'Saisissez le code d’agence',
    'Branch Code': 'Code d’agence',
    'Branch Code:': 'Code d’agence :',
    'Branch:': 'Agence :',
    'Bank Code:': 'Code banque :',
    'Bank Name:': 'Nom de la banque :',
    'Bank:': 'Banque :',
    'Account No.': 'N° de compte',
    'Acc No.:': 'N° de compte :',
    'Account Number:': 'Numéro de compte :',
    'Bank Account Number:': 'Numéro de compte bancaire :',
    'Mobile Provider': 'Opérateur mobile',
    'Telco Provider:': 'Opérateur télécom :',
    'Till Number': 'Numéro de caisse',
    'Till No.:': 'N° de caisse :',
    'Pay Bill No.': 'N° de facturier',
    'Pay bill Number': 'Numéro de facturier',
    'Acc. Balance:': 'Solde du compte :',
    'Available Bal.:': 'Solde disponible :',
    'Current Bal.:': 'Solde actuel :',
    'Running Balance': 'Solde courant',
    'Money In:': 'Entrées :',
    'Money Out:': 'Sorties :',
    'Recent Transactions': 'Transactions récentes',
    'Transaction Details': 'Détails de la transaction',
    'Show More': 'Afficher plus',
    Clear: 'Effacer',
    'Add them': 'Les ajouter',

    // ---- Account statements and reconciliation ----
    'Account Statement': 'Relevé de compte',
    'Upload 6 Months Statement': 'Téléverser le relevé de 6 mois',
    'Current 6 months statement': 'Relevé des 6 derniers mois',
    'Select Statement period': 'Sélectionnez la période du relevé',
    'Statement End Date': 'Date de fin du relevé',
    'Select statement end date': 'Sélectionnez la date de fin du relevé',
    'Download and sign account connection authorization form below':
        'Téléchargez et signez le formulaire d’autorisation de connexion du compte ci-dessous',
    'Upload the signed Authorization form below': 'Téléversez le formulaire d’autorisation signé ci-dessous',
    'Current authorized form': 'Formulaire d’autorisation actuel',
    'Learn this reconciliation': 'Mémoriser ce rapprochement',
    'Unreconciled Amount': 'Montant non rapproché',
    'No. of Unreconciled Credit': 'Nombre de crédits non rapprochés',
    'No. of Unreconciled Debit': 'Nombre de débits non rapprochés',
    'Number of Credits': 'Nombre de crédits',
    'DR/CR': 'DB/CR',
    'DR/CR:': 'DB/CR :',
    'Raw materials purchase': 'Achat de matières premières',
    Rent: 'Loyer',
    Salary: 'Salaire',
    'VALID THRU': 'VALABLE JUSQU’AU',

    // ---- Payments ----
    'Make Payment': 'Effectuer un paiement',
    'Make Payments': 'Effectuer des paiements',
    'Receive Payments': 'Recevoir des paiements',
    'Make A Transfer': 'Effectuer un virement',
    'Make a Transfer': 'Effectuer un virement',
    'Pay an Invoice': 'Payer une facture',
    'Pay New Beneficiary': 'Payer un nouveau bénéficiaire',
    'Pay Saved/Registered Beneficiary': 'Payer un bénéficiaire enregistré',
    'Please select the type of payment you would like to make from below.':
        'Veuillez sélectionner ci-dessous le type de paiement que vous souhaitez effectuer.',
    'Is this a local or international(cross-border) payment?':
        'S’agit-il d’un paiement local ou international (transfrontalier) ?',
    'International (Cross-Border)': 'International (transfrontalier)',
    'Is this a payment against something, e.g., an invoice?':
        'Ce paiement porte-t-il sur un élément précis, par exemple une facture ?',
    'Send to bank or wallet?': 'Envoyer vers une banque ou un portefeuille ?',
    'Who will bear the cost of transfer': 'Qui supportera les frais de virement',
    'Payment Method': 'Mode de paiement',
    'Select payment method': 'Sélectionnez le mode de paiement',
    'Payment Account': 'Compte de paiement',
    'Select payment account': 'Sélectionnez le compte de paiement',
    'Select payment Type': 'Sélectionnez le type de paiement',
    'Select Account/ Wallet': 'Sélectionnez le compte / portefeuille',
    'Payment Details': 'Détails du paiement',
    'Payment Description': 'Description du paiement',
    'Payment date': 'Date de paiement',
    'Payment Date:': 'Date de paiement :',
    'Select Payment date': 'Sélectionnez la date de paiement',
    'Payment ID': 'Identifiant de paiement',
    'Payment ID:': 'Identifiant de paiement :',
    'Payment Ref Number:': 'Numéro de référence du paiement :',
    'Enter Payment Reference Number': 'Saisissez le numéro de référence du paiement',
    'Enter reference number': 'Saisissez le numéro de référence',
    'Reference Number:': 'Numéro de référence :',
    'Negotiated Reference': 'Référence négociée',
    'Amount to Pay': 'Montant à payer',
    'Enter amount': 'Saisissez le montant',
    'Mobile Payment Push': 'Notification de paiement mobile',
    'Send Push to:': 'Envoyer la notification à :',
    'Email payment notification': 'Notification de paiement par e-mail',
    'Send Payment Reminder': 'Envoyer un rappel de paiement',
    'Send Reminder via?': 'Envoyer le rappel via ?',
    'Payment Successful!': 'Paiement réussi !',
    'Payment was successful.': 'Le paiement a été effectué avec succès.',
    'Successful Payments': 'Paiements réussis',
    'Failed Payments': 'Paiements échoués',
    'Total Payments': 'Total des paiements',
    'Upload Supporting Document': 'Téléverser un justificatif',
    'Amounts greater than USD 10,000.00 require supporting document for anti-money laundering compliance purposes.':
        'Les montants supérieurs à 10 000,00 USD nécessitent un justificatif à des fins de conformité en matière de lutte contre le blanchiment d’argent.',

    // ---- Beneficiaries and counterparties ----
    'Beneficiary Details': 'Détails du bénéficiaire',
    'Beneficiary Name': 'Nom du bénéficiaire',
    'Beneficiary name': 'Nom du bénéficiaire',
    'Enter beneficiary name': 'Saisissez le nom du bénéficiaire',
    'Beneficiary added successfully!': 'Bénéficiaire ajouté avec succès !',
    'Remove Beneficiary': 'Supprimer le bénéficiaire',
    "Can't find beneficiary?": 'Bénéficiaire introuvable ?',
    'Search payee': 'Rechercher un bénéficiaire',
    'Search for Supplier': 'Rechercher un fournisseur',
    'Find Supplier/Anchor...': 'Rechercher un fournisseur / donneur d’ordre...',
    'Search for Supplier ID/ Buyer ID/ External ID': 'Rechercher par identifiant fournisseur, acheteur ou externe',
    'Search by Invoice No, supplier ID or supplier Name':
        'Rechercher par n° de facture, identifiant ou nom de fournisseur',
    'Buyer/ Supplier/ External ID:': 'Identifiant acheteur / fournisseur / externe :',
    'Supplier Payment Details:': 'Détails de paiement du fournisseur :',
    'Supplier Name': 'Nom du fournisseur',
    'Supplier ID:': 'Identifiant fournisseur :',
    'Supplier:': 'Fournisseur :',
    'Buyer Name': 'Nom de l’acheteur',

    // ---- Loan repayment ----
    'Repay A Loan': 'Rembourser un prêt',
    'Repay a Loan': 'Rembourser un prêt',
    'Select the loan and enter the amount you would like to repay':
        'Sélectionnez le prêt et saisissez le montant que vous souhaitez rembourser',
    'All Loans:': 'Tous les prêts :',
    'Repay Amt.': 'Montant du remboursement',
    'Outstanding Amount': 'Montant restant dû',
    'Outstanding Amount:': 'Montant restant dû :',
    'Outstanding Amt.': 'Montant restant dû',
    'Total Amount Financed': 'Montant total financé',
    'Financed Orders/Invoices': 'Commandes / factures financées',
    'Financing ID': 'Identifiant de financement',
    'Financing Type': 'Type de financement',
    'Distributor/Retail Financing': 'Financement distributeur / détaillant',
    'Working Capital': 'Fonds de roulement',
    'Invoice Amt.': 'Montant de la facture',
    'Invoice No.': 'N° de facture',

    // ---- Tax payments ----
    'Make Tax Payments': 'Effectuer des paiements d’impôts',
    'Type of Tax': 'Type d’impôt',
    'Select tax type': 'Sélectionnez le type d’impôt',
    'Advance Tax': 'Acompte d’impôt',
    'Agency Revenue': 'Recettes d’agence',
    'Capital Gains Tax': 'Impôt sur les plus-values',
    'Corporation Tax': 'Impôt sur les sociétés',
    'Customs Duty': 'Droits de douane',
    'Excise Duty': 'Droits d’accise',
    'Income Tax': 'Impôt sur le revenu',
    'Instalment Tax': 'Impôt par acomptes',
    'Rental Income Tax': 'Impôt sur les revenus locatifs',
    'Turnover Tax': 'Impôt sur le chiffre d’affaires',
    'Withholding Tax': 'Retenue à la source',

    // ---- Shared field labels ----
    'Amount:': 'Montant :',
    'Total:': 'Total :',
    'Total Amount:': 'Montant total :',
    'Currency:': 'Devise :',
    'Date:': 'Date :',
    'Name:': 'Nom :',
    'Email:': 'E-mail :',
    'SMS:': 'SMS :',
    'Status:': 'Statut :',
    'Type:': 'Type :',
    'Reference:': 'Référence :',
    'Document No.:': 'N° de document :',
    'Enter Code': 'Saisissez le code',
    'Enter OTP': 'Saisissez le code à usage unique',
    'Model type': 'Type de modèle',
    Graduated: 'Progressif',
    Packaged: 'Forfaitaire',
    Others: 'Autres',
    'Out of stock': 'Rupture de stock',

    // ---- Financing requests ----
    'Request Financing': 'Demander un financement',
    'Financing Amount': 'Montant du financement',
    'Amount Required': 'Montant requis',
    'Amount Required:': 'Montant requis :',
    'Date Required:': 'Date requise :',
    'Financed Orders': 'Commandes financées',
    'Invoices Discounted': 'Factures escomptées',
    'Finance ID': 'Identifiant de financement',
    'Financing Id': 'Identifiant de financement',
    'Financing Requests': 'Demandes de financement',
    'Total Financing Request:': 'Total des demandes de financement :',
    'Financing request created successfully': 'Demande de financement créée avec succès',
    'Financing request sent to bank successfully': 'Demande de financement envoyée à la banque avec succès',
    'Financing request returned for changes': 'Demande de financement renvoyée pour modification',
    'Return Financing request for Changes': 'Renvoyer la demande de financement pour modification',
    'Add changes you would like made on the financing request':
        'Indiquez les modifications à apporter à la demande de financement',
    'Delete Financing Request': 'Supprimer la demande de financement',
    'Financing Approved': 'Financement approuvé',
    'Approved Successfully': 'Approuvé avec succès',
    'Requested for Approval': 'Approbation demandée',
    'Request Validation': 'Demander la validation',
    'Validation requested': 'Validation demandée',
    'Validation Requests': 'Demandes de validation',
    'Send To Bank': 'Envoyer à la banque',

    // ---- Financing: document selection ----
    'Select Purchase Order or Invoice to finance:': 'Sélectionnez le bon de commande ou la facture à financer :',
    'Select at least one document to finance': 'Sélectionnez au moins un document à financer',
    'Select at least one document of buyer': 'Sélectionnez au moins un document de l’acheteur',
    'Select the invoice to discount:': 'Sélectionnez la facture à escompter :',
    'Enter or search for Buyer Purchase Order:': 'Saisissez ou recherchez le bon de commande de l’acheteur :',
    'P.O To Finance:': 'Bon de commande à financer :',
    'P.O/Invoice': 'Bon de commande / facture',
    'Supplier To Pay:': 'Fournisseur à payer :',
    'Buyer Documents': 'Documents de l’acheteur',
    'Buyer:': 'Acheteur :',
    'Find Buyer': 'Rechercher un acheteur',
    'Enter buyer name': 'Saisissez le nom de l’acheteur',
    'Search the Buyer': 'Rechercher l’acheteur',
    'Search the Supplier': 'Rechercher le fournisseur',
    'Search supplier by name': 'Rechercher un fournisseur par nom',
    'Add new supplier': 'Ajouter un nouveau fournisseur',
    'Invoice No': 'N° de facture',
    'Invoice/Order No': 'N° de facture / commande',
    'Doc No': 'N° de document',
    'O/S Amount': 'Montant restant dû',
    'Repyt Amount': 'Montant du remboursement',
    'Repayt Amount': 'Montant du remboursement',
    'Repyt Date': 'Date de remboursement',

    // ---- Financing: linked business ----
    'Business Id': 'Identifiant de l’entreprise',
    'Trading Name': 'Nom commercial',
    'VAT Number (Optional)': 'Numéro de TVA (facultatif)',
    'Link Business': 'Associer l’entreprise',
    'Associate this business with a validated business?': 'Associer cette entreprise à une entreprise validée ?',
    'Leave this page?': 'Quitter cette page ?',
    Leave: 'Quitter',
    Stay: 'Rester',
    Building: 'Bâtiment',
    Road: 'Rue',

    // ---- Guarantees and bonds ----
    'Guarantees/ Bonds': 'Garanties / cautions',
    'New Guarantee': 'Nouvelle garantie',
    'Guarantee ID': 'Identifiant de garantie',
    'Guarantee Type': 'Type de garantie',
    'Guarantee saved successfully': 'Garantie enregistrée avec succès',
    'Guarantee approval queue': 'File d’approbation des garanties',
    'Bond Financing': 'Financement de caution',
    'Advance Payment Bond': 'Caution d’acompte',
    'Advanced Payment Bond': 'Caution d’acompte',
    'Issued Bid Bonds': 'Cautions de soumission émises',
    'Issued Performance Bonds': 'Cautions de bonne exécution émises',
    'Issued Advanced Payment Bonds': 'Cautions d’acompte émises',
    Issued: 'Émise',
    Called: 'Appelée',

    // ---- Bid bond application ----
    'Get Express Bid Bonds In Minutes': 'Obtenez des cautions de soumission express en quelques minutes',
    'Bid Bond Issued Successfully': 'Caution de soumission émise avec succès',
    'Download Bid Bond': 'Télécharger la caution de soumission',
    'Cancel Draft Bid Bond': 'Annuler le brouillon de caution de soumission',
    'Your Bid Bond ID': 'Votre identifiant de caution de soumission',
    'Your Bid Bond will be sent to your email': 'Votre caution de soumission sera envoyée à votre adresse e-mail',
    'Your Bid Bond will be sent to your email.': 'Votre caution de soumission sera envoyée à votre adresse e-mail.',
    'Click the button below to download a copy': 'Cliquez sur le bouton ci-dessous pour en télécharger une copie',
    'Enter Bid Bond Addressee': 'Saisissez le destinataire de la caution de soumission',
    'Select bid validity': 'Sélectionnez la validité de l’offre',
    'Select validity period': 'Sélectionnez la période de validité',
    'Select start date': 'Sélectionnez la date de début',
    'Select Template': 'Sélectionnez un modèle',
    'Select One': 'Sélectionnez-en un',
    'Select All': 'Tout sélectionner',
    'Select the type of facility from below': 'Sélectionnez ci-dessous le type de facilité',
    'Facility Details': 'Détails de la facilité',
    'Application Date': 'Date de la demande',
    'Expiry Date': 'Date d’expiration',
    'Valid until:': 'Valable jusqu’au :',

    // ---- Tender and procuring entity ----
    'Tender Details': 'Détails de l’appel d’offres',
    'Tender Information': 'Informations sur l’appel d’offres',
    'Tender information': 'Informations sur l’appel d’offres',
    'Tender Number/Ref No.': 'Numéro / référence de l’appel d’offres',
    'Enter Tender Description': 'Saisissez la description de l’appel d’offres',
    'Upload Tender Document (optional)': 'Téléverser le dossier d’appel d’offres (facultatif)',
    'Upload Award letter': 'Téléverser la lettre d’attribution',
    'Uploaded Award letter': 'Lettre d’attribution téléversée',
    'Upload Documents': 'Téléverser des documents',
    Award: 'Attribution',
    'Procuring Entity': 'Entité adjudicatrice',
    'Procuring Entity/ Buyer Details': 'Détails de l’entité adjudicatrice / acheteur',
    'Procuring Entity/Buyer Details': 'Détails de l’entité adjudicatrice / acheteur',
    'Select procuring entity / buyer': 'Sélectionnez l’entité adjudicatrice / l’acheteur',
    'This Procuring entity has not been onboarded on our system. Please check with us again later':
        'Cette entité adjudicatrice n’est pas encore enregistrée dans notre système. Veuillez réessayer plus tard',
    'Third Party Name': 'Nom du tiers',
    'Signed Acceptance of Offer & Counter Indemnity': 'Acceptation de l’offre signée et contre-garantie',
    'T&Cs': 'Conditions générales',

    // ---- Bond payment and repayment ----
    'Account To Debit From': 'Compte à débiter',
    'How would you like to make payment': 'Comment souhaitez-vous effectuer le paiement',
    'Payment Amount - KES': 'Montant du paiement - KES',
    'Awaiting Payment': 'En attente de paiement',
    'Total Bonds Awaiting Payment': 'Total des cautions en attente de paiement',
    'Total Cost': 'Coût total',
    Commission: 'Commission',
    'Your payment is being processed. We will move you once its complete':
        'Votre paiement est en cours de traitement. Nous vous redirigerons une fois qu’il sera terminé',
    'Return to Dashboard': 'Retour au tableau de bord',
    'Repayment Amt': 'Montant du remboursement',
    'Repayment Date': 'Date de remboursement',
    'Repayment Ref': 'Référence du remboursement',
    'Date Repaid': 'Date de remboursement effectif',

    // ---- Bank review of bonds ----
    'Financial Review': 'Examen financier',
    'Financial Financial Statements:': 'États financiers :',
    'Balance Date': 'Date d’arrêté',
    Turnover: 'Chiffre d’affaires',
    Year: 'Année',
    'Approve all': 'Tout approuver',
    'Return all to creator': 'Tout renvoyer au créateur',
    'Return all to customer': 'Tout renvoyer au client',
    'Return to customer': 'Renvoyer au client',
    'Return for changes': 'Renvoyer pour modification',
    'What are the changes you would like made?': 'Quelles modifications souhaitez-vous apporter ?',
    'Assessment Completed Successfully.': 'Évaluation terminée avec succès.',
    'Generate Guarantee Letter': 'Générer la lettre de garantie',
    'Bond ID:': 'Identifiant de caution :',
    Cash: 'Espèces',
    'Counterparty indemnity Insurance': 'Assurance indemnitaire de contrepartie',
    'Select insurance underwriter': 'Sélectionnez l’assureur',
    'Enter Insurance Policy Number': 'Saisissez le numéro de police d’assurance',
    'Upload Insurance Policy': 'Téléverser la police d’assurance',
    'e.g. Mobile indemnity not signed': 'ex. : indemnité mobile non signée',

    // ---- Contract guarantees: overview ----
    'Advance Payment Guarantees': 'Garanties de restitution d’acompte',
    'Performance Guarantees': 'Garanties de bonne exécution',
    'Active advance payment guarantees': 'Garanties de restitution d’acompte actives',
    'Active performance guarantees': 'Garanties de bonne exécution actives',
    'Total advance covered': 'Total des acomptes couverts',
    'Total contract value covered': 'Valeur totale des contrats couverte',
    'Back to Guarantees/Bonds': 'Retour aux garanties / cautions',
    'Issued this month': 'Émises ce mois-ci',
    'Expiring in 30 days': 'Expire dans 30 jours',
    Outstanding: 'En cours',
    Stage: 'Étape',

    // ---- Contract guarantees: application ----
    'Guarantee Addressee': 'Destinataire de la garantie',
    'Guarantee Amt': 'Montant de la garantie',
    'Guarantee Terms': 'Conditions de la garantie',
    'Guarantee Validity': 'Validité de la garantie',
    'Select validity': 'Sélectionnez la validité',
    'Valid Till': 'Valable jusqu’au',
    'Custom (set expiry date)': 'Personnalisé (définir la date d’expiration)',
    'Extended claim period (days after expiry)': 'Période de réclamation prolongée (jours après expiration)',
    'Contract Ref': 'Référence du contrat',
    'Contract Value': 'Valeur du contrat',
    'Advance Amount': 'Montant de l’acompte',
    'Advance amount': 'Montant de l’acompte',
    Purpose: 'Objet',
    'Wording type': 'Type de rédaction',
    'Unconditional (on-demand)': 'Inconditionnelle (à première demande)',
    'Conditional (default certificate required)': 'Conditionnelle (certificat de défaillance requis)',
    'Conditional (proof of default required)': 'Conditionnelle (preuve de défaillance requise)',
    'With reducing balance clause': 'Avec clause de réduction progressive',
    'Reducing balance': 'Réduction progressive',
    'Reducing balance active': 'Réduction progressive active',
    'Reducing balance — amount reduces as deliveries are certified':
        'Réduction progressive — le montant diminue à mesure que les livraisons sont certifiées',
    'How will this guarantee be secured?': 'Comment cette garantie sera-t-elle couverte ?',
    '100% cash margin': 'Marge en espèces de 100 %',
    'Full guarantee value blocked on your account until expiry or release.':
        'La valeur totale de la garantie est bloquée sur votre compte jusqu’à l’expiration ou la mainlevée.',
    'Lien on fixed deposit': 'Nantissement d’un dépôt à terme',
    'Pledge an existing term deposit as security.': 'Nantissez un dépôt à terme existant en garantie.',
    'Existing guarantee facility': 'Ligne de garantie existante',
    'Utilise your approved bonds and guarantees line.': 'Utilisez votre ligne de cautions et garanties approuvée.',
    'Attach a document (optional)': 'Joindre un document (facultatif)',
    'Review & issue': 'Vérifier et émettre',
    'Save & exit': 'Enregistrer et quitter',
    'Request submitted to the bank': 'Demande soumise à la banque',
    'Your request has been saved as a draft.': 'Votre demande a été enregistrée en tant que brouillon.',
    'With bank for review': 'En cours d’examen par la banque',
    'Changes required': 'Modifications requises',
    'e.g. Procurement Officer': 'ex. : responsable des achats',

    // ---- Contract guarantees: advance confirmation and deliveries ----
    'Advance payment reference / SWIFT MT103': 'Référence du paiement d’acompte / SWIFT MT103',
    'MT103 reference': 'Référence MT103',
    'Advance payment receipt': 'Réception de l’acompte',
    'Advance receipt': 'Réception de l’acompte',
    'Awaiting advance confirmation': 'En attente de confirmation de l’acompte',
    'Confirm receipt': 'Confirmer la réception',
    'Confirmed in T24': 'Confirmé dans T24',
    'Confirmed on': 'Confirmé le',
    'Incoming payment reference': 'Référence du paiement entrant',
    'Certified deliveries': 'Livraisons certifiées',
    'Certified to date': 'Certifié à ce jour',
    'Record a certified delivery': 'Enregistrer une livraison certifiée',
    'Record delivery': 'Enregistrer la livraison',
    'Recorded by': 'Enregistré par',
    'Delivery / certificate reference': 'Référence de livraison / certificat',
    'No deliveries certified against this guarantee yet.':
        'Aucune livraison n’a encore été certifiée au titre de cette garantie.',
    'The certified delivery value must be a number greater than zero.':
        'La valeur de la livraison certifiée doit être un nombre supérieur à zéro.',
    'e.g. DN-2026-014': 'ex. : DN-2026-014',
    'The issued guarantee document has not been generated for this reference.':
        'Le document de garantie émis n’a pas été généré pour cette référence.',

    // ---- Contract guarantees: bank review ----
    'Awaiting decision': 'En attente de décision',
    'Approve & issue': 'Approuver et émettre',
    'Approve and issue this guarantee': 'Approuver et émettre cette garantie',
    'Approval note': 'Note d’approbation',
    'It takes effect once a second officer countersigns.':
        'Elle prend effet une fois qu’un second responsable a contresigné.',
    'Pending countersignature': 'En attente de contreseing',
    'Countersign & finalize': 'Contresigner et finaliser',
    'Countersigned by': 'Contresigné par',
    'Guarantee issued': 'Garantie émise',
    'Decline this request': 'Refuser cette demande',
    'Reason for decline': 'Motif du refus',
    'Request declined': 'Demande refusée',
    'Return for amendment': 'Renvoyer pour modification',
    'Returned to applicant': 'Renvoyé au demandeur',
    'What must change': 'Ce qui doit être modifié',
    'Send back to maker': 'Renvoyer à l’initiateur',
    'The maker can revise their decision.': 'L’initiateur peut revoir sa décision.',
    'The decision has been applied.': 'La décision a été appliquée.',
    'Query the applicant': 'Interroger le demandeur',
    'Raise a clarification query': 'Émettre une demande de clarification',
    'Send query & notify': 'Envoyer la demande et notifier',
    'Message to applicant': 'Message au demandeur',
    'Message to corporate user': 'Message à l’utilisateur entreprise',
    'The applicant can see it on the transaction thread.': 'Le demandeur peut le voir dans le fil de la transaction.',
    'The applicant sees this in the transaction message thread and the event is written to the audit trail.':
        'Le demandeur le voit dans le fil de messages de la transaction et l’événement est inscrit dans la piste d’audit.',
    'Select a category': 'Sélectionnez une catégorie',
    'Select a reason': 'Sélectionnez un motif',
    Note: 'Remarque',

    // ---- Contract guarantees: checks and facility ----
    Checks: 'Contrôles',
    'Automated checks': 'Contrôles automatisés',
    'Not automated': 'Non automatisé',
    'Not connected': 'Non connecté',
    'Manual stand-in': 'Traitement manuel de secours',
    'Missing collateral': 'Garantie manquante',
    'No checks have been run for this request.': 'Aucun contrôle n’a été effectué pour cette demande.',
    'Credit & facility': 'Crédit et facilité',
    'No facility information available.': 'Aucune information sur la facilité n’est disponible.',
    'Limit and utilisation are not yet read from the credit-limits service, so treat the blanks as unknown rather than zero.':
        'La limite et l’utilisation ne sont pas encore lues depuis le service des limites de crédit ; considérez donc les champs vides comme inconnus plutôt que comme nuls.',

    // ---- Secure messages ----
    'Secure Messages — linked to': 'Messages sécurisés — liés à',
    'Immutable message history · linked to guarantee reference':
        'Historique de messages inaltérable · lié à la référence de garantie',
    'Immutable message history · linked to': 'Historique de messages inaltérable · lié à',
    '· all events logged to the audit trail': '· tous les événements sont inscrits dans la piste d’audit',
    '· all events logged to audit trail': '· tous les événements sont inscrits dans la piste d’audit',
    'No messages yet on this transaction.': 'Aucun message pour cette transaction pour le moment.',
    'No activity recorded yet.': 'Aucune activité enregistrée pour le moment.',
    'Write a message to the bank': 'Écrivez un message à la banque',
    'Your message could not be sent. Please try again.': 'Votre message n’a pas pu être envoyé. Veuillez réessayer.',
    reference: 'référence',

    // ---- Business settings ----
    'Business Settings': 'Paramètres de l’entreprise',
    'Team & Security Settings': 'Paramètres d’équipe et de sécurité',
    'Bank Account/Wallet': 'Compte bancaire / portefeuille',
    'Bank Accounts/Wallets': 'Comptes bancaires / portefeuilles',
    'Director Information': 'Informations sur les dirigeants',
    Directors: 'Dirigeants',
    'ID Doc Number': 'Numéro de pièce d’identité',
    'Company Registration Number': 'Numéro d’immatriculation de la société',
    'CRB Certificate': 'Certificat CRB',
    Certificate: 'Certificat',
    'Credit Report': 'Rapport de solvabilité',
    Financials: 'Données financières',
    'Lending Ratios': 'Ratios de prêt',
    Metadata: 'Métadonnées',
    'Mobile Indemnity Agreement': 'Convention d’indemnité mobile',
    'View uploaded version': 'Voir la version téléversée',
    'We/I have read and agree to these terms and conditions':
        'Nous avons / j’ai lu et accepté ces conditions générales',
    'Reset Account for KYC': 'Réinitialiser le compte pour le KYC',
    'Account reset for KYC': 'Compte réinitialisé pour le KYC',
    'Are you sure you want to reset your account for KYC ?':
        'Voulez-vous vraiment réinitialiser votre compte pour le KYC ?',

    // ---- Team and users ----
    'New Member': 'Nouveau membre',
    'Invite New User': 'Inviter un nouvel utilisateur',
    Invite: 'Inviter',
    'Resend Invite': 'Renvoyer l’invitation',
    'Edit User': 'Modifier l’utilisateur',
    'User has been invited!': 'L’utilisateur a été invité !',
    'User has been updated!': 'L’utilisateur a été mis à jour !',
    'Are you sure you want to delete the selected user ?': 'Voulez-vous vraiment supprimer l’utilisateur sélectionné ?',
    'Enter the email address of the user you would like to invite, and choose the roles he/she should have.':
        'Saisissez l’adresse e-mail de l’utilisateur que vous souhaitez inviter et choisissez les rôles à lui attribuer.',
    'Enter the email address of the users you would like to invite, and choose the roles they should have.':
        'Saisissez les adresses e-mail des utilisateurs que vous souhaitez inviter et choisissez les rôles à leur attribuer.',
    'Type or paste email addresses and press `Enter`...':
        'Saisissez ou collez des adresses e-mail puis appuyez sur « Entrée »...',
    'Best for company owners and business administrators':
        'Idéal pour les propriétaires d’entreprise et les administrateurs',
    You: 'Vous',

    // ---- Branding ----
    Branding: 'Image de marque',
    'Brand Elements': 'Éléments de marque',
    'Brand Color': 'Couleur de la marque',
    'Accent Color': 'Couleur d’accentuation',
    'Customize how your brand appears globally across': 'Personnalisez l’apparence de votre marque sur',
    'and how your partners view your brand. Add your logo and brand colors.':
        'et la façon dont vos partenaires perçoivent votre marque. Ajoutez votre logo et vos couleurs.',

    // ---- Fraud central ----
    'Fraud Central': 'Centre antifraude',
    'Fraud Rules': 'Règles antifraude',
    Blacklist: 'Liste noire',
    'Add To Blacklist': 'Ajouter à la liste noire',
    'New key word': 'Nouveau mot-clé',
    'Add another item': 'Ajouter un autre élément',
    'Add another note': 'Ajouter une autre note',
    'Edit Notes': 'Modifier les notes',

    // ---- Developer and connected applications ----
    'Connected Applications': 'Applications connectées',
    'Application Name': 'Nom de l’application',
    'API Key': 'Clé d’API',
    Organization: 'Organisation',
    'Organization Type': 'Type d’organisation',
    'Connection Type': 'Type de connexion',
    'Last Activity': 'Dernière activité',
    'Last Used': 'Dernière utilisation',

    // ---- Profile security ----
    'Security History': 'Historique de sécurité',
    'Last Login': 'Dernière connexion',
    'IP Address': 'Adresse IP',
    'Login Sessions': 'Sessions de connexion',
    'Sign out all other sessions': 'Déconnecter toutes les autres sessions',
    'List of accounts to which you are a member of': 'Liste des comptes dont vous êtes membre',
    '2 Factor Authentication Enabled': 'Authentification à deux facteurs activée',
    'Two Step Authentication': 'Authentification en deux étapes',
    'Two-Step': 'Deux étapes',
    'Add Authentication': 'Ajouter une authentification',
    'Disable Authentication': 'Désactiver l’authentification',
    'Disable Two Factor Authentication': 'Désactiver l’authentification à deux facteurs',
    Disable: 'Désactiver',
    'Use 6 digit code': 'Utiliser un code à 6 chiffres',
    'Download Codes': 'Télécharger les codes',
    'Download and store these backup codes somewhere safe. If you lose access to your authentication device, you can use any of these codes to login to your account.':
        'Téléchargez ces codes de secours et conservez-les en lieu sûr. Si vous perdez l’accès à votre appareil d’authentification, vous pourrez utiliser l’un de ces codes pour vous connecter à votre compte.',
    'Keep your account extra secure with a second authentication step.':
        'Renforcez la sécurité de votre compte grâce à une deuxième étape d’authentification.',
    'Once you enable either SMS or authenticator app, you will be able to add security keys.':
        'Une fois le SMS ou l’application d’authentification activé, vous pourrez ajouter des clés de sécurité.',
    'To disable 2FA, enter the 6-digit verification code generated by your authenticator app below.':
        'Pour désactiver la 2FA, saisissez ci-dessous le code de vérification à 6 chiffres généré par votre application d’authentification.',
    'Edit Password': 'Modifier le mot de passe',
    'Current Password': 'Mot de passe actuel',
    'Enter Current Password': 'Saisissez le mot de passe actuel',
    'Password:': 'Mot de passe :',

    // ---- Shared components: forms and addresses ----
    'Add Account / Wallet': 'Ajouter un compte / portefeuille',
    'Add Address': 'Ajouter une adresse',
    'Add address': 'Ajouter une adresse',
    'Add new address': 'Ajouter une nouvelle adresse',
    'Add Attachment': 'Ajouter une pièce jointe',
    'Add Buyer': 'Ajouter un acheteur',
    'Add Signatory': 'Ajouter un signataire',
    'Signatory Name': 'Nom du signataire',
    'Attach Product': 'Joindre un produit',
    'Attach as PDF': 'Joindre au format PDF',
    'Billing Address': 'Adresse de facturation',
    'Building:': 'Bâtiment :',
    'Road:': 'Rue :',
    'Change Delivery Address': 'Modifier l’adresse de livraison',
    'Is the pickup Address same as the Physical Address?':
        'L’adresse d’enlèvement est-elle la même que l’adresse physique ?',
    'Pick Up': 'Enlèvement',
    'Select country': 'Sélectionnez le pays',
    'Select county': 'Sélectionnez le département',
    'Select cost': 'Sélectionnez le coût',
    'Select User': 'Sélectionnez un utilisateur',
    'National ID': 'Carte nationale d’identité',
    'Reference No.': 'N° de référence',
    'Reference Number': 'Numéro de référence',
    'Successfully Updated': 'Mise à jour effectuée',

    // ---- Shared components: buyers and products ----
    'Change buyer': 'Changer d’acheteur',
    'Are you sure you want to change the Buyer? This will reset the changes made in product table.':
        'Voulez-vous vraiment changer d’acheteur ? Les modifications apportées au tableau des produits seront réinitialisées.',
    'Search buyer by name': 'Rechercher un acheteur par nom',
    'Search beneficiary by name': 'Rechercher un bénéficiaire par nom',
    'Entity/Buyer': 'Entité / acheteur',
    'Enter Entity/buyer': 'Saisissez l’entité / l’acheteur',
    'Enter entity/ buyer': 'Saisissez l’entité / l’acheteur',
    'Procuring entity added': 'Entité adjudicatrice ajoutée',
    'No invoices found for selected buyer': 'Aucune facture trouvée pour l’acheteur sélectionné',
    'No purchase orders found for selected buyer': 'Aucun bon de commande trouvé pour l’acheteur sélectionné',
    'No purchase orders or invoices found for selected supplier':
        'Aucun bon de commande ni facture trouvé pour le fournisseur sélectionné',
    'You can not use the current ID. Please reload the page':
        'Vous ne pouvez pas utiliser l’identifiant actuel. Veuillez recharger la page',
    Product: 'Produit',
    'Product Id:': 'Identifiant du produit :',
    'Product name:': 'Nom du produit :',
    Items: 'Articles',

    // ---- Shared components: cards and wallets ----
    'Card Number': 'Numéro de carte',
    'CVV Number': 'Cryptogramme visuel',
    'Expiry Month': 'Mois d’expiration',
    'Expiry Year': 'Année d’expiration',
    'Select Expiry month': 'Sélectionnez le mois d’expiration',
    'Select Expiry Year': 'Sélectionnez l’année d’expiration',
    'Wallet Type:': 'Type de portefeuille :',
    'Wallet/Till Number': 'Numéro de portefeuille / caisse',
    'Enter Wallet/ Till Number': 'Saisissez le numéro de portefeuille / caisse',
    'Select wallet': 'Sélectionnez le portefeuille',
    'Select mobile money provider': 'Sélectionnez l’opérateur de mobile money',
    'M-Pesa Account Number': 'Numéro de compte M-Pesa',
    'M-Pesa Business Number': 'Numéro professionnel M-Pesa',
    'Enter M-Pesa Account Number': 'Saisissez le numéro de compte M-Pesa',
    'Enter M-Pesa Business Number': 'Saisissez le numéro professionnel M-Pesa',
    'm-Pesa Account Number:': 'Numéro de compte M-Pesa :',
    'm-Pesa Business Number:': 'Numéro professionnel M-Pesa :',
    'Enter SWIFT Code': 'Saisissez le code SWIFT',
    Banks: 'Banques',
    'Select Banks': 'Sélectionnez les banques',
    'Select Bank/Telco': 'Sélectionnez la banque / l’opérateur',
    'Telco Providers': 'Opérateurs télécom',
    'Bank Product': 'Produit bancaire',
    'Bank Slip/Reference': 'Bordereau / référence bancaire',
    'Upload Receipt/Bank Slip': 'Téléverser le reçu / bordereau bancaire',
    'Account Name:': 'Nom du compte :',

    // ---- Shared components: payment ----
    'Select Payment Method': 'Sélectionnez le mode de paiement',
    'Debit My Account': 'Débiter mon compte',
    'Please pay to the following account:': 'Veuillez payer sur le compte suivant :',
    'Pay Bill': 'Payer la facture',
    Pay: 'Payer',
    'Enter the mobile number you wish to pay from and we will send you an STK push.':
        'Saisissez le numéro de mobile depuis lequel vous souhaitez payer et nous vous enverrons une notification STK.',
    'Payment push has been sent to': 'La notification de paiement a été envoyée au',
    'Wrong number?': 'Mauvais numéro ?',
    'Payment failed.': 'Le paiement a échoué.',

    // ---- Shared components: financing summary ----
    'Amount Requested:': 'Montant demandé :',
    'Amount You Will receive:': 'Montant que vous recevrez :',
    'Financing Amount:': 'Montant du financement :',
    'Financing Request:': 'Demande de financement :',
    'Get Financing From:': 'Obtenir un financement auprès de :',
    'Get Discounting From:': 'Obtenir un escompte auprès de :',
    'How to request for financing': 'Comment demander un financement',
    'Discount Amount:': 'Montant de l’escompte :',
    'Discounting Details:': 'Détails de l’escompte :',
    'Total Discounting Request:': 'Total de la demande d’escompte :',
    'Invoice Amount:': 'Montant de la facture :',
    'Invoice:': 'Facture :',
    'Fees & Charges:': 'Frais et commissions :',
    'Interest & Charges:': 'Intérêts et commissions :',
    'Available Limit:': 'Limite disponible :',
    'Limit:': 'Limite :',
    Repay: 'Rembourser',
    Repayments: 'Remboursements',
    'Repayment Amount:': 'Montant du remboursement :',
    'Repayment Date:': 'Date de remboursement :',
    'Repayment Details:': 'Détails du remboursement :',
    'Total Repayment Amount:': 'Montant total du remboursement :',
    'Loan Duration:': 'Durée du prêt :',
    'Facility duration:': 'Durée de la facilité :',
    'Duration:': 'Durée :',
    'Due in': 'Échéance dans',
    'For:': 'Pour :',
    'Details:': 'Détails :',
    'Doc Number:': 'N° de document :',
    'Doc Value:': 'Valeur du document :',
    'Doc type:': 'Type de document :',
    'No Terms has been set for this service': 'Aucune condition n’a été définie pour ce service',
    'What would you like to get?': 'Que souhaitez-vous obtenir ?',
    'Not on this list?': 'Absent de cette liste ?',

    // ---- Shared components: uploads, messaging and branding ----
    'Document must smaller than 25 MB!': 'Le document doit faire moins de 25 Mo !',
    'No file present': 'Aucun fichier',
    'Change Avatar': 'Changer d’avatar',
    'Profile Picture updated successfully': 'Photo de profil mise à jour avec succès',
    'Remove Logo': 'Supprimer le logo',
    'Brand elements Saved Successfully': 'Éléments de marque enregistrés avec succès',
    'Brand elements Updated Successfully': 'Éléments de marque mis à jour avec succès',
    'Line Spacing': 'Interligne',
    Single: 'Simple',
    Subject: 'Objet',
    'Subject:': 'Objet :',
    'Edit Subject': 'Modifier l’objet',
    'Send Email': 'Envoyer l’e-mail',
    'Email sent': 'E-mail envoyé',
    'Send a copy to myself at': 'M’envoyer une copie à',
    'Send registration email to': 'Envoyer l’e-mail d’inscription à',
    'New Notes': 'Nouvelles notes',
    'Posting as': 'Publication en tant que',
    'Type a message…': 'Saisissez un message…',
    'Go to my dashboard': 'Aller à mon tableau de bord',
    'Got questions? Please let us know': 'Des questions ? Faites-le-nous savoir',
    'Learn more about': 'En savoir plus sur',
    'No Internet Connection': 'Aucune connexion Internet',
    'Check your connection, then refresh the page.': 'Vérifiez votre connexion, puis actualisez la page.',

    // ---- Shared components: date ranges and shipping ----
    'Last 7 Days': '7 derniers jours',
    'Last 1 Month': 'Dernier mois',
    'Last 3 Month': '3 derniers mois',
    'Last 6 Month': '6 derniers mois',
    'Last 1 Year': 'Dernière année',
    'Month to date': 'Mois en cours',
    'Quarter to date': 'Trimestre en cours',
    'Year to date': 'Année en cours',
    From: 'De',
    'From:': 'De :',
    To: 'À',
    Air: 'Aérien',
    Ship: 'Maritime',
    Truck: 'Routier',
    Trail: 'Ferroviaire',

    // ---- Letter of credit: identification ----
    'LC Details': 'Détails du crédit documentaire',
    'LC ID': 'Identifiant du crédit documentaire',
    'LC Number': 'Numéro du crédit documentaire',
    'LC Reference': 'Référence du crédit documentaire',
    'LC reference:': 'Référence du crédit documentaire :',
    'Doc ID': 'Identifiant du document',
    'Documentary Credit No.': 'N° de crédit documentaire',
    'Type of Credit': 'Type de crédit',
    'Credit Type & Parties': 'Type de crédit et parties',
    'Created Date': 'Date de création',
    'Issued by': 'Émis par',
    'Issued on': 'Émis le',
    'Edit LC': 'Modifier le crédit documentaire',
    'View LC': 'Voir le crédit documentaire',
    'View letter of credit': 'Voir la lettre de crédit',
    'View application': 'Voir la demande',
    'Get Another LC': 'Obtenir un autre crédit documentaire',
    'Draft saved': 'Brouillon enregistré',
    'Application created': 'Demande créée',
    'Application completed': 'Demande complétée',

    // ---- Letter of credit: amount ----
    'Credit Amount': 'Montant du crédit',
    'Amount & Availability': 'Montant et réalisation',
    'Amount (use ISO currency code)': 'Montant (utilisez le code devise ISO)',
    'Amount in figures': 'Montant en chiffres',
    'Amount in words': 'Montant en lettres',
    'Filled in automatically from the amount in figures': 'Rempli automatiquement à partir du montant en chiffres',
    'Amount Tolerance(%) +/-': 'Tolérance sur le montant (%) +/-',
    'Amount Tolerance(%): +/-': 'Tolérance sur le montant (%) : +/-',
    'Tolerance +/− (%)': 'Tolérance +/− (%)',
    'Tolerance +/− (%) optional': 'Tolérance +/− (%) facultative',
    'LC Amount & Tolerance:': 'Montant et tolérance du crédit documentaire :',
    'LC Amount Required:': 'Montant du crédit documentaire requis :',
    'Exchange Rate': 'Taux de change',
    'Current Exchange Rates:': 'Taux de change actuels :',
    'FX Code:': 'Code devise :',

    // ---- Letter of credit: parties and banks ----
    'Applicant (name and address)': 'Donneur d’ordre (nom et adresse)',
    'Applicant (us)': 'Donneur d’ordre (nous)',
    'Beneficiary address': 'Adresse du bénéficiaire',
    "Beneficiary's country": 'Pays du bénéficiaire',
    "Any bank in beneficiary's country": 'Toute banque dans le pays du bénéficiaire',
    'Advise through Bank subsidiary, agent or affiliate in beneficiary’s country':
        'Notifier via une filiale, un agent ou un affilié de la banque dans le pays du bénéficiaire',
    "Advise through — beneficiary's bankers (SWIFT code / bank / branch / account number)":
        'Notifier via — les banquiers du bénéficiaire (code SWIFT / banque / agence / numéro de compte)',
    'Enter advising bank': 'Saisissez la banque notificatrice',
    'Available with (nominated bank)': 'Réalisable auprès de (banque désignée)',
    'SWIFT Code': 'Code SWIFT',
    'Account No': 'N° de compte',
    'Contact Name': 'Nom du contact',
    'Supplier Email': 'E-mail du fournisseur',
    'Supplier Phone': 'Téléphone du fournisseur',
    'Supplier id': 'Identifiant fournisseur',
    'Are you sure you want to change the supplier? This will reset all changes made':
        'Voulez-vous vraiment changer de fournisseur ? Toutes les modifications apportées seront réinitialisées',

    // ---- Letter of credit: availability and payment terms ----
    'Available by': 'Réalisable par',
    'Select availability': 'Sélectionnez le mode de réalisation',
    'Availability / maturity': 'Réalisation / échéance',
    'Drafts at (Terms)': 'Traites à (conditions)',
    'Drafts on (Bank)': 'Traites sur (banque)',
    'Drafts:': 'Traites :',
    'Sight Payment': 'Paiement à vue',
    'Deferred Payment': 'Paiement différé',
    'Deferred payment terms': 'Conditions de paiement différé',
    'Mixed Payment': 'Paiement mixte',
    Acceptance: 'Acceptation',
    Negotiation: 'Négociation',
    'Select LC Terms of Payment:': 'Sélectionnez les conditions de paiement du crédit documentaire :',
    'Number of Days': 'Nombre de jours',
    'Number of days': 'Nombre de jours',

    // ---- Letter of credit: expiry and presentation ----
    'Date Of Expiry': 'Date d’expiration',
    'Date of expiry': 'Date d’expiration',
    'Expiry date': 'Date d’expiration',
    'Expiry date & place for presentation of documents': 'Date d’expiration et lieu de présentation des documents',
    'Place Of Expiry': 'Lieu d’expiration',
    'Place for presentation': 'Lieu de présentation',
    'LC Expiry:': 'Expiration du crédit documentaire :',
    'Documents to be presented within (days after shipment date)':
        'Documents à présenter dans un délai de (jours après la date d’expédition)',

    // ---- Letter of credit: transferability and confirmation ----
    'Is your Letter Of Credit transferable?': 'Votre lettre de crédit est-elle transférable ?',
    'Does the LC require a confirmation?': 'Le crédit documentaire nécessite-t-il une confirmation ?',
    'Confirmation instructions': 'Instructions de confirmation',
    'Confirmation Charges': 'Frais de confirmation',
    'Confirmation Message': 'Message de confirmation',
    'Confirmed by': 'Confirmé par',
    'Last Confirmed on': 'Dernière confirmation le',
    'May add': 'Peut ajouter',
    Without: 'Sans',
    Allowed: 'Autorisé',
    'Not allowed': 'Non autorisé',

    // ---- Letter of credit: proforma invoice and import declaration ----
    'Order No.': 'N° de commande',
    'Order Value': 'Valeur de la commande',
    'View Order': 'Voir la commande',
    'Proforma Invoice No.': 'N° de facture proforma',
    'Proforma Invoice Number': 'Numéro de facture proforma',
    'Proforma Invoice Date': 'Date de la facture proforma',
    'Proforma Invoice Amount': 'Montant de la facture proforma',
    'Add Proforma Invoice': 'Ajouter une facture proforma',
    'Select Proforma Invoice': 'Sélectionnez la facture proforma',
    'Upload Proforma Invoice': 'Téléverser la facture proforma',
    'View Proforma Invoice': 'Voir la facture proforma',
    'Proforma Invoice (PI) or Sales Contract*': 'Facture proforma (PI) ou contrat de vente*',
    'Invoice Value': 'Valeur de la facture',
    'View Invoice': 'Voir la facture',
    'FOB Value': 'Valeur FOB',
    'IDF Number': 'Numéro IDF',
    'IDF number': 'Numéro IDF',
    'IDF Date': 'Date de l’IDF',
    'IDF amount': 'Montant de l’IDF',
    'IDF(Import Declaration Form):': 'IDF (formulaire de déclaration d’importation) :',
    'Upload IDF (Import Declaration Form)': 'Téléverser l’IDF (formulaire de déclaration d’importation)',
    'Upload IDF(Import Declaration Form)': 'Téléverser l’IDF (formulaire de déclaration d’importation)',
    'View IDF': 'Voir l’IDF',
    'Import Declaration Value': 'Valeur de la déclaration d’importation',
    'Import License*': 'Licence d’importation*',
    'Enter UCR': 'Saisissez la référence UCR',

    // ---- Letter of credit: goods ----
    'Goods & Shipment': 'Marchandises et expédition',
    'Brief description of the goods': 'Brève description des marchandises',
    'Description of goods and/or services (including quantity and unit price)':
        'Description des marchandises et/ou services (y compris la quantité et le prix unitaire)',
    'Product Description': 'Description du produit',
    'Unit of Quantity': 'Unité de quantité',
    'HS Code': 'Code SH',
    'New/Used': 'Neuf / occasion',
    'View import products': 'Voir les produits importés',

    // ---- Letter of credit: shipment ----
    'Shipment Details': 'Détails de l’expédition',
    'Shipment Terms': 'Conditions d’expédition',
    'Shipment Terms:': 'Conditions d’expédition :',
    'Shipment Tracking': 'Suivi de l’expédition',
    'Additional Shipment Info': 'Informations complémentaires sur l’expédition',
    'Incoterms (Shipping Terms)': 'Incoterms (conditions d’expédition)',
    'Trade term': 'Incoterm',
    'Select trade term': 'Sélectionnez l’incoterm',
    'Other trade term': 'Autre incoterm',
    'Enter other trade term': 'Saisissez l’autre incoterm',
    'Named location': 'Lieu convenu',
    'Enter named location': 'Saisissez le lieu convenu',
    'Named location, final destination, other trade term': 'Lieu convenu, destination finale, autre incoterm',
    'Final destination': 'Destination finale',
    'Enter final destination': 'Saisissez la destination finale',
    'Mode of transport': 'Mode de transport',
    'Select mode': 'Sélectionnez le mode',
    'Place of receipt': 'Lieu de prise en charge',
    'Port of loading': 'Port de chargement',
    'Port of loading:': 'Port de chargement :',
    'Port Of Discharge': 'Port de déchargement',
    'Port of discharge': 'Port de déchargement',
    'Port of discharge::': 'Port de déchargement :',
    'Origin Service Area': 'Zone de service d’origine',
    'Destination Service Area': 'Zone de service de destination',
    'Latest Shipment Date': 'Date limite d’expédition',
    'Latest shipment date': 'Date limite d’expédition',
    'Last Shipment Date:': 'Date limite d’expédition :',
    'Is partial shipment allowed?': 'Les expéditions partielles sont-elles autorisées ?',
    'Is transhipment allowed?': 'Le transbordement est-il autorisé ?',
    'Part shipments': 'Expéditions partielles',
    Transshipments: 'Transbordements',
    'Freight Prepaid Terms': 'Conditions de fret payé d’avance',
    'Freight Collect Terms': 'Conditions de fret payable à destination',
    'Shipping marks': 'Marques d’expédition',
    'Enter shipping marks': 'Saisissez les marques d’expédition',
    'Shipping marks, other transport & other documents':
        'Marques d’expédition, autres documents de transport et autres documents',

    // ---- Letter of credit: documents required ----
    'Documents Required': 'Documents requis',
    'Shipping Documents': 'Documents d’expédition',
    'Supporting Documents': 'Pièces justificatives',
    'Document Tracking': 'Suivi des documents',
    'No. of originals': 'Nombre d’originaux',
    'No. of copies': 'Nombre de copies',
    Originals: 'Originaux',
    Copies: 'Copies',
    'Signed Commercial Invoices': 'Factures commerciales signées',
    'Signed Commercial Invoices (copies)': 'Factures commerciales signées (copies)',
    'Signed Commercial Invoices(copies)': 'Factures commerciales signées (copies)',
    'Signed Commercial Invoices(originals)': 'Factures commerciales signées (originaux)',
    'Signed commercial invoice': 'Facture commerciale signée',
    'Packing List (copies)': 'Liste de colisage (copies)',
    'Packing List(copies)': 'Liste de colisage (copies)',
    'Packing List(originals)': 'Liste de colisage (originaux)',
    'Packing list': 'Liste de colisage',
    'Weight list': 'Note de poids',

    // ---- Letter of credit: transport documents ----
    'Bill of lading': 'Connaissement',
    'Airway Bill': 'Lettre de transport aérien',
    'Airways Bill': 'Lettre de transport aérien',
    'Air waybill': 'Lettre de transport aérien',
    'Airway Bill/Bill Of Lading Number': 'Numéro de lettre de transport aérien / connaissement',
    'Document way Bill Number': 'Numéro de lettre de voiture',
    'Air/Sea parcel post receipts': 'Récépissés postaux aériens / maritimes',
    'Combined Transport Document': 'Document de transport combiné',
    'Truck Receipt/Rail/Road consignment note': 'Récépissé routier / lettre de voiture ferroviaire ou routière',
    'Other transport documents': 'Autres documents de transport',
    'Enter other transport documents': 'Saisissez les autres documents de transport',

    // ---- Letter of credit: certificates and insurance ----
    'Certificate of origin': 'Certificat d’origine',
    'Certificate of Analysis': 'Certificat d’analyse',
    'Certificate of analysis': 'Certificat d’analyse',
    'Certificate of Conformity/Pre-Shipment Verification Certificate':
        'Certificat de conformité / certificat de vérification avant expédition',
    'Certificate of conformity / PVoC': 'Certificat de conformité / PVoC',
    'Certificate of health': 'Certificat sanitaire',
    'Health certificate': 'Certificat sanitaire',
    'Fumigation certificate': 'Certificat de fumigation',
    'Safety Data Sheet': 'Fiche de données de sécurité',
    'Insurance Policy / Certificate*': 'Police / certificat d’assurance*',
    'Insurance policy / certificate': 'Police / certificat d’assurance',
    'Insurance Policy/Certificate Number': 'Numéro de police / certificat d’assurance',
    'Insurance Policy/Certificate Amount': 'Montant de la police / du certificat d’assurance',
    'Insurance Certificate Number': 'Numéro du certificat d’assurance',
    'Insurance Certificate Date': 'Date du certificat d’assurance',
    'Insurance cover note for 110% value of the invoice:':
        'Note de couverture d’assurance pour 110 % de la valeur de la facture :',
    'Upload Insurance Policy/Certificate': 'Téléverser la police / le certificat d’assurance',
    'Upload Insurance Policy Certificate (note for 110% value of the invoice)':
        'Téléverser le certificat d’assurance (note pour 110 % de la valeur de la facture)',
    'Other Documents': 'Autres documents',
    'Other documents': 'Autres documents',
    'Enter other documents': 'Saisissez les autres documents',
    'Enter other document name and press enter (↵)': 'Saisissez le nom de l’autre document et appuyez sur Entrée (↵)',
    'Document must smaller than 2MB!': 'Le document doit faire moins de 2 Mo !',
    'Reading this document…': 'Lecture du document…',

    // ---- Letter of credit: charges ----
    'Conditions & Charges': 'Conditions et frais',
    'Conditions, Charges & Undertaking': 'Conditions, frais et engagement',
    'Reimbursement & Undertaking': 'Remboursement et engagement',
    'LC Charges': 'Frais du crédit documentaire',
    'LC issuance commission': 'Commission d’émission du crédit documentaire',
    'SWIFT / handling fee': 'Frais SWIFT / de traitement',
    'Other Charges': 'Autres frais',
    'Total Charges': 'Total des frais',
    'Total payable': 'Total à payer',
    'Total Applicant Charges:': 'Total des frais du donneur d’ordre :',
    'Total Beneficiary Charges:': 'Total des frais du bénéficiaire :',
    'Charges Payable By:': 'Frais à la charge de :',
    'Charges payable by:': 'Frais à la charge de :',
    "All bank charges other than the issuing bank's are for the account of":
        'Tous les frais bancaires autres que ceux de la banque émettrice sont à la charge de',
    'Other charges outside the issuing bank payable by:':
        'Autres frais en dehors de la banque émettrice à la charge de :',
    'Please select the Account Number for the collection of applicant charges':
        'Veuillez sélectionner le numéro de compte pour le prélèvement des frais du donneur d’ordre',
    Yourself: 'Vous-même',

    // ---- Letter of credit: conditions and undertaking ----
    'Additional conditions (special conditions / additional instructions)':
        'Conditions supplémentaires (conditions spéciales / instructions complémentaires)',
    'Add new terms': 'Ajouter de nouvelles conditions',
    'Other Instructions': 'Autres instructions',
    'Other instructions': 'Autres instructions',
    'All documents must be presented within 30 days from date of shipment':
        'Tous les documents doivent être présentés dans les 30 jours suivant la date d’expédition',
    'All documents must have the LC number quoted':
        'Tous les documents doivent mentionner le numéro du crédit documentaire',
    'All documents to be in English': 'Tous les documents doivent être en anglais',
    'All documents to be issued in English': 'Tous les documents doivent être émis en anglais',
    "Third party documents acceptable. Charges outside Kenya are for beneficiary's account.":
        'Les documents de tiers sont acceptés. Les frais hors du Kenya sont à la charge du bénéficiaire.',
    '1) Download the Terms and Conditions below and upload the signed version.':
        '1) Téléchargez les conditions générales ci-dessous et téléversez la version signée.',
    '2) Upload the signed Terms and Conditions below': '2) Téléversez les conditions générales signées ci-dessous',
    "I accept the indemnity and the bank's terms and conditions for commercial letters of credit":
        'J’accepte l’indemnité et les conditions générales de la banque relatives aux crédits documentaires commerciaux',
    'I accept the terms and conditions for this Documentary Credit amendment':
        'J’accepte les conditions générales relatives à cet avenant au crédit documentaire',
    '(UCP 600, articles 19–25)': '(RUU 600, articles 19 à 25)',
    '(UCP 600, articles 19–25) — leave blank to keep unchanged':
        '(RUU 600, articles 19 à 25) — laissez vide pour ne rien changer',

    // ---- Letter of credit: collateral and limits ----
    'Margin / cover %': 'Marge / couverture %',
    'Margin to be blocked': 'Marge à bloquer',
    'Cash Cover:': 'Couverture en espèces :',
    'Use Cash Cover:': 'Utiliser la couverture en espèces :',
    'Marked Limit:': 'Limite affectée :',
    'Use Marked Limit:': 'Utiliser la limite affectée :',
    'Total Collateral USD:': 'Total des garanties en USD :',
    'Total USD:': 'Total en USD :',
    'Available Credit Limit': 'Limite de crédit disponible',
    'Available bal.': 'Solde disponible',
    'Current bal.': 'Solde actuel',
    'Limit amt.': 'Montant de la limite',
    'Utilized amt.': 'Montant utilisé',
    'Existing LC facility': 'Ligne de crédit documentaire existante',
    'Utilise your approved import LC line.': 'Utilisez votre ligne de crédit documentaire import approuvée.',
    'Full credit value blocked on your account until settlement.':
        'La valeur totale du crédit est bloquée sur votre compte jusqu’au règlement.',
    'Debit account': 'Compte à débiter',
    'Debit account for the credit amount': 'Compte à débiter pour le montant du crédit',
    'Debit account for commission & charges': 'Compte à débiter pour la commission et les frais',
    'Debit account for amendment charges': 'Compte à débiter pour les frais d’avenant',
    'Debit account for the increased amount': 'Compte à débiter pour le montant augmenté',
    'Payment Type': 'Type de paiement',
    'Payment Amount': 'Montant du paiement',
    'Payment due': 'Paiement dû',

    // ---- Letter of credit: approval workflow ----
    'Credit & limits approval': 'Approbation du crédit et des limites',
    'Internal authoriser (maker–checker)': 'Autorisateur interne (initiateur–vérificateur)',
    'Authoriser note (optional)': 'Note de l’autorisateur (facultative)',
    Authorised: 'Autorisé',
    'Approve internally': 'Approuver en interne',
    'Approved internally': 'Approuvé en interne',
    'Approve LC': 'Approuver le crédit documentaire',
    'Approve edited LC': 'Approuver le crédit documentaire modifié',
    'Approved by the bank': 'Approuvé par la banque',
    'Submit to bank': 'Soumettre à la banque',
    'Submitted to bank': 'Soumis à la banque',
    'Sent for internal approval': 'Envoyé pour approbation interne',
    'Awaiting bank review': 'En attente de l’examen de la banque',
    'Bank review completed': 'Examen de la banque terminé',
    'Bank trade operations review': 'Examen par les opérations trade de la banque',
    'Ready to submit for approval': 'Prêt à être soumis pour approbation',
    'With the bank, awaiting a second signature': 'Auprès de la banque, en attente d’une seconde signature',
    'Return to creator': 'Renvoyer au créateur',
    'Returned for amendment': 'Renvoyé pour modification',
    'Declined by bank': 'Refusé par la banque',
    'Application declined': 'Demande refusée',
    'Credit issued': 'Crédit émis',
    'Credit issued — MT700 transmitted': 'Crédit émis — MT700 transmis',
    'Documentary credit issued': 'Crédit documentaire émis',
    'Download MT700': 'Télécharger le MT700',
    'An approver submits this to the bank from this page.': 'Un approbateur soumet ceci à la banque depuis cette page.',
    'An authoriser approves this from the application itself, not from here.':
        'Un autorisateur approuve ceci depuis la demande elle-même, et non depuis cette page.',
    'This application is complete and waiting on an authoriser in your business. It does not go to the bank until it is approved here.':
        'Cette demande est complète et attend un autorisateur au sein de votre entreprise. Elle n’est transmise à la banque qu’une fois approuvée ici.',
    'Your application is complete and collateral is selected. It now needs authorising inside your business; it goes to the bank once that is done.':
        'Votre demande est complète et la garantie est sélectionnée. Elle doit maintenant être autorisée au sein de votre entreprise ; elle sera transmise à la banque une fois cette étape franchie.',
    'MT700 has been transmitted to the advising bank. The beneficiary will be advised within 1 working day.':
        'Le MT700 a été transmis à la banque notificatrice. Le bénéficiaire sera avisé sous 1 jour ouvrable.',
    '. Submitting sends this application to the bank for review.':
        '. La soumission envoie cette demande à la banque pour examen.',
    '. The beneficiary will be advised within 1 working day.': '. Le bénéficiaire sera avisé sous 1 jour ouvrable.',

    // ---- Letter of credit: amendments ----
    Amend: 'Modifier',
    'Amend this credit': 'Modifier ce crédit',
    'Amendment No.': 'N° d’avenant',
    'Amendment charges': 'Frais d’avenant',
    'Amendment to Documentary Credit Amount': 'Avenant au montant du crédit documentaire',
    'Application for Amendment to a Commercial Documentary Credit':
        'Demande d’avenant à un crédit documentaire commercial',
    'Choose what you would like to change before continuing.':
        'Choisissez ce que vous souhaitez modifier avant de continuer.',
    'No amendments selected yet.': 'Aucun avenant sélectionné pour le moment.',
    'Other Amendments': 'Autres avenants',
    'Describe any other instructions to be amended': 'Décrivez toute autre instruction à modifier',
    'Increase amount': 'Augmenter le montant',
    'Decrease amount': 'Diminuer le montant',
    'Current amount': 'Montant actuel',
    'New amount after amendment': 'Nouveau montant après avenant',
    'Current expiry': 'Expiration actuelle',
    'New expiry date': 'Nouvelle date d’expiration',
    'New Date of Expiry': 'Nouvelle date d’expiration',
    'New value': 'Nouvelle valeur',
    'Confirm & continue': 'Confirmer et continuer',
    'Confirm All': 'Tout confirmer',
    'SWIFT MT707 — preview': 'SWIFT MT707 — aperçu',
    'MT707 has been transmitted to the advising bank against':
        'Le MT707 a été transmis à la banque notificatrice au titre de',
    'The amendment has been sent to the bank for authorisation.':
        'L’avenant a été envoyé à la banque pour autorisation.',
    'Review the changes above, then submit — this sends the amendment to the bank. The credit stays on its current terms until the bank authorises it.':
        'Vérifiez les modifications ci-dessus, puis soumettez — cela envoie l’avenant à la banque. Le crédit reste soumis à ses conditions actuelles jusqu’à ce que la banque l’autorise.',
    'stays on its current terms until the bank agrees to the change.':
        'reste soumis à ses conditions actuelles jusqu’à ce que la banque accepte la modification.',

    // ---- Import Orders, Loans & Escrow ----
    'Import Order': 'Commande à l’import',
    'Import Orders': 'Commandes à l’import',
    'Import Orders - LCs': 'Commandes à l’import — lettres de crédit',
    'New Import Order': 'Nouvelle commande à l’import',
    'Add Single Import Order': 'Ajouter une seule commande à l’import',
    'Bulk Upload Import Order': 'Téléverser des commandes à l’import en masse',
    'Bulk Upload Import Orders': 'Téléverser des commandes à l’import en masse',
    'How would you like to add the Import order(s)?': 'Comment souhaitez-vous ajouter la ou les commandes à l’import ?',
    'Post Shipment Financing': 'Financement post-expédition',
    'Loan Amount/Duration:': 'Montant / durée du prêt :',
    'Select loan currency:': 'Sélectionnez la devise du prêt :',
    'What would you like to finance': 'Que souhaitez-vous financer',
    'Currency Exchange Rates': 'Taux de change',
    'Freight Charges': 'Frais de transport',
    'Customs, Duties & Other Charges': 'Douanes, droits et autres frais',
    'Find Supplier by name': 'Rechercher un fournisseur par son nom',
    'Search Supplier': 'Rechercher un fournisseur',
    'Search by Supplier': 'Rechercher par fournisseur',
    'Item Name': 'Nom de l’article',
    'Add Description': 'Ajouter une description',
    'Add Order/Invoice': 'Ajouter une commande / facture',
    'Add Payment Details': 'Ajouter les détails du paiement',
    'Add Invoice': 'Ajouter une facture',
    'Order/Invoice Amount:': 'Montant de la commande / facture :',
    'Invoice/Order:': 'Facture / commande :',
    'Invoices/Order:': 'Factures / commande :',
    'Select Invoice/Order:': 'Sélectionnez la facture / commande :',
    'Select Invoices/Order:': 'Sélectionnez les factures / commande :',
    'Invoice/Order No.': 'N° de facture / commande',
    'Invoice/ Order No': 'N° de facture / commande',
    'Payment Details:': 'Détails du paiement :',
    'Payment Information:': 'Informations de paiement :',
    'Transaction Amount:': 'Montant de la transaction :',
    'Resend Order': 'Renvoyer la commande',
    'Kenya Shillings': 'Shillings kényans',
    'US Dollars': 'Dollars américains',
    Dollar: 'Dollar',
    'FX code': 'Code de change',
    Item: 'Article',
    Port: 'Port',
    'Enter port': 'Saisissez le port',
    'Port of discharge:': 'Port de déchargement :',
    'Supplier :': 'Fournisseur :',
    'Order:': 'Commande :',
    Charges: 'Frais',
    INCOTERMS: 'INCOTERMS',
    When: 'Quand',
    Months: 'Mois',
    '1 month': '1 mois',
    '48 Months': '48 mois',
    'Order Date:': 'Date de la commande :',
    'Order Number:': 'Numéro de commande :',
    'Order Summary:': 'Récapitulatif de la commande :',
    'Payment Type:': 'Type de paiement :',
    'Pre Shipment': 'Avant expédition',
    'Post Shipment': 'Après expédition',
    'Pre-Shipment Inspection': 'Inspection avant expédition',
    'Shipment Clearing': 'Dédouanement de l’expédition',
    'Shipment Confirmation': 'Confirmation de l’expédition',
    'Supplier Payments': 'Paiements aux fournisseurs',
    'Supplier Verification': 'Vérification du fournisseur',
    Escrow: 'Séquestre',
    'New Escrow': 'Nouveau séquestre',
    'Escrow Agreement:': 'Convention de séquestre :',
    'Escrow Fees:': 'Frais de séquestre :',
    'Escrow No.': 'N° de séquestre',
    'Submit Escrow': 'Soumettre le séquestre',
    'Release Payments': 'Libérer les paiements',
    'Dispute Goods': 'Contester les marchandises',
    'Require Pre-Shipment verification': 'Exiger une vérification avant expédition',
    'Pre-Shipment Verification Fees:': 'Frais de vérification avant expédition :',
    'Payment will be triggered on shipment': 'Le paiement sera déclenché à l’expédition',
    'Description Of Merchandise': 'Description des marchandises',
    'Import Order Timeline': 'Chronologie de la commande à l’import',
    'View Tracking Details': 'Voir les détails du suivi',
    'Close LC': 'Clôturer la lettre de crédit',
    KYS: 'Connaissance du fournisseur',
    'Add Tracking Number Details': 'Ajouter les détails du numéro de suivi',
    'Document Tracking Number': 'Numéro de suivi du document',
    'Document Tracking:': 'Suivi des documents :',
    'Order Tracking Details': 'Détails du suivi de la commande',
    'Shipment/Goods Tracking:': 'Suivi de l’expédition / des marchandises :',
    'Fees & Commission:': 'Frais et commission :',
    'Financing Limit:': 'Limite de financement :',
    Avalized: 'Avalisé',
    'Service Provider': 'Prestataire de services',
    'Proforma No.': 'N° de proforma',
    'Ref ID': 'Réf.',
    SKU: 'UGS',
    'Doc Type': 'Type de document',
    'Date Received': 'Date de réception',
    Declaration: 'Déclaration',
    'Collateral Details': 'Détails de la garantie',
    'Message/Instructions': 'Message / instructions',
    Milestone: 'Jalon',
    'Get Letter Of Credit': 'Obtenir une lettre de crédit',
    'Get Letter of Credit (LC)': 'Obtenir une lettre de crédit',
    'Trade Finace': 'Financement du commerce',

    // ---- Shipping Guarantees ----
    'Shipping Guarantees': 'Garanties de livraison',
    'New Shipping Guarantee': 'Nouvelle garantie de livraison',
    'Back to Shipping Guarantees': 'Retour aux garanties de livraison',
    'A shipping guarantee is issued against an active Import LC.':
        'Une garantie de livraison est émise au titre d’une lettre de crédit à l’import active.',
    'A shipping guarantee must be issued against an active Import LC. Enter the LC reference to validate it against the core banking system (T24) before continuing.':
        'Une garantie de livraison doit être émise au titre d’une lettre de crédit à l’import active. Saisissez la référence de la lettre de crédit afin de la valider auprès du système bancaire central (T24) avant de continuer.',
    'Validate against T24': 'Valider auprès de T24',
    'Status in T24': 'Statut dans T24',
    'LC validation': 'Validation de la lettre de crédit',
    'LC reference': 'Référence de la lettre de crédit',
    'LC amount': 'Montant de la lettre de crédit',
    'LC expiry': 'Échéance de la lettre de crédit',
    'LC type': 'Type de lettre de crédit',
    'Underlying LC': 'Lettre de crédit sous-jacente',
    'Undrawn amount': 'Montant non utilisé',
    'Linked to LC CIF value': 'Rattaché à la valeur CAF de la lettre de crédit',
    'CIF value': 'Valeur CAF',
    'CIF value of goods': 'Valeur CAF des marchandises',
    'Consignee (nominated agent)': 'Destinataire (agent désigné)',
    'Same as applicant unless specified': 'Identique au donneur d’ordre sauf indication contraire',
    Vessel: 'Navire',
    'Vessel / Voyage': 'Navire / voyage',
    'Vessel name / Voyage no.': 'Nom du navire / n° de voyage',
    'Vessel and shipment details verified against LC goods description.':
        'Le navire et les détails de l’expédition ont été vérifiés au regard de la description des marchandises de la lettre de crédit.',
    'Bill of lading / AWB': 'Connaissement / lettre de transport aérien',
    'Bill of lading / AWB number': 'Numéro de connaissement / de lettre de transport aérien',
    'Goods description': 'Description des marchandises',
    'Guarantee amount': 'Montant de la garantie',
    'Guarantee validity date': 'Date de validité de la garantie',
    'Validity date': 'Date de validité',
    'Issue date': 'Date d’émission',
    'Indemnity wording': 'Libellé de l’indemnité',
    'No-LC reason': 'Motif d’absence de lettre de crédit',
    'Not yet available — goods arrived ahead of docs':
        'Pas encore disponible — les marchandises sont arrivées avant les documents',
    'Countersign & issue': 'Contresigner et émettre',
    'Submit request to bank': 'Soumettre la demande à la banque',
    'Officer note': 'Note du chargé de dossier',
    'Awaiting bank': 'En attente de la banque',
    'Draft with applicant': 'Brouillon chez le donneur d’ordre',
    'No checks available for this request.': 'Aucun contrôle disponible pour cette demande.',
    '(visible in audit trail)': '(visible dans la piste d’audit)',
    'A second officer must countersign before the bank undertakes anything.':
        'Un second chargé de dossier doit contresigner avant que la banque ne s’engage.',
    '. A second officer must countersign before the bank undertakes anything.':
        '. Un second chargé de dossier doit contresigner avant que la banque ne s’engage.',
    'You made this recommendation, so it needs a different officer to countersign it.':
        'Vous êtes à l’origine de cette recommandation ; elle doit donc être contresignée par un autre chargé de dossier.',
    'Your roles on this account do not include deciding on shipping guarantees.':
        'Vos rôles sur ce compte ne vous permettent pas de statuer sur les garanties de livraison.',
    Accept: 'Accepter',
    Accepted: 'Acceptée',
    Decline: 'Refuser',
    Declined: 'Refusée',
    DRAFT: 'BROUILLON',

    // ---- Received Bills on Collection ----
    'Received Bills On Collection': 'Effets reçus à l’encaissement',
    'Received Bills on collection': 'Effets reçus à l’encaissement',
    'Received Documents': 'Documents reçus',
    'Bills On Collection Details': 'Détails des effets à l’encaissement',
    'Bills On Collection Amount Required:': 'Montant requis pour les effets à l’encaissement :',
    'Bills on Collection Charges': 'Frais sur effets à l’encaissement',
    'Bills on Collection Charges:': 'Frais sur effets à l’encaissement :',
    'Bills on Collection Summary': 'Récapitulatif des effets à l’encaissement',
    'Collection ID:': 'Identifiant de l’encaissement :',
    'Acceptance SWIFT Charges:': 'Frais SWIFT d’acceptation :',
    'Settlement SWIFT Charges:': 'Frais SWIFT de règlement :',
    'Closing Commission:': 'Commission de clôture :',
    'Handling Fee:': 'Frais de traitement :',
    'Total Charges:': 'Total des frais :',
    'Account Number for the collection of charges:': 'Numéro de compte pour le prélèvement des frais :',
    'Please select the Account Number for the debit of Bills on Collection Charges':
        'Veuillez sélectionner le numéro de compte à débiter des frais sur effets à l’encaissement',
    'Please select the Account Number to debit payment':
        'Veuillez sélectionner le numéro de compte à débiter du paiement',
    'Request Delivery': 'Demander une livraison',
    'Delivery Details': 'Détails de la livraison',
    'Preferred Delivery Date & Time': 'Date et heure de livraison souhaitées',
    'Enter your preferred delivery location': 'Saisissez le lieu de livraison souhaité',
    'Please deliver them': 'Veuillez les livrer',
    'We have the original copies of these documents.': 'Nous détenons les originaux de ces documents.',
    'What’s the best way we can get them to you?': 'Quel est le meilleur moyen de vous les faire parvenir ?',
    'I’ll pick from the bank': 'Je viendrai les retirer à la banque',
    'Invite Supplier': 'Inviter un fournisseur',
    'Save this supplier': 'Enregistrer ce fournisseur',
    'Supplier Details': 'Détails du fournisseur',
    'Supplier/Beneficiary': 'Fournisseur / bénéficiaire',
    'Supplier/Beneficiary ID': 'Identifiant du fournisseur / bénéficiaire',

    // ---- Payment Milestones ----
    'New Payment Milestone': 'Nouveau jalon de paiement',
    'Payment Milestone': 'Jalon de paiement',
    'Pay supplier milestones.': 'Payez les jalons du fournisseur.',
    'Cash Payment': 'Paiement au comptant',
    'Est Date': 'Date estimée',
    'Estimated Delievery Date': 'Date de livraison estimée',
    Automatically: 'Automatiquement',
    Manually: 'Manuellement',
    'Select Account': 'Sélectionnez un compte',

    // ---- Export Orders & Bills on Collection ----
    'Export Orders': 'Commandes à l’export',
    'Export Orders - Bills on collection': 'Commandes à l’export — effets à l’encaissement',
    'New Export Order': 'Nouvelle commande à l’export',
    'Export Invoices': 'Factures à l’export',
    'Order No': 'N° de commande',
    'P.O. Details': 'Détails du bon de commande',
    'Letters of Credit': 'Lettres de crédit',
    'Received Letters Of Credit': 'Lettres de crédit reçues',
    'Bills On Collection': 'Effets à l’encaissement',
    'Register Bills On Collection': 'Enregistrer des effets à l’encaissement',
    'Request Bills on Collection Financing?': 'Demander le financement des effets à l’encaissement ?',
    'Does the Bill on Collection require avalization?': 'L’effet à l’encaissement doit-il être avalisé ?',
    'Documents to be released against Acceptance': 'Documents à remettre contre acceptation',
    'Documents to be released against Acceptance and avalization': 'Documents à remettre contre acceptation et aval',
    'Documents to be released to drawee against payment only':
        'Documents à remettre au tiré contre paiement uniquement',
    'Instruction Set': 'Jeu d’instructions',
    'Additional Conditions': 'Conditions supplémentaires',
    '1. Please forward the attached documents on collection.':
        '1. Veuillez transmettre les documents ci-joints à l’encaissement.',
    '2. Please release documents against payment only': '2. Veuillez remettre les documents contre paiement uniquement',
    'Please credit the following account on settlement': 'Veuillez créditer le compte suivant au règlement',

    // ---- Buyer, Drawee & Collecting Bank ----
    'Buyer Details': 'Détails de l’acheteur',
    'Buyer Verification': 'Vérification de l’acheteur',
    'Buyer/Drawee Details': 'Détails de l’acheteur / tiré',
    'Collecting/Drawee Bank Details': 'Détails de la banque chargée de l’encaissement / du tiré',
    'Beneficiary Bank(Advising Bank)': 'Banque du bénéficiaire (banque notificatrice)',
    'Swift Address': 'Adresse SWIFT',
    'Select branch': 'Sélectionnez une agence',
    'Search a business': 'Rechercher une entreprise',
    'Search for a buyer': 'Rechercher un acheteur',

    // ---- Export Documents & Financing ----
    'Invoice Details': 'Détails de la facture',
    'Document Details': 'Détails du document',
    'Document Name': 'Nom du document',
    'Document Reference Number': 'Numéro de référence du document',
    'Required Documents': 'Documents requis',
    'Number of Copies': 'Nombre de copies',
    'Certificate of Conformity/Pre-Shipment Verification Certficate to Kenya Bureau of Standards Issued by a KRA approved':
        'Certificat de conformité / certificat de vérification avant expédition auprès du Kenya Bureau of Standards, émis par un organisme agréé par la KRA',
    'Insurance Policy/Certificate for at least 110% value of invoice':
        'Police / certificat d’assurance couvrant au moins 110 % de la valeur de la facture',
    'Financing Limit': 'Limite de financement',
    'Available Limit': 'Limite disponible',
    'Enter Financing Request:': 'Saisissez la demande de financement :',
    'Amount can not be greater than financing limit': 'Le montant ne peut pas dépasser la limite de financement',
    'Discount Amount': 'Montant de l’escompte',
    'Discount Rate:': 'Taux d’escompte :',
    'Advance Payment(%)': 'Acompte (%)',
    'Usuance(%)': 'Usance (%)',
    'Due Date': 'Date d’échéance',
    'Select due date': 'Sélectionnez la date d’échéance',

    // ---- Document Pickup ----
    'Request Document Pickup': 'Demander l’enlèvement des documents',
    'Request Pickup': 'Demander un enlèvement',
    'Pick-Up Details': 'Détails de l’enlèvement',
    'Pick-Up Location': 'Lieu d’enlèvement',
    'Preferred Pickup Date & Time': 'Date et heure d’enlèvement souhaitées',
    'Enter Document Pickup Location': 'Saisissez le lieu d’enlèvement des documents',
    'I will drop them off': 'Je les déposerai moi-même',
    'Pick them from me': 'Venez les récupérer chez moi',
    'We would need the physical copies of these documents.':
        'Nous aurons besoin des copies physiques de ces documents.',
    'What’s the best way we can get them?': 'Quel est le meilleur moyen de nous les faire parvenir ?',
    'NOTE: Requesting document pick-up will incur charges of':
        'REMARQUE : demander l’enlèvement des documents entraîne des frais de',
    Postcode: 'Code postal',
    'Share copy with the customer': 'Partager une copie avec le client',
    'Accept Escrow': 'Accepter le séquestre',
    'Escrow Timelinee': 'Chronologie du séquestre',
    'Secure Imports': 'Importations sécurisées',
    'Shipment Forrwarding': 'Transit de l’expédition',

    // ---- Bank Settings: Charges & Financing Products ----
    'Charges & Financing Products': 'Frais et produits de financement',
    'Applied Charges': 'Frais appliqués',
    'Applied Charges & Financing': 'Frais appliqués et financement',
    'Apply Charge': 'Appliquer des frais',
    'Apply Product:': 'Appliquer le produit :',
    'Apply Single Business Charge': 'Appliquer des frais à une seule entreprise',
    Charge: 'Frais',
    'Charge Name': 'Nom des frais',
    'Enter Charge name': 'Saisissez le nom des frais',
    'New Charge': 'Nouveaux frais',
    'Charges & Interest': 'Frais et intérêts',
    'Charging Basis': 'Base de facturation',
    'Charging Event': 'Fait générateur de la facturation',
    'Charging Structure': 'Structure de facturation',
    'Charging Type': 'Type de facturation',
    'Product Charging Is Defined :': 'La facturation du produit est définie :',
    'Product Settings': 'Paramètres du produit',
    'Setup New Product': 'Configurer un nouveau produit',
    'Product ID': 'Identifiant du produit',
    'Product Id': 'Identifiant du produit',
    'Product Name:': 'Nom du produit :',
    'Product Description:': 'Description du produit :',
    'Product Duration:': 'Durée du produit :',
    'Financing Product added successfully.': 'Produit de financement ajouté avec succès.',
    "Upload the Product's Terms & Conditions": 'Téléversez les conditions générales du produit',

    // ---- Bank Settings: Collection & Disbursement ----
    'Collection & Disbursement Accounts': 'Comptes d’encaissement et de décaissement',
    'Collection & Disbursement accounts': 'Comptes d’encaissement et de décaissement',
    'Add Collection Account': 'Ajouter un compte d’encaissement',
    'Add Capital/Disbursement Account': 'Ajouter un compte de capital / décaissement',
    'Capital Account/Disbursement Account': 'Compte de capital / compte de décaissement',
    'Capital/Disbursement Account': 'Compte de capital / décaissement',
    'Collection ID': 'Identifiant de l’encaissement',
    'Collection Name': 'Nom de l’encaissement',
    'Collection Strategies': 'Stratégies de recouvrement',
    'Collection Strategy': 'Stratégie de recouvrement',
    'Collection Strategy added successfully.': 'Stratégie de recouvrement ajoutée avec succès.',
    'Edit Collection Strategy Details': 'Modifier les détails de la stratégie de recouvrement',
    'Strategy Actions:': 'Actions de la stratégie :',
    'Add Strategy Action': 'Ajouter une action à la stratégie',
    'Edit Strategy Action': 'Modifier l’action de la stratégie',
    'New Strategy': 'Nouvelle stratégie',
    'Add Action': 'Ajouter une action',
    'Are you sure delete this strategy action?': 'Voulez-vous vraiment supprimer cette action de la stratégie ?',
    'Number of Triggers': 'Nombre de déclencheurs',
    Trigger: 'Déclencheur',
    Operand: 'Opérande',
    Conditions: 'Conditions',
    Frequency: 'Fréquence',
    Function: 'Fonction',
    'For the last?': 'Sur les derniers ?',
    'Previous Period': 'Période précédente',
    'When (Days)': 'Quand (jours)',

    // ---- Bank Settings: Credit Limits ----
    'Credit Limits': 'Limites de crédit',
    'Global Credit Limit': 'Limite de crédit globale',
    'Global Limit': 'Limite globale',
    'Global Limits:': 'Limites globales :',
    'Global limit:': 'Limite globale :',
    'Update Global Limit to:': 'Mettre à jour la limite globale à :',
    'Update Limit': 'Mettre à jour la limite',
    Limit: 'Limite',
    'Micro Limit': 'Micro-limite',
    'Micro Limits:': 'Micro-limites :',
    'New Micro Limit': 'Nouvelle micro-limite',
    'Add Micro Limit:': 'Ajouter une micro-limite :',
    'You cannot set micro limits using uploads': 'Vous ne pouvez pas définir de micro-limites par téléversement',
    'Utilized Limit': 'Limite utilisée',
    'Utilized Limit:': 'Limite utilisée :',
    'Utilized limit:': 'Limite utilisée :',
    'Upload Limits': 'Téléverser des limites',
    Maximum: 'Maximum',
    Minimum: 'Minimum',

    // ---- Bank Settings: Groups & Businesses ----
    'Supplier Groups': 'Groupes de fournisseurs',
    'Supplier Settings': 'Paramètres des fournisseurs',
    'User Groups': 'Groupes d’utilisateurs',
    'Create Group': 'Créer un groupe',
    'New Group': 'Nouveau groupe',
    'Group Code': 'Code du groupe',
    'Group Name': 'Nom du groupe',
    'Group Members': 'Membres du groupe',
    'Group/Branch Code': 'Code du groupe / de l’agence',
    'Group/Branch Name': 'Nom du groupe / de l’agence',
    'New Members': 'Nouveaux membres',
    'Add members': 'Ajouter des membres',
    'Upload Groups': 'Téléverser des groupes',
    'User deleted from the group successfully': 'Utilisateur supprimé du groupe avec succès',
    'Invite New Users': 'Inviter de nouveaux utilisateurs',
    'Main Branch': 'Agence principale',
    Agents: 'Agents',
    Anchor: 'Donneur d’ordre',
    'Business ID': 'Identifiant de l’entreprise',
    'Business ID:': 'Identifiant de l’entreprise :',
    'Business Name:': 'Nom de l’entreprise :',
    'Business Types': 'Types d’entreprise',
    'Account ID': 'Identifiant du compte',
    'Financial Institution': 'Établissement financier',
    'Search For Business using business ID or name': 'Rechercher une entreprise par identifiant ou par nom',
    'Validated Procuring Entities': 'Entités adjudicatrices validées',
    'Validated Suppliers': 'Fournisseurs validés',
    'Activate Procuring Entity': 'Activer l’entité adjudicatrice',
    'Are you sure you want to activate this procuring entity?':
        'Voulez-vous vraiment activer cette entité adjudicatrice ?',
    'Are you sure you want to deactivate this procuring entity?':
        'Voulez-vous vraiment désactiver cette entité adjudicatrice ?',
    Activate: 'Activer',
    'Activated On': 'Activé le',
    Deactivate: 'Désactiver',
    Deactivated: 'Désactivé',
    'Approved By': 'Approuvé par',
    'Approved By:': 'Approuvé par :',
    'Created By:': 'Créé par :',
    'Created On': 'Créé le',
    'Created On:': 'Créé le :',
    'Date Added': 'Date d’ajout',

    // ---- Bank Settings: KYC Documents & Templates ----
    'KYC Documents': 'Documents de connaissance client',
    'KYC Terms & Conditions': 'Conditions générales de connaissance client',
    'Add Document': 'Ajouter un document',
    'Add New Document': 'Ajouter un nouveau document',
    'Document Title': 'Titre du document',
    'Custom document': 'Document personnalisé',
    'Upload document': 'Téléverser un document',
    'Physical Documents Needed?': 'Documents physiques nécessaires ?',
    'Physical Documents Required': 'Documents physiques requis',
    'Is Required?': 'Est-il obligatoire ?',
    'Has Validity Period': 'Comporte une période de validité',
    'Validity Period': 'Période de validité',
    'Label/Description': 'Libellé / description',
    'Upload Frequency': 'Fréquence de téléversement',
    'Upload frequency': 'Fréquence de téléversement',
    'Please select and add the types Terms & Conditions required by the following types of businesses':
        'Veuillez sélectionner et ajouter les types de conditions générales exigés pour les types d’entreprise suivants',
    'Email, SMS Settings & Templates': 'Paramètres et modèles e-mail et SMS',
    Templates: 'Modèles',
    'Template Name': 'Nom du modèle',
    Publish: 'Publier',
    'Publish Template': 'Publier le modèle',
    'Un-publish Template?': 'Dépublier le modèle ?',
    'Template published': 'Modèle publié',
    'Template un-published': 'Modèle dépublié',
    'Are you sure you want to publish this template?': 'Voulez-vous vraiment publier ce modèle ?',
    'Are you sure you want to un-publish this template?': 'Voulez-vous vraiment dépublier ce modèle ?',
    'Save Draft': 'Enregistrer le brouillon',
    Trash: 'Corbeille',

    // ---- Bank Settings: Guarantees & Insurance Partners ----
    'Guarantee Settings': 'Paramètres des garanties',
    'Insurance Partner': 'Partenaire d’assurance',
    'Insurance Partners': 'Partenaires d’assurance',
    'Insurance Company': 'Compagnie d’assurance',
    'Add Insurance Partner': 'Ajouter un partenaire d’assurance',
    'Contact Email': 'E-mail de contact',
    'Contact Phone': 'Téléphone de contact',
    'Express Attributes': 'Attributs express',
    'In Gapstack': 'Dans Gapstack',
    'Outside Gapstack': 'Hors Gapstack',
    'Please change the selected combination!': 'Veuillez modifier la combinaison sélectionnée !',
    'No file': 'Aucun fichier',
    'Note:': 'Remarque :',
    'Successfully created sample': 'Échantillon créé avec succès',
    'Successfully updated sample': 'Échantillon mis à jour avec succès',
    'e.g Upload the signed Title Deed': 'ex. téléversez le titre de propriété signé',

    // ---- Business Account Onboarding ----
    'Company Documents': 'Documents de la société',
    'Company Registration': 'Immatriculation de la société',
    'Registration Date': 'Date d’immatriculation',
    'Business Certificate Created Saved Successfully': 'Certificat d’entreprise créé et enregistré avec succès',
    'Business Certificate Updated Successfully': 'Certificat d’entreprise mis à jour avec succès',
    'Business Information Updated Successfully': 'Informations sur l’entreprise mises à jour avec succès',
    'Contact Information Saved Successfully': 'Coordonnées enregistrées avec succès',
    'Contact Information Updated Successfully': 'Coordonnées mises à jour avec succès',
    'Financial Information Created Saved Successfully': 'Informations financières créées et enregistrées avec succès',
    'Financial Information Updated Successfully': 'Informations financières mises à jour avec succès',
    'Director Created Saved Successfully': 'Administrateur créé et enregistré avec succès',
    'Director Updated Successfully': 'Administrateur mis à jour avec succès',
    'Mobile Indemnity Saved Successfully': 'Formulaire d’indemnité mobile enregistré avec succès',
    'Mobile Indemnity Updated Successfully': 'Formulaire d’indemnité mobile mis à jour avec succès',
    'Personal details uploaded successfully': 'Informations personnelles téléversées avec succès',
    'Terms and Conditions updated successfully': 'Conditions générales mises à jour avec succès',
    'Pick up details recorded successfully': 'Détails de l’enlèvement enregistrés avec succès',
    'USSD PIN set up successfully': 'Code PIN USSD configuré avec succès',
    'OTP has been resent successfully': 'Le code à usage unique a été renvoyé avec succès',
    'Please upload all required fields.': 'Veuillez téléverser tous les champs obligatoires.',
    'Which of the following best describes your buiness?': 'Laquelle de ces options décrit le mieux votre entreprise ?',
    'Don’t see what best describes your business? Add it below. You can add more than one category by separating them with a coma.':
        'Vous ne trouvez pas ce qui décrit le mieux votre entreprise ? Ajoutez-le ci-dessous. Vous pouvez saisir plusieurs catégories en les séparant par une virgule.',

    // ---- Business Account Financials & Documents ----
    'Tax Information': 'Informations fiscales',
    'Audited Accounts': 'Comptes audités',
    'Bank Accounts': 'Comptes bancaires',
    'Bank / Wallet/ Biller Statements': 'Relevés bancaires, de portefeuille et de facturier',
    'Upload the audited account': 'Téléversez les comptes audités',
    'Upload the bank account statement': 'Téléversez le relevé du compte bancaire',
    'Upload the tax documents of the business.': 'Téléversez les documents fiscaux de l’entreprise.',
    'Upload Latest CR12': 'Téléversez le dernier CR12',
    'Payment (CR12 Search Charges - KES. 650.00)': 'Paiement (frais de recherche CR12 — 650,00 KES)',
    'Post Code': 'Code postal',

    // ---- Directors & Identity Verification ----
    'Confirm Directors': 'Confirmer les administrateurs',
    'Select director': 'Sélectionnez un administrateur',
    'Select document date': 'Sélectionnez la date du document',
    'Send Invite': 'Envoyer l’invitation',
    'Cancel Invite': 'Annuler l’invitation',
    'Please enter the details of directors you would like to invite':
        'Veuillez saisir les informations des administrateurs que vous souhaitez inviter',
    'Upload ID Document': 'Téléversez une pièce d’identité',
    'Upload ID Document -Front of the ID': 'Téléversez la pièce d’identité — recto',
    'Front of the ID': 'Recto de la pièce d’identité',
    'Back of the ID': 'Verso de la pièce d’identité',
    Passport: 'Passeport',
    'Liveness Detection': 'Détection de vivacité',
    'To scan your face, please follow these instructions.':
        'Pour scanner votre visage, veuillez suivre ces instructions.',
    'Please turn your head from left to right then tilt your head to the left then to the right..':
        'Veuillez tourner la tête de gauche à droite, puis l’incliner vers la gauche puis vers la droite.',
    'Processing video ...': 'Traitement de la vidéo…',
    'Verifying Your Documents': 'Vérification de vos documents',

    // ---- Phone Confirmation & USSD PIN ----
    'Phone Number Confirmation': 'Confirmation du numéro de téléphone',
    'Please enter the four digit code sent to': 'Veuillez saisir le code à quatre chiffres envoyé au',
    'Resend code': 'Renvoyer le code',
    'Create PIN': 'Créer un code PIN',
    'Confirm PIN': 'Confirmer le code PIN',
    PIN: 'Code PIN',
    'This is the phone number that will be used by the business when transacting on Whatsapp/ Telegram and USSD.':
        'Il s’agit du numéro de téléphone que l’entreprise utilisera pour ses transactions sur WhatsApp, Telegram et USSD.',

    // ---- KYC Physical Documents ----
    '1. Board Resolution': '1. Résolution du conseil d’administration',
    '2. Signed T&Cs': '2. Conditions générales signées',
    '3. Mobile Indemnity Form': '3. Formulaire d’indemnité mobile',
    '1. Download the Terms & Conditions below and upload the signed version.':
        '1. Téléchargez les conditions générales ci-dessous et téléversez la version signée.',
    '2. Uploaded the signed Terms and Conditions': '2. Téléversez les conditions générales signées',
    'We would need the physical copies of the following documents:':
        'Nous aurons besoin des copies physiques des documents suivants :',
    'Please ensure the following documents are ready for pick-up.':
        'Veuillez vous assurer que les documents suivants sont prêts à être enlevés.',
    'The bank will be expecting the documents': 'La banque attendra les documents',
    'Prefred Pickup Date & Time': 'Date et heure d’enlèvement souhaitées',
    'The KYC has been sent to the bank for approval. We will notify you once the approval is complete.':
        'Le dossier de connaissance client a été envoyé à la banque pour approbation. Nous vous préviendrons dès qu’elle sera accordée.',

    // ---- Marketing Consent & Account Prompt ----
    'Get emails from': 'Recevoir des e-mails de',
    'If you change your mind, you can unsubscribe at any time.':
        'Si vous changez d’avis, vous pouvez vous désabonner à tout moment.',
    'Privacy policy': 'Politique de confidentialité',
    'Do you have an existing': 'Avez-vous déjà un',
    'Don’t have an existing account?': 'Vous n’avez pas encore de compte ?',
    'Yes, I have': 'Oui, j’en ai un',
    Logo: 'Logo',

    // ---- Bank: Import LC Review ----
    'Import Orders - Letters of Credit': 'Commandes à l’import — lettres de crédit',
    'Import Details': 'Détails de l’import',
    'Import Information': 'Informations sur l’import',
    'Import Docs & Verification': 'Documents d’import et vérification',
    'Import licence': 'Licence d’importation',
    'Applicant & Beneficiary': 'Donneur d’ordre et bénéficiaire',
    'Amount & Terms': 'Montant et conditions',
    'Conditions Information': 'Informations sur les conditions',
    'Documents & Charges': 'Documents et frais',
    'Shipment Documents': 'Documents d’expédition',
    'Physical Documents': 'Documents physiques',
    'Uploaded Documents': 'Documents téléversés',
    'Submitted docs': 'Documents soumis',
    'Proforma invoice / sales contract': 'Facture proforma / contrat de vente',
    'IDF / UCR present': 'IDF / UCR présents',
    'Insurance at 110% of invoice': 'Assurance à 110 % de la facture',
    'UCP 600 consistency': 'Conformité aux RUU 600',
    'Not attached': 'Non joint',

    // ---- Bank: Credit, Collateral & Limits ----
    'Credit amount': 'Montant du crédit',
    'Credit reference': 'Référence du crédit',
    'Credit & collateral': 'Crédit et garantie',
    'Commission & charges': 'Commission et frais',
    Collateral: 'Garantie',
    'Collateral instruction': 'Instruction relative à la garantie',
    'Block the margin': 'Bloquer la marge',
    'Margin held': 'Marge bloquée',
    'Secure the credit': 'Garantir le crédit',
    'Draw on the import LC facility': 'Utiliser la ligne de crédit documentaire à l’import',
    Facility: 'Ligne de crédit',
    'Approved limit': 'Limite approuvée',
    'Currently utilised': 'Actuellement utilisé',
    'Utilisation after issuance': 'Utilisation après émission',
    'Limit availability': 'Disponibilité de la limite',
    'Product Limit': 'Limite du produit',
    'Loan Limit': 'Limite de prêt',
    'Set Up Global Limit': 'Définir la limite globale',
    Duration: 'Durée',

    // ---- Bank: Review Workflow & Decisions ----
    'Application submitted by applicant': 'Demande soumise par le donneur d’ordre',
    'Assigned to trade operations': 'Affectée aux opérations documentaires',
    'Automated checks completed': 'Contrôles automatiques terminés',
    'Running checks…': 'Contrôles en cours…',
    'Rerun Verification': 'Relancer la vérification',
    'Rerunning verification on documents': 'Relance de la vérification des documents',
    'Verification queue': 'File d’attente de vérification',
    'Awaiting officer decision': 'En attente de la décision du chargé de dossier',
    'Awaiting decision (with overrides)': 'En attente de décision (avec dérogations)',
    'Awaiting countersign': 'En attente de contreseing',
    'Maker recommended': 'Recommandation de l’initiateur',
    'Checker action': 'Action du vérificateur',
    'Decision SLA': 'Délai de décision',
    'Approve and issue this credit': 'Approuver et émettre ce crédit',
    'Approval requires a second checker before MT700 is transmitted.':
        'L’approbation requiert un second vérificateur avant la transmission du MT700.',
    'Decline this credit': 'Refuser ce crédit',
    'Return this application for amendment': 'Renvoyer cette demande pour modification',
    'Sent back to maker': 'Renvoyée à l’initiateur',
    'Sent For Approval': 'Envoyée pour approbation',
    'Returned For Changes': 'Renvoyée pour modification',
    'Draft / ready to submit': 'Brouillon / prête à être soumise',
    'Ready to submit': 'Prête à être soumise',
    'MT700 transmitted to advising bank': 'MT700 transmis à la banque notificatrice',
    'MT Message': 'Message MT',
    'Officer note (visible in the audit trail)': 'Note du chargé de dossier (visible dans la piste d’audit)',
    '. Countersign to apply it, or send it back to the maker.':
        '. Contresignez pour l’appliquer, ou renvoyez-la à l’initiateur.',
    'Could not load this application from the gateway.': 'Impossible de charger cette demande depuis la passerelle.',
    'This application': 'Cette demande',
    'Creator Summary': 'Récapitulatif du créateur',
    'Creator Name:': 'Nom du créateur :',
    'Creator Contacts:': 'Coordonnées du créateur :',
    'L C Details': 'Détails de la lettre de crédit',
    'LC ID:': 'Identifiant de la lettre de crédit :',
    'Last updated:': 'Dernière mise à jour :',
    'Notify Importer': 'Notifier l’importateur',
    'Additonal instructions': 'Instructions supplémentaires',
    Instructions: 'Instructions',

    // ---- Bank: Register Bills on Collection ----
    'Business/ Buyer /Drawee Details': 'Détails de l’entreprise / de l’acheteur / du tiré',
    'Supplier/Drawer Details': 'Détails du fournisseur / tireur',
    'Select Bills On Collection Terms of Payment':
        'Sélectionnez les conditions de paiement des effets à l’encaissement',
    'Bills registered successfully': 'Effets enregistrés avec succès',
    'Reference Type:': 'Type de référence :',
    'Service Type': 'Type de service',
    'Funding Type': 'Type de financement',
    'Document Pick-up': 'Enlèvement des documents',
    'Drop At Branch': 'Déposer en agence',
    'Pick-up': 'Enlèvement',

    // ---- Bank: KYC, AML & Business Portfolio ----
    'KYC & customer standing': 'Connaissance client et situation du client',
    'KYC Documentation': 'Documentation de connaissance client',
    'KYC/ AML Compliance Status': 'Statut de conformité KYC / LBC',
    'KYC/AML Reports': 'Rapports KYC / LBC',
    'Sanctions & watchlist screening': 'Filtrage des sanctions et listes de surveillance',
    'Risk Rating': 'Notation du risque',
    'Political Parties': 'Partis politiques',
    'Other Names': 'Autres noms',
    'Bio data': 'Données biographiques',
    Addresses: 'Adresses',
    Contacts: 'Contacts',
    References: 'Références',
    Links: 'Liens',
    Family: 'Famille',
    'Match Rate :': 'Taux de correspondance :',
    'Name :': 'Nom :',
    'Title:': 'Titre :',
    'To:': 'À :',
    'Since:': 'Depuis :',
    Mode: 'Mode',
    'No matches found': 'Aucune correspondance trouvée',
    'All Businesses': 'Toutes les entreprises',
    'List of businesses': 'Liste des entreprises',
    'Businesses on hold': 'Entreprises en attente',
    'Verified Businesses': 'Entreprises vérifiées',
    'Unverified Businesses': 'Entreprises non vérifiées',
    'Onboard Business': 'Intégrer une entreprise',
    'Business:': 'Entreprise :',
    'Trades with Supplier': 'Échanges avec le fournisseur',
    'Trades with Supplier (KES)': 'Échanges avec le fournisseur (KES)',
    'Portfolio Trades with Supplier': 'Échanges du portefeuille avec le fournisseur',
    'AI Extracted Financials': 'Données financières extraites par IA',
    'AI Validated': 'Validé par IA',
    'Add Product': 'Ajouter un produit',
    'Are you sure you want to delete the selected row ?': 'Voulez-vous vraiment supprimer la ligne sélectionnée ?',
    'Please select a row to delete': 'Veuillez sélectionner une ligne à supprimer',
    'Kindly add a reason for declining this field': 'Veuillez indiquer un motif de refus pour ce champ',
    'Kindly confirm if the number on the indemnity form is the same as the one added by the user, and mark as correct or wrong.':
        'Veuillez vérifier que le numéro figurant sur le formulaire d’indemnité est identique à celui saisi par l’utilisateur, puis indiquez s’il est correct ou erroné.',
    'Correct Number': 'Numéro correct',
    'Wrong Number': 'Numéro erroné',
    'An email has been sent to the customer to verify the mobile number and set PIN':
        'Un e-mail a été envoyé au client afin de vérifier le numéro de mobile et de définir le code PIN',
    'The profile has been returned back for changes': 'Le profil a été renvoyé pour modification',
    'Return to': 'Retour à',

    // ---- Products & Inventory ----
    Products: 'Produits',
    'New Product': 'Nouveau produit',
    'Edit Product': 'Modifier le produit',
    'Add a single product': 'Ajouter un seul produit',
    'Add Another Product': 'Ajouter un autre produit',
    'Bulk Upload Products': 'Téléverser des produits en masse',
    'Bulk Upload products': 'Téléverser des produits en masse',
    'Bank user can not bulk upload products': 'Un utilisateur de la banque ne peut pas téléverser de produits en masse',
    'Here are your uploaded products': 'Voici les produits que vous avez téléversés',
    'Complete Upload': 'Terminer le téléversement',
    '1) Download the sample excel file below': '1) Téléchargez le fichier Excel d’exemple ci-dessous',
    'Accepted file types are xlsx and xls': 'Les formats acceptés sont xlsx et xls',
    'Product created successfully': 'Produit créé avec succès',
    'Product updated Successfully': 'Produit mis à jour avec succès',
    'Product Deleted Successfully': 'Produit supprimé avec succès',
    'Product Information:': 'Informations sur le produit :',
    'Available Quantity': 'Quantité disponible',
    'Available quantity': 'Quantité disponible',
    'Inventory:': 'Stock :',
    Stock: 'Stock',
    'Unit(s)': 'Unité(s)',
    'Units:': 'Unités :',
    'Price:': 'Prix :',
    Pricing: 'Tarification',
    'Pricing:': 'Tarification :',
    'Pricing Model': 'Modèle de tarification',
    'Pricing Model:': 'Modèle de tarification :',
    'Pricing Name': 'Nom de la tarification',
    'Add tier': 'Ajouter un palier',
    'First Unit': 'Première unité',
    'First Unit:': 'Première unité :',
    'Last Unit': 'Dernière unité',
    'Last Unit:': 'Dernière unité :',
    'Per Unit': 'Par unité',
    'Per Unit:': 'Par unité :',
    'Flat Fee': 'Forfait',
    'Flat Fee:': 'Forfait :',
    Discount: 'Remise',
    'SKU:': 'UGS :',
    'Upload Image': 'Téléverser une image',
    'Drag your image here or': 'Faites glisser votre image ici ou',

    // ---- Ecosystem & Connections ----
    'Ecosystem Financing': 'Financement de l’écosystème',
    Connections: 'Connexions',
    'Connection Requests': 'Demandes de connexion',
    'Sent connections': 'Connexions envoyées',
    'Request Connect': 'Demander une connexion',
    'Request Connects': 'Demander des connexions',
    'You have no connections.': 'Vous n’avez aucune connexion.',
    'Your Platform’s Connections': 'Les connexions de votre plateforme',
    'Find a business': 'Trouver une entreprise',
    'Search for a business': 'Rechercher une entreprise',
    'Add new business': 'Ajouter une nouvelle entreprise',
    'Create a New Business': 'Créer une nouvelle entreprise',
    'Create Your Business Account': 'Créez votre compte entreprise',
    'Select Business': 'Sélectionnez une entreprise',
    'Please select the business you would like to sign into.':
        'Veuillez sélectionner l’entreprise à laquelle vous souhaitez vous connecter.',
    'Date Connected:': 'Date de connexion :',
    'Date Invited': 'Date d’invitation',
    'Add message': 'Ajouter un message',
    'Message:': 'Message :',
    'Hey, please add me to your ecosystem': 'Bonjour, merci de m’ajouter à votre écosystème',
    'Requested:': 'Demandé :',
    'Requestor:': 'Demandeur :',
    Ignore: 'Ignorer',
    Resend: 'Renvoyer',
    Proceed: 'Continuer',
    Congratulations: 'Félicitations',
    'Suppliers & Customers': 'Fournisseurs et clients',
    Buyers: 'Acheteurs',
    'Both Supplier & Buyer': 'À la fois fournisseur et acheteur',
    'They are?': 'Ils sont ?',
    'Synchronize Data': 'Synchroniser les données',
    'We found these invoices and orders that are registered to you by George Enterprises. Would you like us to add them?':
        'Nous avons trouvé ces factures et commandes enregistrées à votre nom par George Enterprises. Souhaitez-vous que nous les ajoutions ?',
    'Create, Invite & Manage your buyers & Suppliers': 'Créez, invitez et gérez vos acheteurs et fournisseurs',

    // ---- Product Catalogue & Landing Page ----
    'Working Capital Financing': 'Financement du fonds de roulement',
    'Invoice Discounting': 'Escompte de factures',
    'P.O. Financing': 'Financement de bons de commande',
    'Pre/Post -Shipment Financing': 'Financement avant / après expédition',
    'Retail, Distributor Financing': 'Financement du commerce de détail et des distributeurs',
    'Export LC Confirmation': 'Confirmation de lettre de crédit à l’export',
    'Discount Export LC': 'Escompter une lettre de crédit à l’export',
    'Advanced Payment Guarantee': 'Garantie de restitution d’acompte',
    'Get a Bid Bond': 'Obtenir une garantie de soumission',
    'Get a Performance Bond': 'Obtenir une garantie de bonne exécution',
    'Get an Import LC': 'Obtenir une lettre de crédit à l’import',
    'Get a loan to finance a P.O you own': 'Obtenez un prêt pour financer un bon de commande qui vous est adressé',
    'Get paid early for an export': 'Soyez payé par anticipation pour une exportation',
    'Finance your imports & exports': 'Financez vos importations et exportations',
    'Financing to run your business': 'Un financement pour faire tourner votre entreprise',
    'Bid for tenders in seconds': 'Soumissionnez à des appels d’offres en quelques secondes',
    'Execute your tenders with ease': 'Exécutez vos marchés en toute simplicité',
    'Check your bid bond details': 'Consultez les détails de votre garantie de soumission',
    'Verify A Bid Bond': 'Vérifier une garantie de soumission',
    'Results for Bid Bond -': 'Résultats pour la garantie de soumission —',
    'Guarantee created successfully': 'Garantie créée avec succès',
    'Confirm your export sales': 'Confirmez vos ventes à l’export',
    'Receive payments on documents': 'Recevez des paiements contre documents',
    'Send LCs fast to your overseas suppliers':
        'Envoyez rapidement des lettres de crédit à vos fournisseurs à l’étranger',
    'Send a P.O. to your Suppliers': 'Envoyez un bon de commande à vos fournisseurs',
    'Send an Invoice to your Customers': 'Envoyez une facture à vos clients',
    'Create & Send a PO': 'Créer et envoyer un bon de commande',
    'Create & Send an Invoice': 'Créer et envoyer une facture',
    'Upload Invoices and Get Financing': 'Téléversez vos factures et obtenez un financement',
    'Upload your Invoices and get discounting': 'Téléversez vos factures et obtenez un escompte',
    'Upload your Invoices and get paid early': 'Téléversez vos factures et soyez payé par anticipation',
    'Also Available on:': 'Également disponible sur :',
    Mail: 'E-mail',
    Whatsapp: 'WhatsApp',
    Telegram: 'Telegram',
    'Phone:': 'Téléphone :',
    'Pin:': 'Code PIN :',
    'Start Date:': 'Date de début :',
    'Expiry Date:': 'Date d’expiration :',
    'Validity:': 'Validité :',
    'Request Payment': 'Demander un paiement',
    Orders: 'Commandes',

    // ---- Purchase Order & Quotation Notifications ----
    'Purchase order added successfully': 'Bon de commande ajouté avec succès',
    'Purchase order updated successfully': 'Bon de commande mis à jour avec succès',
    'Purchase order deleted': 'Bon de commande supprimé',
    'Purchase Order sent successfully': 'Bon de commande envoyé avec succès',
    'Purchase order sent to supplier': 'Bon de commande envoyé au fournisseur',
    'Purchase Order sent for Approval': 'Bon de commande envoyé pour approbation',
    'Purchase order sent for approval': 'Bon de commande envoyé pour approbation',
    'Purchase Order Approved Successfully': 'Bon de commande approuvé avec succès',
    'Purchase order Validated': 'Bon de commande validé',
    'Purchase order validated successfully': 'Bon de commande validé avec succès',
    'Purchase order Returned for Changes': 'Bon de commande renvoyé pour modification',
    'Purchase order returned for changes': 'Bon de commande renvoyé pour modification',
    'Return Purchase Order for Changes': 'Renvoyer le bon de commande pour modification',
    'Are you sure you want to delete this purchase order?': 'Voulez-vous vraiment supprimer ce bon de commande ?',
    "Bank can't copy the purchase order from here. You will have to view a particular purchase order to copy":
        'La banque ne peut pas copier le bon de commande depuis cet écran. Vous devez ouvrir un bon de commande précis pour le copier',
    "Bank can't edit the purchase order from here. You will have to come back from table to edit":
        'La banque ne peut pas modifier le bon de commande depuis cet écran. Vous devez revenir au tableau pour le modifier',
    'Add changes you would like made on the P.O': 'Indiquez les modifications à apporter au bon de commande',
    'P.O Timeline': 'Chronologie du bon de commande',
    'Send to Supplier': 'Envoyer au fournisseur',
    'Onboard Supplier': 'Intégrer un fournisseur',
    'Validate Order': 'Valider la commande',
    'Validation sent Successfully': 'Demande de validation envoyée avec succès',
    'Received For Validation': 'Reçu pour validation',
    '4) Upload the Scanned copies of the Orders if required':
        '4) Téléversez les copies numérisées des commandes si nécessaire',
    '4) Click on Continue below to add the Certificate of Incorporation/ Business certificates and Director IDs':
        '4) Cliquez sur Continuer ci-dessous pour ajouter le certificat de constitution, les certificats d’entreprise et les pièces d’identité des administrateurs',
    'Quotation accepted': 'Devis accepté',
    'Quotation declined': 'Devis refusé',
    'Quotation moved to Validate': 'Devis transmis pour validation',
    'Quotation#': 'Devis n°',
    'From quote': 'À partir du devis',
    'Invoice#': 'Facture n°',
    'Invoice Accepted': 'Facture acceptée',
    'Invoice accepted': 'Facture acceptée',
    'Invoice declined': 'Facture refusée',
    'Invoice validated': 'Facture validée',
    'Invoice has been sent for Validation': 'La facture a été envoyée pour validation',
    'Resend Invoice': 'Renvoyer la facture',
    'Upload invoice': 'Téléverser une facture',
    'Search Port': 'Rechercher un port',
    Commercial: 'Commercial',
    International: 'International',

    // ---- Authentication Messages ----
    'OTP Sent Successfully': 'Code à usage unique envoyé avec succès',
    'OTP has been sent to your registered phone number':
        'Un code à usage unique a été envoyé à votre numéro de téléphone enregistré',
    'Unable to send otp': 'Impossible d’envoyer le code à usage unique',
    'Email resent successfully': 'E-mail renvoyé avec succès',
    'Resend email': 'Renvoyer l’e-mail',
    'Accepting invitation!': 'Acceptation de l’invitation…',
    'Enter your new password and confirm it': 'Saisissez votre nouveau mot de passe et confirmez-le',
    'Password has been updated successfully!': 'Le mot de passe a été mis à jour avec succès !',
    'Password reset link as been sent to your email':
        'Un lien de réinitialisation du mot de passe a été envoyé à votre adresse e-mail',
    'Password reset link has been sent to your email':
        'Un lien de réinitialisation du mot de passe a été envoyé à votre adresse e-mail',
    'We have sent an SMS with your verification code sent to':
        'Nous avons envoyé un SMS contenant votre code de vérification au',
    'We have sent an SMS with your verification code to':
        'Nous avons envoyé un SMS contenant votre code de vérification au',
    "We can't sign you in": 'Nous ne pouvons pas vous connecter',
    'Unauthorizated!': 'Non autorisé !',
    'Go back home': 'Revenir à l’accueil',
    'The page you are looking for might be unavailable or you don’t have acces due to invalid credentials.':
        'La page que vous recherchez est peut-être indisponible, ou vous n’y avez pas accès en raison d’identifiants non valides.',
    'Your browser is currently set to block cookies. You need to allow cookies to use this service.':
        'Votre navigateur est actuellement configuré pour bloquer les cookies. Vous devez les autoriser pour utiliser ce service.',
    "Cookies are small text files stored on your computer that tell us when you're signed in. To learn how to allow cookies, check the online help in your web browser.":
        'Les cookies sont de petits fichiers texte enregistrés sur votre ordinateur qui nous indiquent que vous êtes connecté. Pour savoir comment les autoriser, consultez l’aide en ligne de votre navigateur.',
    'This will only take a short while.': 'Cela ne prendra qu’un instant.',

    // ---- Dashboard Insights ----
    'Insights Overview': 'Aperçu des indicateurs',
    'Working Capital Trend': 'Évolution du fonds de roulement',
    'Consumed Limit': 'Limite consommée',
    Financing: 'Financement',
    Payables: 'Dettes fournisseurs',
    Receivables: 'Créances clients',
    'Repayment Due': 'Remboursement dû',
    'Latest Guarantees/Bonds': 'Dernières garanties et cautions',
    'Latest Import Loans': 'Derniers prêts à l’import',
    'This Month': 'Ce mois-ci',
    'This Quarter': 'Ce trimestre',
    'Master Card': 'Mastercard',
    'Sign Up': 'S’inscrire',
    Placeholder: 'Espace réservé',

    // ---- Data Residency & Sovereignty ----
    'Access Log —': 'Journal des accès —',
    'Request tagged': 'Requête étiquetée',
    '(local)': '(locale)',
    '(cross-country)': '(transfrontalière)',
    HTTP: 'HTTP',
    "Each installation holds only its own country's data, so a record from":
        'Chaque installation ne détient que les données de son propre pays ; un enregistrement provenant de',
    "is not present here to be returned. The control below is the second layer: a request that arrives tagged for another installation is refused before any query runs, and the refusal is written to this installation's log stream.":
        'n’est donc pas présent ici et ne peut être restitué. Le contrôle ci-dessous constitue la seconde couche : une requête étiquetée pour une autre installation est refusée avant même l’exécution de toute interrogation, et ce refus est consigné dans le flux de journalisation de cette installation.',
    "Every country-tagged request reaching this installation, refused and permitted alike, held in this installation's own database. Requests carrying no country tag make no claim about a jurisdiction and are not listed here.":
        'Toute requête étiquetée par pays parvenant à cette installation, qu’elle soit refusée ou autorisée, est conservée dans la base de données propre à cette installation. Les requêtes dépourvues d’étiquette de pays n’invoquent aucune juridiction et ne figurent pas ici.',
    'DR Congo data may not transit Tanzania. The two installations share a codebase and nothing else — separate database, EDMS namespace, integration gateway, log stream and disaster recovery site.':
        'Les données de la RD Congo ne peuvent pas transiter par la Tanzanie. Les deux installations ne partagent que leur code source — base de données, espace de noms GED, passerelle d’intégration, flux de journalisation et site de reprise après sinistre sont distincts.',
    'Listed from configuration only. This installation holds no credential for':
        'Indiqué d’après la configuration uniquement. Cette installation ne détient aucune habilitation pour',
    'and cannot query it.': 'et ne peut pas l’interroger.',

    // ---- Documentary Collection Notices ----
    'Alerts cannot be dismissed until the bill is settled.':
        'Les alertes ne peuvent pas être masquées tant que l’effet n’est pas réglé.',
    'Indicative only:': 'À titre indicatif uniquement :',
    'This collection is payable at sight (D/P). Documents will be released to you once payment is authorized.':
        'Cet encaissement est payable à vue (documents contre paiement). Les documents vous seront remis dès que le paiement sera autorisé.',

    // ---- Messages & Notifications ----
    Compose: 'Rédiger',
    'Compose Email': 'Rédiger un e-mail',
    'Compose message': 'Rédiger un message',
    'Email sent successfully!': 'E-mail envoyé avec succès !',
    'Login to email service': 'Connectez-vous au service de messagerie',
    'Open a mail to view': 'Ouvrez un message pour le consulter',
    'Please add email query para in url': 'Veuillez ajouter le paramètre de requête e-mail dans l’URL',
    'Reply mail...': 'Répondre au message…',
    'Save as draft': 'Enregistrer comme brouillon',
    'Search messages...': 'Rechercher des messages…',
    'No notifications': 'Aucune notification',
    'Older Notifications': 'Notifications plus anciennes',

    // ---- Request Tracker ----
    'Current Stage': 'Étape actuelle',
    'Expected Completion': 'Achèvement prévu',
    'Request Date': 'Date de la demande',
    'Request Type': 'Type de demande',
    'Your Request': 'Votre demande',

    // ---- Location Picker ----
    Coordinates: 'Coordonnées',
    'Coordinates (latitude, longitude)': 'Coordonnées (latitude, longitude)',
    'GPS (Pick Exact location from the map)': 'GPS (sélectionnez l’emplacement exact sur la carte)',
    'Pick location on Maps': 'Sélectionner un lieu sur la carte',
    'Search an address': 'Rechercher une adresse',

    // ---- Business Switcher & Session ----
    'Change business': 'Changer d’entreprise',
    'Create Business': 'Créer une entreprise',
    'Create a new business': 'Créer une nouvelle entreprise',
    'Log Out': 'Se déconnecter',
    'Sign Out': 'Se déconnecter',
    'Select Group': 'Sélectionner un groupe',
    Switch: 'Changer',
    'Switch Group': 'Changer de groupe',
    'You are signed in as': 'Vous êtes connecté en tant que',
    'Your Businesses': 'Vos entreprises',

    // ---- Remaining Labels ----
    'Buyer/ Retailer/ Distributor Financing': 'Financement des acheteurs, détaillants et distributeurs',
    'Onboarded Buyers/ Wholesalers/ Retailers': 'Acheteurs, grossistes et détaillants intégrés',
    'Retailer/ Wholesaler Limits': 'Limites des détaillants et grossistes',
    'Bid Bonds': 'Garanties de soumission',
    'Import loans': 'Prêts à l’import',
    'FAX/Telex': 'Fax / télex',
    'Interest:': 'Intérêts :',
    'George Enterprises has been added to your list of connections successfully.':
        'George Enterprises a été ajouté à votre liste de connexions avec succès.',
    'has been issued and sent to your email or Whatsapp.':
        'a été émise et envoyée à votre adresse e-mail ou sur WhatsApp.',
    'PesaPal will charge KES': 'PesaPal prélèvera KES',
    'will be debited from this account': 'sera débité de ce compte',
    'will be debited.': 'sera débité.',
};

export default fr;
